/**
 * Le jeu: assemble le monde, la simulation et l'interface, et fait tourner la
 * boucle.
 *
 * La simulation avance a pas fixe (TICK_HZ) pour rester reproductible et
 * portable telle quelle sur le serveur en multijoueur; le rendu, lui, suit le
 * rythme de l'ecran. Le temps de jeu s'ecoule a la meme vitesse que le temps
 * reel: une journee complete dure DAY_LENGTH_MS, soit 24 minutes.
 *
 * CE QUI N'EST PLUS ICI. Ce fichier est celui que presque chaque modification
 * doit ouvrir: tout ce qui pouvait s'en aller sans passer huit champs a la main
 * est parti, avec ses explications. Chacun de ces modules garde son propre etat,
 * et c'est le vrai gain: on ne peut plus le remettre a zero par megarde depuis
 * la boucle de rendu.
 *
 *   client/reseau/sauvegarde.ts  tout ce qu'on ECRIT sur le serveur
 *   client/zoo.ts                apprivoiser, installer, rendre a la nature
 *   client/peche.ts              lancer la ligne, attendre, ferrer
 *   client/conduite.ts           « va la-bas », le filet anti-blocage, le
 *                                chemin sur lequel on pietine
 *   client/horloge-suivie.ts     comment le client SUIT l'heure du serveur
 *   client/log/etat-partie.ts    le battement d'etat du journal
 *   client/passerelles-test.ts   les passerelles de test qui calculent
 */
import * as THREE from 'three'

import {
  ARRIVE_EPSILON,
  CARTE_X,
  CARTE_Z,
  DAY_LENGTH_MS,
  MS_PER_GAME_HOUR,
  NAV_CELL,
  PROFONDEUR_NAGE,
  SEA_LEVEL,
  MAX_CATCHUP_TICKS,
  REACH,
  RUN_SPEED,
  TICK_DT,
  WALK_SPEED,
} from '../shared/constants.ts'
import type { Besoins, Joueur } from '../shared/types/player.ts'
import type {
  Animal,
  AnimalSauve,
  Batiment,
  BatimentKind,
  FormeFeu,
  JardinCueilli,
  Ressource,
  Vehicule,
} from '../shared/types/monde.ts'
import type { Cout, Inventaire, ItemId } from '../shared/types/items.ts'
import type { BiomeId, Vec2 } from '../shared/types/world.ts'

import { Alea, clamp, dist } from '../shared/sim/alea.ts'
import { formaterHeure, horlogeDepuis, prochaineAube, type Horloge, type PhaseJour } from '../shared/sim/temps.ts'
import { genererProps, genererPropsIle2, hauteurSecuritaire } from '../shared/sim/terrain.ts'
import { Carte, numeroDeMonde } from '../shared/sim/carte.ts'
import { GrilleNav, trouverChemin, vueDilatee } from '../shared/sim/navigation.ts'
import { arreter, definirPathfinder, joueurNeuf, majNageEtBesoins, vitesseNage } from '../shared/sim/joueur.ts'
import { formeDe } from '../shared/sim/besoins.ts'
import { MAX_EN_VOL, Reconciliation, appliquerEntree, entreeDeTick, type Entree } from '../shared/sim/entree.ts'
import {
  IndexRessources,
  type NoeudsSolides,
  estDisponible,
  frapper,
  noeudSousLeRayon,
  genererRessources,
  repousserListe,
} from '../shared/sim/ressources.ts'
import { ESPECES_PHASE_1, animalDef } from '../shared/data/animaux.ts'
import {
  REPAS_APPRIVOISEMENT,
  animalLePlusProche,
  estFamilier,
  genererAnimaux,
  majAnimaux,
} from '../shared/sim/animaux.ts'
import {
  barreLePassage,
  batimentContenant,
  creerBatiment,
  porteInterieur,
  positionCuisine,
  positionLit,
  positionMeuble,
  positionTable,
  validerPlacement,
  type MeubleInteractif,
} from '../shared/sim/batiments.ts'
import { couleursDefaut } from '../shared/sim/batiments.ts'
import { DUREE_COUP_MINE, depenserCoupMine, peutMiner } from '../shared/sim/mine.ts'
import { batimentDef } from '../shared/data/batiments.ts'
import { cloisonsInterieures, dansEmprise, dansUnMur, dansPiscine, peutDemolir, renduDemolition } from '../shared/sim/batiments.ts'
import { enclosDe } from '../shared/sim/zoo.ts'
import {
  accesEtage,
  avancerGlissade,
  batimentEtageContenant,
  cheminGlissade,
  dansSpa,
  glissadesDe,
  hauteurNiveau,
  niveauxDe,
  pisteDe,
  planEtageDe,
  positionAscenseur,
  positionEscalier,
  positionPortable,
  positionRestaurant,
  rayonVoisinage,
  RECUL_PARKING,
  VITESSE_GLISSADE,
  type CheminGlissade,
  type Piste,
} from '../shared/sim/batiments.ts'
import { Batiments } from '../shared/sim/monde-batiments.ts'
import { BIOMES } from '../shared/data/biomes.ts'
import { couleurCompte, estAdmin, melangerVersProprietaire } from '../shared/data/proprietaires.ts'
import { ressourceDef } from '../shared/data/ressources.ts'
import { objet } from '../shared/data/objets.ts'
import { recette } from '../shared/data/recettes.ts'
import { ajouterLot, payer, peutPayer, quantite } from '../shared/sim/inventaire.ts'
import { CeNom, LeNom, Pronom, UnNom, accorde, ceNom, duNom, leComplement, leNom, pronom } from '../shared/sim/grammaire.ts'
import { depenserRecolte, dormir, facteurRecolte, manger } from '../shared/sim/besoins.ts'
import {
  DUREE_ACTION,
  NOM_ACTION,
  aCanne,
  actionDisponible,
  actionVisee,
  eauAPortee,
  peutCreuser,
  porteeLancer,
  type ActionTerrain,
} from '../shared/sim/actions.ts'

import { RigCamera, pointPlan, pointSurface, rayonDepuisEcran } from './render/camera.ts'
import { RenduTerrain } from './render/terrain.ts'
import { RenduEau } from './render/eau.ts'
import { RenduProps } from './render/props.ts'
import { Ambiance } from './render/ambiance.ts'
import { RenduBatiments, hauteurGlissade } from './render/batiments.ts'
import { RenduRessources } from './render/ressources.ts'
import { RenduAnimaux } from './render/animaux.ts'
import { RenduZoo } from './render/zoo.ts'
import { RenduFeux } from './render/feux.ts'
import { RenduVehicules } from './render/vehicules.ts'
import { RenduJoueurs } from './render/joueurs.ts'
import { EtiquettesJoueurs } from './ui/etiquettes.ts'
import { Boussole } from './ui/boussole.ts'
import { SalonClient } from './reseau/salon.ts'
import { Sauvegarde, type EtatSauve } from './reseau/sauvegarde.ts'
import { Zoo } from './zoo.ts'
import { FiletAntiBlocage, SurveillanceProgres, conduireVers } from './conduite.ts'
import { Peche } from './peche.ts'
import { HorlogeSuivie } from './horloge-suivie.ts'
import {
  animalVu,
  autresJoueurs,
  enclosAutour,
  pointConstructible,
  pointMarchable,
  pointsBatiment,
  type AnimalVu,
  type EnclosVu,
  type JoueurVu,
  type PointsBatiment,
} from './passerelles-test.ts'
import type { EtatPresence, EvenementMonde, MessageChat } from '../shared/types/reseau.ts'
import { PERIODE_PUBLICATION_MS, SuiviPresence } from '../shared/sim/presence.ts'
import { SuiviFaune } from '../shared/sim/faune-reseau.ts'
import { HAUTEUR_ASSISE, estAvion, vehiculeDef } from '../shared/data/vehicules.ts'
import type { ContexteMontee } from '../shared/sim/vehicules.ts'
import {
  auSol,
  estGare,
  etatAvionNeuf,
  majAvion,
  viserAeroport,
  viserPoint,
  type EtatAvion,
} from '../shared/sim/avion.ts'
import {
  basculerAncre,
  creerVehicule,
  distanceVehicule,
  estProprietaire,
  majVehicule,
  multiplicateurVitesse,
  peutDescendre,
  peutMonter,
  peutJeterAncre,
  peutPecherDepuis,
  pisteSousAvion,
  pointPecheVehicule,
  poseSurPiste,
  positionDescente,
  positionValide,
  tournerLibre,
  validerPlacementVehicule,
  validerVehicule,
  vehiculeProche,
} from '../shared/sim/vehicules.ts'
import type { VehiculeKind } from '../shared/types/monde.ts'
import { Lampe } from './render/lampe.ts'
import { Bonhomme } from './character/bonhomme.ts'
import { CanneEnMain } from './render/canne.ts'
import { AnimationBonhomme, actionDepuisEtat } from './character/animation.ts'
import { JoystickVue, MarqueurDestination } from './input/joystick.ts'
import { Pointeur } from './input/pointeur.ts'
import { audio } from './audio/audio.ts'
import { Hud } from './ui/hud.ts'
import { VoileHorsLigne } from './ui/hors-ligne.ts'
import { PanneauEcole } from './ui/ecole.ts'
import { PanneauMusee } from './ui/musee.ts'
import { PanneauBibliotheque } from './ui/bibliotheque.ts'
import { PanneauAscenseur } from './ui/ascenseur.ts'
import { PanneauConfirmation } from './ui/confirmation.ts'
import { fusionnerProgres } from '../shared/sim/ecole/index.ts'
import type { Niveau, ProgresEcole, ResultatPartie } from '../shared/sim/ecole/types.ts'
import { Creation } from './ui/creation.ts'
import { MenuConstruction } from './ui/construction.ts'
import { PanneauInventaire } from './ui/inventaire.ts'
import { PanneauCuisine } from './ui/cuisine.ts'
import { PanneauFeux } from './ui/feux.ts'
import { PanneauReglages, chargerReglages, type Reglages } from './ui/reglages.ts'
import { PanneauAdmin } from './ui/admin.ts'
import { Chat } from './ui/chat.ts'
import { ecrireLocal, lireLocal } from './stockage.ts'
import { deciderResolution, etatResolutionNeuf } from './render/resolution.ts'
import { journal, noterPerf } from './log/journal.ts'
import { BattementEtat } from './log/etat-partie.ts'

/** Duree d'un coup de recolte, en secondes. */
const DUREE_COUP = 0.5
/** Duree de l'animation de repas, en secondes. */
const DUREE_REPAS = 1.4
/**
 * Ce que le repas rend EN PLUS quand il est pris a table, en fraction.
 *
 * On n'enleve rien au grignotage: la regle du proprietaire est qu'on mange ce
 * qu'on veut, ou on veut, sans jamais rien perdre. S'attabler vaut simplement
 * mieux que manger debout — c'est ce qui donne une raison de rentrer chez soi.
 */
const BONUS_REPAS_TABLE = 0.3
/** Intervalle de sauvegarde automatique, en secondes. */
const INTERVALLE_SAUVEGARDE = 15
/** Distance sous laquelle un tap compte comme "sur" une cible. */
const TOLERANCE_CIBLE = 1.8
/**
 * Ecart (m) entre la fin d'un chemin et le point vise au-dela duquel le chemin
 * est PARTIEL. Deux cases de la grille de navigation: un point vise dans une
 * case bloquee (un tronc, un bord d'eau) finit legitimement a la case libre
 * d'a cote.
 */
const MANQUE_CHEMIN_PARTIEL = 2 * NAV_CELL
/**
 * A quelle distance du bas du toboggan la descente est declaree finie.
 *
 * Pas zero, et ce n'est pas de la coquetterie: la derniere entree vise le point
 * d'arrivee, donc sa direction devient nulle des qu'on est dessus — le bonhomme
 * s'arreterait a quelques centimetres du but et n'y arriverait jamais. Trente
 * centimetres, c'est moins que le rayon du bonhomme: on est deja dans l'eau.
 */
const FIN_GLISSADE = 0.3
/** Au-dela, une descente est declaree bloquee: voir `DescenteGlissade.temps`. */
const DUREE_MAX_GLISSADE = 8

/**
 * Delai entre deux rappels d'ancre, en millisecondes de temps de JEU.
 *
 * Meme grandeur que le message du filet anti-blocage, et pour la meme raison:
 * l'apprendre une fois est utile, l'apprendre soixante fois en quatorze minutes
 * est un defaut en soi et noie tous les autres messages.
 */
const DELAI_RAPPEL_ANCRE = 8_000

/**
 * Epaisseur ajoutee aux murs pour l'emprise d'un vehicule: la bande (mur plus
 * deux rayons, 1,28 m) doit rester plus large que l'ecart entre deux points
 * de `pointsVehicule` (1,05 m) et que le pas d'un tick, sinon un mur passerait
 * entre deux points.
 */
const RAYON_MUR_VEHICULE = 0.55

/** Au-dela de cette distance, un batiment ne peut pas toucher la voiture. */
const RAYON_MURS_VEHICULE = 30
/**
 * Avance donnee a un animal sur un noeud de ressource quand les deux sont sous
 * le doigt, en metres. Voir `cibleAuPoint`.
 */
const MARGE_ANIMAL_VISE = 0.7
/*
 * LE RAYON DE RECHERCHE DES NOEUDS vient du MODELE (`rayonVoisinage`), et plus
 * d'une constante ecrite ici. Il valait 14 m, « large devant la villa 12 x 9 »,
 * ce qui etait vrai jusqu'a l'aerogare: sa piste s'etend a vingt-cinq metres,
 * le client ne voyait donc pas le palmier plante dessus, acceptait la pose, et
 * le serveur — qui, lui, regarde tout le monde — la refusait aussitot.
 */
/**
 * Delai entre deux collations, en millisecondes de jeu. Court: c'est gratuit et
 * ce doit le rester, on empeche seulement de vider le comptoir d'un seul appui.
 */
const DELAI_COLLATION = 12_000
/** Demi-hauteur du frustum pendant un feu d'artifice, pour voir l'explosion. */
const ZOOM_SPECTACLE = 24
/** Duree du recul de camera apres le dernier tir, en secondes. */
const DUREE_SPECTACLE = 9
/**
 * Intervalle minimal entre deux demandes de liste des joueurs, en millisecondes
 * reelles. Voir `demanderListeJoueurs`: le symptome se repete dix fois par
 * seconde, le remede ne doit pas.
 */
const DELAI_DEMANDE_LISTE_MS = 3000

/**
 * Resolution de rendu, en pixels par pixel CSS.
 *
 * Sur un ecran 4K, `devicePixelRatio` vaut 2: une fenetre de 1920x1080 se
 * rendait donc en 3840x2160, soit 8,3 millions de pixels, et le jeu tombait a
 * 22 images par seconde alors que le nombre de triangles etait raisonnable.
 * Le cout etait le remplissage, pas la geometrie. On plafonne donc, et on
 * ajuste ensuite tout seul selon les images par seconde reelles.
 */
const RATIO_MAX = 1.5

export type { EtatSauve } from './reseau/sauvegarde.ts'

export interface OptionsJeu {
  toile: HTMLCanvasElement
  couche: HTMLElement
  /** Nom du compte connecte, ou null en mode invite. Donne sa couleur au joueur. */
  compte: string | null
  graine: number
  tempsJeu: number
  sauvegarde: EtatSauve
  batiments: Batiment[]
  /** Vehicules deja poses dans le monde. */
  vehicules: Vehicule[]
  ressourcesEpuisees: { ressourceId: number; pv: number; repousseA: number }[]
  /** Animaux apprivoises, adoptes ou installes dans un enclos. */
  animaux?: AnimalSauve[]
  /** Jardins cueillis et leur echeance de repousse, en temps de jeu absolu. */
  jardins?: JardinCueilli[]
  progression?: (pct: number, texte: string) => void
}

type Mode = 'jeu' | 'construction' | 'demolition'

/** Ce que le joueur va faire en arrivant a destination. */
/**
 * UNE DESCENTE DE GLISSADE EN COURS.
 *
 * DESCENDRE EST UN TRAJET, PAS UNE TELEPORTATION, et tout ce type existe pour
 * ca. Le serveur fait autorite sur les positions: poser le bonhomme au bas du
 * toboggan lui vaudrait un `recalage` et le ferait remonter d'un coup — le
 * symptome exact du bogue de l'arbre coupe, paye en aout. On descend donc par
 * le PAS PARTAGE, en emettant des entrees ORDINAIRES le long du chemin, a
 * `VITESSE_GLISSADE` (6 m/s, sous RUN_SPEED, donc sous la borne que
 * `decoderEntrees` applique deja): le serveur rejoue exactement la meme chose,
 * il n'y a rien de neuf sur le fil et rien de neuf a tricher.
 *
 * Voir shared/sim/batiments.ts, section « glissades », qui prescrit cette
 * conception et porte `cheminGlissade` / `avancerGlissade`.
 */
interface DescenteGlissade {
  /** Le batiment qui porte la glissade, et le rang de celle-ci dans son plan. */
  batiment: string
  index: number
  /** Le chemin, en coordonnees du monde: calcule une fois, au depart. */
  chemin: CheminGlissade
  /**
   * Metres deja parcourus le long du toboggan.
   *
   * RECALCULE A CHAQUE TICK par projection de la position REELLE sur l'axe, et
   * jamais accumule: le bonhomme fatigue glisse moins vite (le pas ordinaire et
   * ses modificateurs), un recalage du serveur le deplace, et un compteur tenu
   * a part aurait fini par designer un point ou il n'est pas — donc une hauteur
   * de dessin fausse, et un toboggan qu'on descend a cote.
   */
  parcouru: number
  /**
   * Duree de la descente, en secondes.
   *
   * Un garde-fou, et il a une raison precise: si quoi que ce soit empechait
   * d'avancer (un mur pose entre-temps, une reconnexion malheureuse), le
   * bonhomme resterait coince en l'air a emettre des entrees que rien
   * n'arrete. Au-dela de `DUREE_MAX_GLISSADE`, on redescend et l'on ecrit
   * pourquoi.
   */
  temps: number
}

type ActionEnAttente =
  | { quoi: 'recolte'; id: number }
  | { quoi: 'nourrir'; id: number }
  | { quoi: 'adopter'; id: number }
  | { quoi: 'entrer'; id: string }
  | { quoi: 'cuisine'; id: string }
  | { quoi: 'table'; id: string }
  | { quoi: 'lit'; id: string }
  /** Le pupitre de l'ecole: on s'y assoit pour travailler. */
  | { quoi: 'pupitre'; id: string }
  /** Le front de taille de la mine: on y pioche, coup apres coup, sans fin. */
  | { quoi: 'filon'; id: string }
  | { quoi: 'terrain'; action: ActionTerrain; pos: Vec2 }
  | { quoi: 'jardin'; id: string }
  | { quoi: 'collation'; id: string }
  | { quoi: 'vehicule'; id: string }
  | { quoi: 'ecole'; id: string }
  /** Le musee: on y dessine, et on y regarde ce qui est accroche. */
  | { quoi: 'musee'; id: string }
  /** La bibliotheque: on y lit des bandes dessinees. */
  | { quoi: 'bibliotheque'; id: string }
  /** Prendre l'escalier d'une villa a etage: on monte, ou on redescend. */
  | { quoi: 'escalier'; id: string }
  /** L'ascenseur de l'hotel: il ouvre ses boutons, un par niveau. */
  | { quoi: 'ascenseur'; id: string }
  /** Le portable pose sur la table du bureau, la-haut. */
  | { quoi: 'portable'; id: string }
  /** Le comptoir du restaurant, a l'etage d'une aerogare: on y mange gratis. */
  | { quoi: 'restaurant'; id: string }
  /**
   * LE HAUT D'UNE GLISSADE, sur la passerelle du parc aquatique: on s'assoit
   * et l'on descend jusqu'au bassin. `index` est le rang de la glissade dans le
   * plan du modele — c'est lui, et non un point recopie, qu'on repasse a
   * `cheminGlissade`.
   */
  | { quoi: 'glissade'; id: string; index: number }
  | null

export class Jeu {
  // --------------------------------------------------------------- monde
  readonly terrain: Carte
  readonly grille: GrilleNav
  /** La meme grille vue a la largeur d'une voiture (`vueDilatee`), faite a la demande. */
  private grilleVoiture: GrilleNav | null = null
  readonly joueur: Joueur
  private ressources: Ressource[]
  private indexRessources: IndexRessources
  private animaux: Animal[]
  private aleaAnimaux: Alea
  private aleaRecolte: Alea
  private tempsJeu: number
  private horloge: Horloge

  // --------------------------------------------------------------- rendu
  private rendu: THREE.WebGLRenderer
  private scene: THREE.Scene
  private rig: RigCamera
  private renduTerrain: RenduTerrain
  private renduEau: RenduEau
  private renduProps: RenduProps
  private ambiance: Ambiance
  /**
   * Ce qui est bati dans le monde. LA verite: le rendu n'en garde qu'un reflet,
   * pour pouvoir afficher. C'est ce qui permettra au serveur de faire autorite
   * sans jamais importer three.js.
   */
  private readonly batiments = new Batiments()
  private renduBatiments: RenduBatiments
  private renduRessources: RenduRessources
  private renduAnimaux: RenduAnimaux
  private renduZoo: RenduZoo
  private renduFeux: RenduFeux
  private renduVehicules: RenduVehicules
  private renduJoueurs: RenduJoueurs
  private lampe: Lampe
  private bonhomme: Bonhomme
  private animation: AnimationBonhomme
  private canneEnMain: CanneEnMain

  // --------------------------------------------------------------- saisie
  private pointeur: Pointeur
  private joystick: JoystickVue
  private marqueur: MarqueurDestination

  // --------------------------------------------------------------- interface
  private hud: Hud
  private creation: Creation
  private menuConstruction: MenuConstruction
  private panneauInventaire: PanneauInventaire
  private panneauCuisine: PanneauCuisine
  private panneauFeux: PanneauFeux
  private panneauReglages: PanneauReglages
  private panneauAdmin: PanneauAdmin
  private voileHorsLigne: VoileHorsLigne
  private panneauEcole: PanneauEcole
  /** Le musee: la galerie et le chevalet. */
  private panneauMusee: PanneauMusee
  /** La bibliotheque: le rayon et les planches. */
  private panneauBibliotheque: PanneauBibliotheque
  private panneauAscenseur: PanneauAscenseur
  private panneauConfirmation: PanneauConfirmation
  /**
   * Niveau scolaire choisi la derniere fois, par COMPTE.
   *
   * Deux enfants d'ages differents partagent la maison: rouvrir l'ecole en
   * troisieme annee parce que l'autre y a joue serait une petite vexation a
   * chaque partie.
   */
  private niveauEcole: Niveau = 3
  /**
   * Progres scolaire cumule du compte.
   *
   * IL VIT SUR LE SERVEUR, par jeton de compte, comme le bonhomme lui-meme. Le
   * stockage local reste ecrit a chaque partie, mais il n'est plus que le
   * SECOURS HORS LIGNE: le monde s'arrete quand la liaison tombe, une partie
   * finie juste avant ne doit pas s'evaporer pour autant. Au demarrage on
   * garde le plus avance des deux (voir `recupererProgresEcoleServeur`).
   */
  private progresEcole: ProgresEcole | null = null
  private etiquettes: EtiquettesJoueurs
  private boussole: Boussole
  private chat: Chat
  private reglages: Reglages

  // --------------------------------------------------------------- reseau
  /**
   * Liaison temps reel. `null` en invite: jouer ensemble demande un compte, que
   * le serveur puisse verifier le jeton (voir server/salon.ts).
   */
  private salon: SalonClient | null = null
  /** Tout ce qu'on ECRIT sur le serveur (voir client/reseau/sauvegarde.ts). */
  private readonly sauvegarde: Sauvegarde
  /** Historique recent des autres joueurs, pour les afficher lisses. */
  private readonly suivi = new SuiviPresence()
  /** Etats interpoles de l'image courante. Reutilise: une allocation par image. */
  private readonly etatsAutres = new Map<string, EtatPresence>()
  /** Temps de jeu du dernier rappel d'ancre, pour ne pas le repeter. */
  private dernierRappelAncre = -Infinity
  /** Temps de jeu du dernier « ça ne passe pas », pour ne pas le repeter. */
  private dernierRappelObstacle = -Infinity
  /** Dernier point touche a bord: s'il est dans le mauvais milieu, c'est LA raison de l'arret. */
  private butABord: Vec2 | null = null
  /** Heure reelle de la derniere publication, en millisecondes. */
  private dernierePublication = -Infinity
  /** Heure reelle de la derniere liste redemandee au serveur. */
  private derniereDemandeListe = -Infinity
  /** Dernier etat publie: on ne renvoie pas dix fois par seconde la meme chose. */
  private dernierPublie: EtatPresence | null = null
  /**
   * LA PREDICTION LOCALE et sa correction (voir shared/sim/entree.ts).
   *
   * Le client n'annonce plus sa position: il envoie ce qu'il essaie de faire,
   * l'applique tout de suite chez lui — sans quoi trente millisecondes de
   * reseau suffiraient a rendre le jeu injouable — et corrige quand le serveur
   * repond. Elle existe meme en solo, ou elle ne sert qu'a numeroter: c'est ce
   * qui garde UN SEUL chemin de code pour le deplacement.
   */
  private readonly reconciliation = new Reconciliation()
  /** Numero de la prochaine entree. Croissant, jamais reutilise. */
  private sequence = 0

  // --------------------------------------------------------------- etat
  private token: string
  /** Compte connecte: sert a colorer ce que le joueur construit. */
  private compte: string | null
  private mode: Mode = 'jeu'
  private enAttente: ActionEnAttente = null
  /** La descente de glissade en cours, ou null. Voir `DescenteGlissade`. */
  private descente: DescenteGlissade | null = null
  private minuterieCoup = 0
  /**
   * Instant (horloge du navigateur) avant lequel le prochain coup ne part pas.
   * Retaper l'arbre remettait la minuterie a zero: chaque tap frappait tout de
   * suite, et trois taps rapides abattaient l'arbre en trois images, fatigue
   * comprise.
   */
  private prochainCoupMs = 0

  /** Ce qui reste du coup en cours, en secondes de minuterie. */
  private resteAvantCoup(): number {
    const reste = (this.prochainCoupMs - performance.now()) / 1000
    return reste > 0 ? reste * facteurRecolte(this.joueur.besoins) : 0
  }
  private minuterieAction = 0
  private cibleRessource: Ressource | null = null
  private cibleAnimal: Animal | null = null
  private cibleMeuble: MeubleInteractif | null = null
  private pointIndice: Vec2 | null = null
  private interieur: string | null = null
  private biomeCourant: BiomeId | null = null
  private phaseCourante: PhaseJour | null = null
  private appat: ItemId | null = null

  /**
   * La faune telle que le serveur la voit.
   *
   * `fauneRecue` est la BASCULE entre les deux mondes: tant qu'elle est fausse,
   * le client fait vivre ses animaux lui-meme (partie solo, invite, liaison
   * coupee); des la premiere trame, il ne fait plus que suivre. Une bascule et
   * non une lecture de `salon.connecte`, parce que la liaison s'ouvre quelques
   * dixiemes de seconde avant la premiere trame, et pendant ce temps-la les
   * animaux ne doivent pas se figer.
   */
  private readonly suiviFaune = new SuiviFaune()
  private fauneRecue = false
  /** Apprivoiser, installer, rendre a la nature (voir client/zoo.ts). */
  private readonly zoo: Zoo
  /** La ligne a l'eau, depuis la rive comme depuis la barque (voir client/peche.ts). */
  private readonly peche: Peche
  /** Action de terrain possible ici (creuser, pecher), sinon null. */
  private actionTerrain: ActionTerrain | null = null
  /** Etat deja journalise de la canne, pour n'ecrire que les changements. */
  private canneVisible = false
  /** Zoom a restaurer apres un feu d'artifice, sinon null. */
  private zoomAvantFeu: number | null = null
  private minuterieFeu = 0

  // Placement en cours (mode construction).
  private kindEnPose: BatimentKind | null = null
  private rotEnPose = 0
  private posEnPose: Vec2 | null = null
  private couleursEnPose = { murs: 0, toit: 0, porte: 0 }

  // Boucle.
  /** Ce qui remet le bonhomme sur une case libre quand il se retrouve coince. */
  private readonly filet: FiletAntiBlocage
  /** Le battement d'etat du journal (voir client/log/etat-partie.ts). */
  private readonly battement: BattementEtat
  /** Ce qui abandonne un chemin sur lequel on pietine. */
  private readonly progres = new SurveillanceProgres(() => {
    if (!this.enAttente) return
    this.hud.message('Je n’arrive pas à aller jusque-là.', 2200, 'erreur')
    this.enAttente = null
  })
  /** L'heure du monde vient du serveur; ce module dit comment la suivre. */
  private readonly horlogeSuivie = new HorlogeSuivie()
  private tempsPrecedent = 0
  private resteTick = 0
  private frames = 0
  private compteurFps = 0
  private fps = 0
  private minuterieSauvegarde = 0
  /** Resolution de rendu courante, ajustee automatiquement. */
  private ratioRendu = 1
  /** Nombre de secondes consecutives sous le seuil, pour ne pas osciller. */
  private readonly etatResolution = etatResolutionNeuf()
  private arrete = false
  private boucleId = 0

  private constructor(o: OptionsJeu) {
    const avance = o.progression ?? (() => {})
    this.token = o.sauvegarde.token
    this.sauvegarde = new Sauvegarde(this.token, {
      message: (texte) => this.hud.message(texte, 5000, 'erreur'),
      batimentRefuse: (b, raison) => this.retirerBatimentRefuse(b, raison),
    })
    this.compte = o.compte
    this.chargerProgresEcole()
    // Le serveur a le dernier mot, mais on ne l'ATTEND PAS: le jeu doit
    // s'ouvrir meme si le reseau est mort, et l'ecole n'est pas ce qu'on ouvre
    // en premier. La reponse arrive bien avant qu'on ne pousse la porte de la
    // classe; si elle n'arrive jamais, le stockage local a deja repondu.
    void this.recupererProgresEcoleServeur()
    this.tempsJeu = o.tempsJeu
    this.horloge = horlogeDepuis(this.tempsJeu)
    // Un generateur par sous-systeme: partager un seul flux ferait diverger le
    // client du serveur des la premiere difference de cadence, en multijoueur.
    this.aleaAnimaux = new Alea(o.graine ^ 0x5f3a1c)
    this.aleaRecolte = new Alea(o.graine ^ 0x2b7e15)
    const aleaPeche = new Alea(o.graine ^ 0x7f4a2c)

    // ------------------------------------------------------------- monde
    avance(15, 'Modelage du terrain…')
    this.terrain = new Carte(o.graine)
    definirPathfinder(trouverChemin)
    this.grille = new GrilleNav(this.terrain)

    avance(28, 'Plantation de la vegetation…')
    this.ressources = genererRessources(this.terrain, o.graine)
    // Rejouer les noeuds deja recoltes: le reste vient de la graine.
    const parId = new Map(this.ressources.map((r) => [r.id, r]))
    for (const e of o.ressourcesEpuisees) {
      const r = parId.get(e.ressourceId)
      if (r) {
        r.pv = e.pv
        r.repousseA = e.repousseA
      }
    }
    this.indexRessources = new IndexRessources(this.ressources)
    this.animaux = genererAnimaux(this.terrain, o.graine, ESPECES_PHASE_1)

    // Les collaborateurs, ici et pas plus haut: le monde dont ils ont besoin
    // (terrain, grille, batiments, animaux) n'existe qu'a partir de cette ligne.
    this.battement = new BattementEtat(this.terrain, this.batiments)
    this.filet = new FiletAntiBlocage(this.terrain, this.grille, (texte) => this.hud.message(texte, 2600, 'info'))
    this.peche = new Peche(this.terrain, aleaPeche, {
      message: (texte, duree, genre) => this.hud.message(texte, duree, genre),
      // La prise est demandee au serveur: c'est lui qui tient le sac.
      gagnerPrise: (item) => void this.demanderSac({ quoi: 'peche', kind: item }),
      sauverJoueur: () => void this.sauver(),
    })
    this.zoo = new Zoo(this.terrain, this.batiments, this.animaux, this.token, {
      depenser: (cout) => {
        const id = Object.keys(cout)[0]
        if (id) void this.demanderSac({ quoi: 'consommer', kind: id })
      },
      message: (texte, duree, genre) => this.hud.message(texte, duree, genre),
      sauverAnimal: (a) => void this.sauvegarde.animal(a),
      sauverJoueur: () => void this.sauver(),
    })
    // Les animaux apprivoises sont des EXCEPTIONS a la faune regeneree, comme
    // les noeuds de ressource epuises: on les rejoue par-dessus.
    if (o.animaux && o.animaux.length > 0) this.zoo.appliquerSauvegarde(o.animaux, o.batiments)

    // ------------------------------------------------------------- rendu
    avance(42, 'Allumage du rendu…')
    this.rendu = new THREE.WebGLRenderer({
      canvas: o.toile,
      antialias: true,
      powerPreference: 'high-performance',
    })
    this.ratioRendu = Math.min(window.devicePixelRatio, RATIO_MAX)
    this.rendu.setPixelRatio(this.ratioRendu)
    this.rendu.setSize(window.innerWidth, window.innerHeight, false)
    this.rendu.shadowMap.enabled = true
    this.rendu.shadowMap.type = THREE.PCFSoftShadowMap

    this.scene = new THREE.Scene()
    this.rig = new RigCamera(window.innerWidth, window.innerHeight)

    avance(55, 'Coloration du sol…')
    this.renduTerrain = new RenduTerrain(this.scene, this.terrain)
    this.renduEau = new RenduEau(this.scene, this.terrain)
    // Les deux iles ont leur decor, et chacune son semis: celui de l'ile 1 lit
    // la disposition de ses cinq biomes, celui de l'ile 2 sa table de regions.
    this.renduProps = new RenduProps(this.scene, [
      ...genererProps(this.terrain.ile1, o.graine),
      ...genererPropsIle2(this.terrain.ile2, o.graine),
    ])

    avance(70, 'Lever du soleil…')
    this.ambiance = new Ambiance(this.scene)

    avance(80, 'Reveil des animaux…')
    this.renduRessources = new RenduRessources(this.scene, this.ressources, this.tempsJeu)
    this.renduAnimaux = new RenduAnimaux(this.scene, this.animaux)
    this.renduZoo = new RenduZoo(this.scene)
    this.renduBatiments = new RenduBatiments(this.scene, this.terrain)
    this.renduVehicules = new RenduVehicules(this.scene)
    for (const brut of o.vehicules) {
      // Une ligne abimee fait disparaitre UN vehicule, jamais planter la partie.
      const v = validerVehicule(brut)
      if (v) this.renduVehicules.ajouter(v)
      else journal.warn('vehicule', 'entree ignoree a la lecture du monde', { brut })
    }

    this.renduJoueurs = new RenduJoueurs(this.scene)
    this.renduFeux = new RenduFeux(this.scene)
    this.lampe = new Lampe(this.scene)
    // Le son part a l'explosion, pas au tir: le module audio applique lui-meme
    // le retard distance/340, donc un feu lointain gronde en differe.
    this.renduFeux.onExplosion = (position) => {
      const d = dist(this.joueur.pos.x, this.joueur.pos.z, position.x, position.z)
      audio.feuExplosion(d)
      journal.debug('feu', `explosion a ${d.toFixed(0)} m`)
    }

    // ------------------------------------------------------------- joueur
    avance(88, 'Habillage du bonhomme…')
    this.joueur = joueurNeuf(o.sauvegarde.nom, o.sauvegarde.apparence)
    this.joueur.pos = { x: o.sauvegarde.pos.x, z: o.sauvegarde.pos.z }
    this.joueur.inventaire = o.sauvegarde.inventaire
    this.joueur.besoins = o.sauvegarde.besoins
    this.bonhomme = new Bonhomme(o.sauvegarde.apparence)
    this.scene.add(this.bonhomme.racine)
    this.animation = new AnimationBonhomme(this.bonhomme)
    this.canneEnMain = new CanneEnMain(this.bonhomme.articulations)
    this.majCanneEnMain()

    for (const b of o.batiments) {
      this.batiments.ajouter(b)
      this.renduBatiments.ajouter(b)
      this.bloquerSiPlein(b)
    }
    // Les batiments sont lus APRES le semis du decor et des noeuds: c'est ici,
    // et pas ailleurs, qu'on escamote ce qui se retrouve sous leurs murs.
    this.majCouvertureBatiments()
    // Rejouer les jardins cueillis, comme les noeuds de ressource epuises: un
    // jardin mur est l'etat par defaut, seule la cueillette est une exception.
    for (const j of o.jardins ?? []) this.jardinsPrets.set(j.batimentId, j.repousseA)

    // ------------------------------------------------------------- interface
    avance(94, 'Mise en place de l’interface…')
    this.reglages = chargerReglages()
    this.hud = new Hud(o.couche)
    this.creation = new Creation(o.couche)
    this.menuConstruction = new MenuConstruction(o.couche)
    this.panneauInventaire = new PanneauInventaire(o.couche)
    this.panneauCuisine = new PanneauCuisine(o.couche)
    this.panneauFeux = new PanneauFeux(o.couche)
    this.panneauReglages = new PanneauReglages(o.couche)
    this.panneauAdmin = new PanneauAdmin(o.couche)
    this.voileHorsLigne = new VoileHorsLigne(o.couche)
    this.panneauEcole = new PanneauEcole(o.couche)
    this.panneauMusee = new PanneauMusee(o.couche, {
      lire: () => this.sauvegarde.lireDessins(),
      lireUn: (id) => this.sauvegarde.lireDessin(id),
      accrocher: (titre, traits) => this.sauvegarde.accrocherDessin(titre, traits),
      decrocher: (id) => this.sauvegarde.decrocherDessin(id),
      compte: () => this.compte,
      message: (t, d, g) => this.hud.message(t, d, g),
    })
    this.panneauBibliotheque = new PanneauBibliotheque(o.couche)
    this.panneauAscenseur = new PanneauAscenseur(o.couche)
    this.panneauConfirmation = new PanneauConfirmation(o.couche)
    this.etiquettes = new EtiquettesJoueurs(o.couche)
    this.boussole = new Boussole(o.couche)
    this.chat = new Chat(o.couche)
    // La liaison n'existe pas encore (elle s'ouvre plus tard, et jamais en
    // invite): on la lit a chaque envoi, et son absence est dite au joueur.
    this.chat.onEnvoyer = (texte) => this.salon?.dire(texte) === true
    this.joystick = new JoystickVue(o.couche)
    this.marqueur = new MarqueurDestination(this.scene, this.terrain)

    this.pointeur = new Pointeur(
      o.toile,
      {
        tap: (x, y) => this.surTap(x, y),
        joystickDebut: (x, y) => {
          // Un pouce POSE n'est pas encore une poussee: on n'interrompt rien
          // (ni la hache, ni le chemin en cours) tant qu'il n'a pas quitte la
          // zone morte — voir la premiere poussee ci-dessous.
          this.joystickAPousse = false
          this.joystick.montrer(x, y)
        },
        joystickBouge: (dx, dz, intensite, ecranX, ecranY) => {
          if (!this.joystickAPousse && (dx !== 0 || dz !== 0)) {
            this.joystickAPousse = true
            this.interrompre()
            this.joueur.chemin = []
            this.enAttente = null
            this.butABord = null
            this.marqueur.effacer()
          }
          this.joueur.direction = dx === 0 && dz === 0 ? null : { x: dx, z: dz }
          this.intensiteJoystick = intensite
          // Sans ca le pouce du joystick restait fige au centre: aucune trace a
          // l'ecran de la direction ni de la force de la poussee.
          this.joystick.bouger(ecranX, ecranY, intensite)
        },
        joystickFin: () => {
          this.joystick.cacher()
          this.joueur.direction = null
          this.intensiteJoystick = 0
        },
        pivot: (d) => this.rig.pivoter(d * this.reglages.sensibilite),
        zoom: (f) => this.rig.zoomer(f),
      },
      () => this.rig.azimut,
    )

    this.brancherInterface()
    this.appliquerReglages()
    avance(100, 'Prêt.')
  }

  private intensiteJoystick = 0
  /** Vrai des que le pouce du joystick a quitte la zone morte. */
  private joystickAPousse = false

  static async creer(o: OptionsJeu): Promise<Jeu> {
    const jeu = new Jeu(o)
    // Laisser le navigateur peindre l'ecran de chargement a 100 % avant de
    // rendre la premiere image, qui compile les shaders et bloque un instant.
    await new Promise((r) => requestAnimationFrame(() => r(null)))
    return jeu
  }

  // ------------------------------------------------------------- interface

  private brancherInterface(): void {
    this.hud.onCible = () => this.surPastille()
    this.hud.onBouton = (nom) => {
      void audio.init()
      audio.clic()
      switch (nom) {
        case 'construire':
          if (this.mode === 'construction') this.quitterConstruction()
          else if (this.mode === 'demolition') {
            /*
             * LE BOUTON ALLUME ETEINT CE QU'IL A ALLUME. En demolition il est
             * marque actif, comme en construction — mais l'appuyer rouvrait le
             * menu, ou le seul bouton visible est « Mode démolition », qui la
             * RALLUMAIT (vu au telephone le 13 octobre). On en sort ici.
             */
            this.quitterConstruction()
            this.hud.message('Mode démolition terminé.', 2000, 'info')
            journal.input('construire', 'mode demolition quitte (bouton)')
          } else {
            this.mode = 'construction'
            this.menuConstruction.ouvrir(this.joueur.inventaire)
            this.hud.marquerActif('construire')
          }
          break
        case 'inventaire':
          this.panneauInventaire.ouvrir(this.joueur.inventaire, this.cibleMeuble === 'cuisine')
          break
        case 'ancre':
          this.basculerAncreCourante('bouton')
          break
        case 'verrou':
          void this.basculerVerrouCourant('bouton')
          break
        case 'monde':
          this.partirVersAutreMonde()
          break
        case 'lampe': {
          const allumee = this.lampe.basculer()
          this.hud.marquerActif(allumee ? 'lampe' : null)
          this.hud.message(allumee ? '🔦 Lampe allumée' : 'Lampe éteinte', 1400, 'info')
          journal.input('lampe', allumee ? 'allumee' : 'eteinte')
          break
        }
        case 'feux':
          if (!this.horloge.nuit) {
            this.hud.message('Les feux d’artifice ne se lancent que la nuit. Reviens après 20 h 30.', 3200, 'erreur')
            audio.erreurInterface()
            return
          }
          this.panneauFeux.ouvrir(this.joueur.inventaire, this.horloge.nuit)
          break
        case 'personnage':
          void this.ouvrirCreateur()
          break
        case 'reglages':
          this.panneauReglages.ouvrir()
          break
        case 'admin':
          this.panneauAdmin.ouvrir(this.token, lireLocal('jeu.compte') ?? '')
          break
      }
    }

    this.menuConstruction.onVehicule = (kind) => {
      this.mode = 'construction'
      this.vehiculeEnPose = kind
      this.kindEnPose = null
      journal.input('construire', `vehicule choisi: ${kind}`)
    }

    // Un outil ne se pose pas: il tombe dans le sac et le panneau reste ouvert,
    // pour qu'on voie tout de suite qu'on l'a.
    this.menuConstruction.onOutil = (idRecette) => this.cuisiner(idRecette)

    this.menuConstruction.onChoix = (kind) => {
      this.vehiculeEnPose = null
      this.kindEnPose = kind
      this.rotEnPose = 0
      const base = couleursDefaut(kind)
      this.couleursEnPose = { ...base }
      journal.input('construire', `modele choisi: ${kind}`)
    }
    this.menuConstruction.onCouleurs = (c) => {
      this.couleursEnPose = c
    }
    this.menuConstruction.onTourner = () => {
      this.rotEnPose = (this.rotEnPose + 1) % 4
      journal.input('construire', `rotation ${this.rotEnPose}`)
    }
    this.menuConstruction.onPoser = () => this.poser()
    this.menuConstruction.onDemolition = (actif) => {
      this.mode = actif ? 'demolition' : 'jeu'
      this.kindEnPose = null
      this.renduBatiments.cacherFantome()
      this.hud.marquerActif(actif ? 'construire' : null)
      this.hud.message(
        actif
          ? estAdmin(this.compte)
            ? '🧨 Mode démolition : touche une construction.'
            : '🧨 Mode démolition : touche une de tes constructions.'
          : 'Mode démolition terminé.',
        3000,
        'info',
      )
    }
    this.menuConstruction.onAnnuler = () => this.quitterConstruction()

    this.panneauInventaire.onManger = (id) => this.mangerObjet(id)
    this.panneauCuisine.onCuisiner = (idRecette) => this.cuisiner(idRecette)
    this.panneauEcole.onPartieFinie = (r) => this.recompenserEcole(r)
    this.panneauFeux.onLancer = (couleur, forme) => this.lancerFeu(couleur, forme)
    this.panneauFeux.onFabriquer = () => this.cuisiner('fusee')

    this.panneauAdmin.onMessage = (texte, genre) => this.hud.message(texte, 3600, genre)
    this.panneauAdmin.onMondeChange = (raison) => {
      // Le monde a change sous les pieds du joueur (remise a zero, restauration):
      // on recharge plutot que de laisser une scene qui ne correspond plus a rien.
      journal.warn('admin', `monde modifie par l'administration: ${raison}`)
      location.reload()
    }

    this.panneauReglages.onReglage = (cle, valeur) => {
      journal.input('reglages', `${cle} = ${String(valeur)}`)
      this.reglages = this.panneauReglages.valeurs
      this.appliquerReglages()
      if (cle === 'recentrer') this.rig.alignerSur90()
      if (cle === 'deconnexion') this.onDeconnexion?.()
    }

    // Gardes en champ pour pouvoir les retirer: sans ca, un futur "retour au
    // menu" qui recreerait un Jeu empilerait les ecouteurs.
    this.surRedim = () => this.redimensionner()
    this.surClavier = (e: KeyboardEvent) => this.surTouche(e)
    addEventListener('resize', this.surRedim)
    addEventListener('keydown', this.surClavier)
  }

  private appliquerReglages(): void {
    audio.muet(this.reglages.muet)
    audio.volume(this.reglages.volume)
    this.hud.montrerFps(this.reglages.fps)
    const ombres = this.reglages.ombres
    this.rendu.shadowMap.enabled = ombres !== 'aucune'
    const taille = ombres === 'haute' ? 2048 : 1024
    const carte = this.ambiance.lumiere.soleil.shadow
    if (carte.mapSize.width !== taille) {
      carte.mapSize.set(taille, taille)
      carte.map?.dispose()
      carte.map = null
    }
    this.rendu.shadowMap.needsUpdate = true
  }

  private redimensionner(): void {
    const l = window.innerWidth
    const h = window.innerHeight
    this.rig.redimensionner(l, h)
    this.rendu.setPixelRatio(this.ratioRendu)
    this.rendu.setSize(l, h, false)
    journal.info('fenetre', `redimensionnee ${l}x${h}`)
  }

  private surTouche(e: KeyboardEvent): void {
    if (e.key === 'r' && this.mode === 'construction') {
      this.rotEnPose = (this.rotEnPose + 1) % 4
      journal.input('construire', `rotation clavier ${this.rotEnPose}`)
    }
    if (e.key === 'Escape' && this.mode === 'construction') this.quitterConstruction()
    if (e.key === 'v' || e.key === 'V') this.basculerVehicule()
    if (e.key === 'a' || e.key === 'A') this.basculerAncreCourante('clavier')
    if (e.key === 'l' || e.key === 'L') {
      const allumee = this.lampe.basculer()
      this.hud.marquerActif(allumee ? 'lampe' : null)
      journal.input('lampe', `${allumee ? 'allumee' : 'eteinte'} au clavier`)
    }
  }

  private async ouvrirCreateur(): Promise<void> {
    const r = await this.creation.ouvrir(this.joueur.apparence, this.joueur.nom, true)
    if (!r) return
    this.joueur.apparence = r.apparence
    this.bonhomme.appliquer(r.apparence)
    this.hud.message('Nouveau look adopté.', 2000, 'succes')
    void this.sauver()
  }

  // ------------------------------------------------------------- saisie

  /**
   * Interrompt une action verrouillee en cours.
   *
   * 'recolte' fait partie des etats verrouilles: tant qu'il dure, la
   * simulation refuse de deplacer le bonhomme. Si le joueur tape ailleurs
   * pendant qu'il coupe un arbre, sans ca il frappait dans le vide pour
   * toujours, incapable de bouger.
   */
  private interrompre(): void {
    if (this.joueur.etat === 'recolte') {
      this.joueur.etat = 'immobile'
      this.minuterieCoup = 0
      journal.debug('recolte', 'interrompue par le joueur')
    }
  }

  /**
   * OU LE DOIGT TOMBE, quand on est sur une terrasse.
   *
   * `pointSol` marche le long du RELIEF et rien d'autre: un tap ne peut donc
   * pas viser nativement une surface surelevee, et viser le relief pendant
   * qu'on est trois metres plus haut designe un point du jardin qu'on voit
   * a travers sa propre dalle. Le choix, ecrit ici une fois pour toutes:
   *
   *   TANT QU'ON EST A L'ETAGE, LE TAP EST PROJETE SUR LE PLAN DE LA TERRASSE,
   *   ET IL NE VAUT QUE DANS L'EMPRISE DU BATIMENT.
   *
   * Une dalle est exactement un plan y = constante, donc l'intersection est
   * exacte et ne coute rien. Hors de l'emprise il n'y a pas de plancher: le tap
   * est ignore plutot que traduit en « marche par la », ce qui ferait tenter de
   * sortir du toit — et l'acrotere repousserait le bonhomme a chaque pas, ce
   * qui se lit comme un jeu casse. Le joystick, lui, continue de marcher
   * exactement comme en bas: c'est la deuxieme facon de se deplacer la-haut.
   */
  private surTap(clientX: number, clientY: number): void {
    void audio.init()
    const rayon = rayonDepuisEcran(this.rig.camera, clientX, clientY, window.innerWidth, window.innerHeight)

    if (this.joueur.niveau > 0) {
      const dalle = hauteurNiveau(this.batiments.liste(), this.joueur.pos.x, this.joueur.pos.z, this.joueur.niveau)
      const surTerrasse = dalle === null ? null : pointPlan(rayon, dalle)
      if (!surTerrasse) {
        journal.input('tap', 'hors de la terrasse')
        return
      }
      const dessus = batimentEtageContenant(this.batiments.liste(), surTerrasse.x, surTerrasse.z)
      /*
       * LE BATIMENT TROUVE DOIT MONTER JUSQU'AU NIVEAU DU JOUEUR. Le plan du
       * tap est a la hauteur de SA dalle: depuis le toit de l'hotel (7 m), un
       * rayon qui coupe ce plan au-dessus de la villa voisine designe un point
       * 3,5 m au-dessus de sa terrasse — accepter la villa ordonnerait de
       * marcher hors du toit, et l'acrotere repousserait a chaque pas.
       */
      if (!dessus || niveauxDe(dessus.kind) < this.joueur.niveau) {
        const acces = accesEtage(this.batimentSousMesPieds()?.kind ?? 'villa_etage') === 'ascenseur'
          ? 'l’ascenseur'
          : 'l’escalier'
        this.hud.message(`Ici, c’est le vide. Reprends ${acces} pour descendre.`, 1800, 'info')
        journal.input('tap', 'hors de la terrasse')
        return
      }
      this.taperLePoint(surTerrasse)
      return
    }

    // `pointSurface` et non `pointSol`: sur l'eau, ce qu'on vise flotte sur la
    // nappe, pas sur le fond. Voir client/render/camera.ts.
    const p = pointSurface(rayon, this.terrain)
    if (!p) {
      journal.input('tap', 'hors du monde')
      // Ligne a l'eau, un tap FERRE, ou qu'il tombe — y compris sur la mer
      // au-dela du bord de la carte, que l'on regarde justement depuis la
      // barque ancree pres de la rive ouest (vu au telephone le 13 octobre).
      if (this.peche.enCours && this.peche.ferrerMaintenant(this.joueur)) return
      /*
       * UN TAP QUI NE TOMBE NULLE PART DOIT LE DIRE, au moins au pilote.
       *
       * Aux commandes, le tap n'est pas UNE facon de jouer, c'est LA SEULE:
       * l'avion se dirige en designant un endroit. Un tap hors de la carte ne
       * renvoyait rien du tout — ni cap, ni message, ni son — et l'appareil
       * continuait tranquillement d'orbiter autour de sa derniere cible. Vu du
       * siege, ca se lit « l'avion refuse de sortir de son orbite », c'est-a-dire
       * qu'on accuse le pilotage alors que l'ordre n'est jamais parti.
       *
       * C'est le « geste sans effet » deja paye avec l'ancre de la barque, et il
       * faisait mal ici parce que la mer est precisement l'endroit vers lequel on
       * veut partir. Le remede est le meme: le jeu le DIT.
       */
      const monture = this.renduVehicules.vehicules().find((v) => v.id === this.joueur.vehicule)
      // A pied et en voiture aussi: la mer se dessine bien au-dela du bord de la
      // carte, et un tap muet sur l'eau qu'on voit se lit comme un jeu qui
      // n'a pas entendu (vu au telephone le 13 octobre).
      if (monture && estAvion(monture.kind)) {
        this.hud.message('Là-bas, il n’y a plus de monde. Vise plus près.', 2400, 'info')
      } else {
        this.hud.message('Le monde s’arrête là : vise plus près.', 2000, 'info')
      }
      return
    }
    this.taperLePoint(this.noeudVise(rayon, p) ?? p)
  }

  /**
   * Le noeud dont la SILHOUETTE est sous le doigt (voir `noeudSousLeRayon`):
   * toucher la cime d'un arbre vise l'arbre, pas le sol quelques metres
   * derriere lui. A pied seulement: aux commandes, un tap designe un endroit.
   */
  private noeudVise(rayon: THREE.Ray, sol: Vec2): Vec2 | null {
    if (this.joueur.vehicule !== null) return null
    const o = rayon.origin
    const d = rayon.direction
    const horiz = Math.hypot(d.x, d.z)
    if (horiz < 1e-6) return null
    const tSol = Math.hypot(sol.x - o.x, sol.z - o.z) / horiz
    // Une cime de 7 m se projette au sol a moins de 10 m de son pied.
    const candidats = this.indexRessources.dansRayon(sol.x, sol.z, 12, this.tempsJeu, this.batiments.liste())
    const n = noeudSousLeRayon(candidats, o, d, tSol)
    if (!n) return null
    journal.input('tap', `silhouette ${n.kind}#${n.id} sous le doigt`)
    return { x: n.pos.x, z: n.pos.z }
  }

  /**
   * Ce qu'un tap declenche, une fois l'ecran converti en point du monde.
   *
   * Separe de `surTap` pour que la passerelle de test entre par ICI plutot que
   * de rejouer une version simplifiee: `viserTest` ne connaissait ni le mode
   * demolition ni la ligne a l'eau, si bien qu'un test pouvait croire taper
   * comme un joueur tout en empruntant un chemin different — exactement les deux
   * gestes qui etaient casses.
   */
  private taperLePoint(p: Vec2): void {
    /*
     * PENDANT UNE DESCENTE, LE TAP NE SERT A RIEN, et il faut le dire.
     *
     * Le toboggan tient la direction jusqu'en bas: accepter un ordre de marche
     * ici ferait viser un point du bassin, quitter la ligne du toboggan et
     * arriver a cote — et le joueur ne comprendrait pas pourquoi. Meme regle que
     * le joystick en vol, qui ne sert a rien non plus et n'en fait pas mystere.
     */
    if (this.descente) {
      journal.input('tap', 'ignore: descente de glissade en cours')
      return
    }

    this.interrompre()

    // Une ligne a l'eau prend la main sur tout le reste: le tap sert a ferrer.
    // Vrai depuis la rive comme depuis la barque — c'est la meme peche.
    if (this.peche.enCours && this.peche.ferrerMaintenant(this.joueur)) return

    // A bord d'un vehicule, un tap sert a pecher, pas a marcher.
    if (this.joueur.vehicule !== null && this.gererTapABord(p)) return

    if (this.mode === 'demolition') {
      this.demolir(p)
      return
    }

    /*
     * EN POSE, UN TAP NE FAIT QUE DEPLACER LE FANTOME. Il posait aussi des que
     * l'emplacement PRECEDENT etait valable (sa validite datait de l'image
     * d'avant): le premier tap pour choisir sa place batissait donc a l'aveugle,
     * a un endroit jamais vu en apercu, et payait. Seul le bouton « Poser »
     * batit — un seul geste, une seule regle.
     */
    if (this.mode === 'construction' && (this.vehiculeEnPose || this.kindEnPose)) {
      this.posEnPose = p
      journal.input('construire', `deplacement du fantome (${p.x.toFixed(1)}, ${p.z.toFixed(1)})`)
      return
    }

    /*
     * Le tap vise-t-il quelque chose d'interactif? PAS DEPUIS UN VEHICULE: a
     * bord, toucher pres d'un arbre conduisait jusqu'a lui et l'abattait a la
     * hache par la portiere (vu au telephone le 13 octobre), alors que
     * l'infobulle n'annoncait que « Descendre ». A bord, un tap conduit; ce
     * qu'on y fait d'autre (pecher, descendre) est deja passe plus haut.
     */
    const cible = this.joueur.vehicule === null ? this.cibleAuPoint(p) : null
    if (cible) {
      this.enAttente = cible.action
      const d = dist(this.joueur.pos.x, this.joueur.pos.z, cible.pos.x, cible.pos.z)
      journal.input('tap', `cible ${cible.action.quoi} a ${d.toFixed(1)} m`)
      // Une ligne se lance de loin: aller jusqu'a l'eau ferait nager le bonhomme
      // au lieu de pecher, alors que c'est justement ce que la canne evite.
      if (d <= this.porteeCible(cible.action)) {
        this.declencherAction()
      } else if (!this.conduireVers(cible.pos)) {
        this.hud.message('Impossible d’aller là.', 1600, 'erreur')
        this.enAttente = null
      } else {
        this.poserMarqueur(cible.pos)
        // Une cible s'atteint a portee, pas au centimetre: c'est la portee qui juge.
        this.direSiCheminPartiel(cible.pos, this.porteeCible(cible.action) + MANQUE_CHEMIN_PARTIEL)
      }
      return
    }

    this.enAttente = null
    this.butABord = this.joueur.vehicule !== null ? { x: p.x, z: p.z } : null
    if (this.conduireVers(p)) {
      const f = this.joueur.chemin[this.joueur.chemin.length - 1] ?? p
      this.poserMarqueur(f)
      journal.input('tap', `marche vers (${f.x.toFixed(1)}, ${f.z.toFixed(1)})`, {
        points: this.joueur.chemin.length,
      })
      /*
       * UN VEHICULE QU'ON ENVOIE HORS DE SON MILIEU s'arrete au bord, et doit le
       * DIRE des le tap. Le chemin finit sur le sable sec a trois ou quatre
       * metres de l'eau, c'est-a-dire sous la tolerance du chemin partiel: la
       * voiture arrivait, s'arretait, et rien ne disait pourquoi (vu au
       * telephone le 14 octobre).
       */
      const v = this.joueur.vehicule !== null
        ? this.renduVehicules.vehicules().find((x) => x.id === this.joueur.vehicule)
        : undefined
      const milieu = v ? vehiculeDef(v.kind).milieu : null
      if (v && milieu !== 'air' && this.terrain.estEau(p.x, p.z) !== (milieu === 'eau')) {
        const def = vehiculeDef(v.kind)
        this.hud.message(
          v.kind === 'barque'
            ? 'La barque ne va pas à terre : elle s’arrête au bord.'
            : `${LeNom(def)} ne va pas dans l’eau : ${def.feminin ? 'elle' : 'il'} s’arrête au bord.`,
          2600,
          'info',
        )
        journal.info('vehicule', `vise hors de son milieu en (${p.x.toFixed(1)}, ${p.z.toFixed(1)})`)
      } else {
        this.direSiCheminPartiel(p)
      }
    } else {
      this.hud.message('Aucun chemin par là.', 1600, 'erreur')
      journal.input('tap', `sans chemin vers (${p.x.toFixed(1)}, ${p.z.toFixed(1)})`)
    }
  }

  /**
   * UN CHEMIN QUI S'ARRETE EN ROUTE DOIT LE DIRE.
   *
   * L'A* rend son meilleur chemin PARTIEL quand le but est hors d'atteinte
   * (de l'autre cote d'une falaise, budget epuise): le bonhomme partait, puis
   * s'arretait a mi-chemin sans un mot. On juge sur la DISTANCE entre la fin du
   * chemin et le point vise, pas sur `dernierCheminComplet` — global, il dirait
   * l'etat du dernier A* lance, qui n'est pas forcement celui-ci (la nage et la
   * ligne droite n'en lancent pas).
   */
  private direSiCheminPartiel(vise: Vec2, tolere = MANQUE_CHEMIN_PARTIEL): void {
    const f = this.joueur.chemin[this.joueur.chemin.length - 1]
    if (!f) return
    const manque = dist(f.x, f.z, vise.x, vise.z)
    if (manque <= tolere) return
    this.hud.message('Je ne peux pas aller plus loin par là.', 1800)
    journal.info('nav', `chemin partiel: s'arrete a ${manque.toFixed(1)} m du point vise`)
  }

  /**
   * Pose l'anneau de destination, sauf a l'etage.
   *
   * Le marqueur EPOUSE LE TERRAIN, secteur par secteur, pour ne pas s'enterrer
   * dans une pente: sur une terrasse il irait donc chercher le relief trois
   * metres plus bas et se dechirerait sous la dalle. Une terrasse est plate et
   * tient dans un ecran: on s'en passe plutot que de lui inventer un second
   * mode de placage.
   */
  private poserMarqueur(p: Vec2): void {
    if (this.joueur.niveau > 0) return
    this.marqueur.poser(p.x, this.terrain.hauteur(p.x, p.z), p.z)
  }

  /**
   * A quelle distance cette action se declenche. Le bras pour tout, sauf lancer
   * une ligne: avec une canne on peche de loin.
   */
  private porteeCible(a: NonNullable<ActionEnAttente>): number {
    if (a.quoi === 'terrain' && a.action === 'pecher') return porteeLancer(this.joueur.inventaire)
    // La meme portee que `peutMonter`, sinon on marche jusqu'au vehicule pour
    // s'entendre dire qu'on est trop loin.
    if (a.quoi === 'vehicule') return REACH + 1
    return REACH
  }

  /** Ce que le joueur a designe en tapant a cet endroit du sol. */
  private cibleAuPoint(p: Vec2): { action: NonNullable<ActionEnAttente>; pos: Vec2 } | null {
    /*
     * A L'ETAGE, IL N'Y A QUE DEUX CHOSES A TOUCHER.
     *
     * Les arbres, les betes, les vehicules, les jardins: tout cela est trois
     * metres plus bas. Les proposer la-haut, ce serait faire marcher le
     * bonhomme vers quelque chose qu'il ne peut pas atteindre sans redescendre
     * — et l'acrotere le repousserait a chaque pas. On ne garde donc que ce qui
     * existe VRAIMENT sur la terrasse, et c'est encore la distance qui arbitre.
     */
    if (this.joueur.niveau > 0) {
      const port = this.batiments.portableProche(p.x, p.z, TOLERANCE_CIBLE, this.joueur.niveau)
      const esc = this.batiments.escalierProche(p.x, p.z, TOLERANCE_CIBLE + 0.6)
      const asc = this.batiments.ascenseurProche(p.x, p.z, TOLERANCE_CIBLE + 0.6)
      // La table de la salle a manger: c'est le PLAN qui dit ou elle est
      // dressee et a quel niveau — jamais « le modele est-il l'hotel ? ».
      const table = this.batiments.tableEtageProche(p.x, p.z, TOLERANCE_CIBLE, this.joueur.niveau)
      // Le comptoir du restaurant d'une aerogare: meme regle, meme table.
      const resto = this.batiments.restaurantProche(p.x, p.z, TOLERANCE_CIBLE, this.joueur.niveau)
      const posPort = port ? positionPortable(port) : null
      const posEsc = esc ? positionEscalier(esc) : null
      const posAsc = asc ? positionAscenseur(asc) : null
      const posTable = table ? positionTable(table) : null
      const posResto = resto ? positionRestaurant(resto) : null
      // LE HAUT D'UNE GLISSADE: la passerelle du parc aquatique. C'est le PLAN
      // qui dit qu'il y en a une, et `glissadeAuPoint` ne repond que depuis le
      // niveau ou elle part — proposer « Glisser » depuis le bord du bassin,
      // trois metres plus bas, serait le mensonge deja paye par « Couper —
      // Arbre » annonce a quelqu'un sur son toit.
      const gliss = this.batiments.glissadeProche(p.x, p.z, TOLERANCE_CIBLE, this.joueur.niveau)
      const posGliss = gliss ? cheminGlissade(gliss.batiment, gliss.index)?.depart ?? null : null
      const dGliss = posGliss ? dist(p.x, p.z, posGliss.x, posGliss.z) : Infinity
      const dPort = posPort ? dist(p.x, p.z, posPort.x, posPort.z) : Infinity
      const dEsc = posEsc ? dist(p.x, p.z, posEsc.x, posEsc.z) : Infinity
      const dAsc = posAsc ? dist(p.x, p.z, posAsc.x, posAsc.z) : Infinity
      const dTable = posTable ? dist(p.x, p.z, posTable.x, posTable.z) : Infinity
      const dResto = posResto ? dist(p.x, p.z, posResto.x, posResto.z) : Infinity
      // C'est la DISTANCE qui arbitre, comme partout: un ordre fixe finirait
      // par rendre l'une des cibles inatteignable.
      const min = Math.min(dPort, dEsc, dAsc, dTable, dResto, dGliss)
      if (gliss && posGliss && dGliss === min) {
        return { action: { quoi: 'glissade', id: gliss.batiment.id, index: gliss.index }, pos: posGliss }
      }
      if (port && posPort && dPort === min) return { action: { quoi: 'portable', id: port.id }, pos: posPort }
      if (resto && posResto && dResto === min) return { action: { quoi: 'restaurant', id: resto.id }, pos: posResto }
      if (table && posTable && dTable === min) return { action: { quoi: 'table', id: table.id }, pos: posTable }
      if (asc && posAsc && dAsc === min) return { action: { quoi: 'ascenseur', id: asc.id }, pos: posAsc }
      if (esc && posEsc) return { action: { quoi: 'escalier', id: esc.id }, pos: posEsc }
      return null
    }

    /*
     * UN MUR SEPARE CE QU'IL SEPARE. Un point touche DANS une maison (vue de
     * dehors, c'est son toit) ne designe pas le buisson colle a sa facade: le
     * tap sur le salon d'une cabane partait cueillir les baies de l'autre cote
     * du mur, et le bonhomme restait coince dans l'angle (campagne du 13
     * octobre). Ce que le rayon touche VRAIMENT — la silhouette d'un arbre
     * devant la maison — a deja ete tranche avant, par `noeudSousLeRayon`.
     */
    const sous = batimentContenant(this.batiments.liste(), p.x, p.z)
    const murs = sous && batimentDef(sous.kind).habitable ? sous : null
    const memeCote = (q: Vec2) => murs === null || dansEmprise(murs, q.x, q.z)
    const rBrut = this.indexRessources.plusProche(p.x, p.z, TOLERANCE_CIBLE, this.tempsJeu, this.batiments.liste())
    const r = rBrut && memeCote(rBrut.pos) ? rBrut : null
    const aBrut = animalLePlusProche(this.animaux, p.x, p.z, TOLERANCE_CIBLE)
    const aProche = aBrut && memeCote(aBrut.pos) ? aBrut : null
    // Une bete a qui l'on n'a rien a offrir — l'animal d'un autre, une bete
    // sauvage sans friandise en poche — ne prend pas le tap: la luciole d'un
    // autre compte rendait l'arbre d'a cote impossible a couper.
    const a = aProche && this.animalQuiCompte(aProche) ? aProche : null

    /*
     * ENTRE UN ARBRE ET UNE BETE, LA BETE PASSE D'ABORD.
     *
     * L'arbre gagnait toujours, sans condition. Or la foret est dense et c'est
     * justement la que vivent les animaux: un lapin y est presque toujours a
     * moins de deux metres d'un tronc, et viser le lapin abattait l'arbre
     * derriere lui. Le nourrissage — donc l'apprivoisement, donc tout le zoo —
     * etait injouable exactement la ou il doit se jouer. C'est le piege deja
     * note pour « creuser gagnait toujours contre pecher ».
     *
     * L'arbre ne reprend la main que s'il est NETTEMENT plus pres: un tronc est
     * gros, immobile et facile a viser une seconde fois, tandis qu'une bete est
     * petite et bouge entre le moment ou on la voit et celui ou on la touche.
     */
    const dR = r ? dist(p.x, p.z, r.pos.x, r.pos.z) : Infinity
    const dA = a ? dist(p.x, p.z, a.pos.x, a.pos.z) : Infinity

    /*
     * ON MONTE EN TOUCHANT LE VEHICULE.
     *
     * Monter n'a jamais existe qu'au clavier (« v »), et le jeu se joue au
     * doigt: on pouvait donc batir une voiture et une barque sans pouvoir y
     * monter, pendant que l'infobulle promettait « Monter dans voiture ». Le
     * journal en garde 44 invitations et pas un seul embarquement.
     *
     * L'arbitrage est celui de partout ailleurs, la DISTANCE: la coque rend 0
     * des qu'on tape dessus, donc le vehicule gagne contre l'arbre derriere lui
     * sans jamais voler un tap qui ne le visait pas. A bord, on n'entre pas
     * ici: c'est `gererTapABord` qui a deja tranche.
     */
    if (this.joueur.vehicule === null) {
      const veh = vehiculeProche(this.renduVehicules.vehicules(), p.x, p.z, TOLERANCE_CIBLE)
      const dV = veh ? distanceVehicule(veh, p.x, p.z) : Infinity
      // UN AVION EN VOL N'EST PAS UNE CIBLE: sa coque passe au-dessus du
      // bonhomme, et la distance se mesure a plat. Sans cette exception, un tap
      // sous un appareil en approche proposait d'y monter depuis la pelouse.
      if (veh && !this.avionEnVol(veh) && dV <= dR && dV <= dA) {
        return { action: { quoi: 'vehicule', id: veh.id }, pos: veh.pos }
      }
    }

    if (r && dR + MARGE_ANIMAL_VISE < dA) return { action: { quoi: 'recolte', id: r.id }, pos: r.pos }
    if (r && !a) return { action: { quoi: 'recolte', id: r.id }, pos: r.pos }

    const meuble = this.batiments.meubleProche(p.x, p.z, TOLERANCE_CIBLE, this.interieur)

    // CHEZ SOI, LE MEUBLE PASSE AVANT LA BETE ADOPTEE. Elle erre autour de son
    // panier, donc pres du lit et du comptoir: un tap pour dormir la rendait a
    // la nature pendant que l'infobulle annoncait « Dormir ».
    if (a && !(meuble && estFamilier(a) && a.ancre !== null)) {
      // Sur un familier, le geste devient « installe-toi la »: le jeu dira sur
      // place ce qui manque si l'endroit ne convient pas. Sur une bete sauvage,
      // c'est manger, la seule facon de l'apprivoiser.
      if (estFamilier(a)) return { action: { quoi: 'adopter', id: a.id }, pos: a.pos }
      if (this.zoo.friandisePour(a, this.joueur.inventaire)) return { action: { quoi: 'nourrir', id: a.id }, pos: a.pos }
      // Rien a lui offrir: c'est peut-etre l'arbre derriere lui qu'on visait.
      if (r) return { action: { quoi: 'recolte', id: r.id }, pos: r.pos }
    }

    /*
     * L'ESCALIER EST UN MEUBLE COMME UN AUTRE pour l'arbitrage: c'est la
     * DISTANCE au point touche qui tranche, pas un ordre de priorite fixe. Un
     * ordre fixe finit toujours par exclure l'un des deux — la lecon de
     * « creuser gagnait toujours contre pecher ».
     */
    const escalier = this.batiments.escalierProche(p.x, p.z, TOLERANCE_CIBLE + 0.6)
    const posEscalier = escalier ? positionEscalier(escalier) : null
    const dEscalier = posEscalier ? dist(p.x, p.z, posEscalier.x, posEscalier.z) : Infinity
    const ascenseur = this.batiments.ascenseurProche(p.x, p.z, TOLERANCE_CIBLE + 0.6)
    const posAscenseur = ascenseur ? positionAscenseur(ascenseur) : null
    const dAscenseur = posAscenseur ? dist(p.x, p.z, posAscenseur.x, posAscenseur.z) : Infinity

    if (meuble && meuble.distance <= dEscalier && meuble.distance <= dAscenseur) {
      const pos = meuble.meuble === 'cuisine' ? p : p
      return { action: { quoi: meuble.meuble, id: meuble.batiment.id }, pos }
    }
    if (ascenseur && posAscenseur && dAscenseur <= dEscalier) {
      return { action: { quoi: 'ascenseur', id: ascenseur.id }, pos: posAscenseur }
    }
    if (escalier && posEscalier) {
      return { action: { quoi: 'escalier', id: escalier.id }, pos: posEscalier }
    }

    if (this.interieur === null) {
      const porte = this.batiments.porteProche(p.x, p.z, TOLERANCE_CIBLE + 0.6)
      if (porte) return { action: { quoi: 'entrer', id: porte.id }, pos: p }
    }

    // La cabane a collations sert gratuitement.
    const cabane = this.batiments.cabaneProche(p.x, p.z, TOLERANCE_CIBLE + 1.5)
    if (cabane) return { action: { quoi: 'collation', id: cabane.id }, pos: cabane.pos }

    /*
     * L'ECOLE, LE MUSEE ET LA BIBLIOTHEQUE S'OUVRENT PAR LA PROXIMITE, depuis
     * le seuil comme depuis l'interieur — et par un ancrage de meuble, jamais:
     * un pupitre ajoute a `ANCRAGE_MEUBLE` en aurait pose un dans toutes les
     * maisons du monde, y compris la tente. C'est la lecon deja payee pour
     * l'ecole, et elle vaut pour les deux nouveaux.
     *
     * C'est la DISTANCE qui arbitre entre eux, pas un ordre fixe: un village
     * serre peut avoir les trois cote a cote, et un ordre fixe finirait par
     * rendre l'un des trois inatteignable — le piege de « creuser gagnait
     * toujours contre pecher ».
     */
    const village: { quoi: 'ecole' | 'musee' | 'bibliotheque'; b: Batiment }[] = []
    const ecole = this.batiments.ecoleProche(p.x, p.z, TOLERANCE_CIBLE + 2)
    if (ecole) village.push({ quoi: 'ecole', b: ecole })
    const musee = this.batiments.villageProche('musee', p.x, p.z, TOLERANCE_CIBLE + 2)
    if (musee) village.push({ quoi: 'musee', b: musee })
    const biblio = this.batiments.villageProche('bibliotheque', p.x, p.z, TOLERANCE_CIBLE + 2)
    if (biblio) village.push({ quoi: 'bibliotheque', b: biblio })
    if (village.length > 0) {
      let meilleur = village[0] as { quoi: 'ecole' | 'musee' | 'bibliotheque'; b: Batiment }
      for (const v of village) {
        if (dist(p.x, p.z, v.b.pos.x, v.b.pos.z) < dist(p.x, p.z, meilleur.b.pos.x, meilleur.b.pos.z)) meilleur = v
      }
      return { action: { quoi: meilleur.quoi, id: meilleur.b.id }, pos: meilleur.b.pos }
    }

    // Le jardin se cueille.
    const jardin = this.batiments.jardinProche(p.x, p.z, TOLERANCE_CIBLE + 1)
    if (jardin) return { action: { quoi: 'jardin', id: jardin.id }, pos: jardin.pos }

    // Creuser le sable ou pecher: pas de noeud, c'est le terrain qui repond.
    const terre = actionVisee(this.terrain, p.x, p.z, this.joueur.pos, porteeLancer(this.joueur.inventaire))
    if (terre) return { action: { quoi: 'terrain', action: terre, pos: p }, pos: p }

    return null
  }

  /**
   * Les troncs et les rochers, pour le pas partage: le MEME index que le
   * serveur (`noeudsSolidesServeur`), a la meme heure du monde et avec les
   * memes batiments — un noeud sous une maison n'arrete personne.
   */
  private noeudsSolides(): NoeudsSolides {
    return { index: this.indexRessources, tempsJeu: this.tempsJeu, batiments: this.batiments.liste() }
  }

  private conduireVers(cible: Vec2): boolean {
    const monde = {
      terrain: this.terrain,
      grille: this.grille,
      batiments: this.batiments.liste(),
      interieur: this.interieur,
    }
    /*
     * EN VOITURE, L'ITINERAIRE EST A SA TAILLE. Elle suivait celui du pieton,
     * trace pour un point: son emprise accrochait la premiere pente ou le
     * premier mur au bord du couloir, et elle s'arretait (« bloquée ») la ou un
     * detour existait. On planifie d'abord sur la grille DILATEE; s'il n'y a
     * pas de chemin entier a sa taille, on reprend celui du pieton, et c'est
     * `signalerObstacle` qui dira ou ca coince.
     */
    const v = this.joueur.vehicule === null ? null : this.renduVehicules.vehicules().find((x) => x.id === this.joueur.vehicule)
    if (v && vehiculeDef(v.kind).milieu === 'terre') {
      this.grilleVoiture ??= vueDilatee(this.grille, 1)
      // Une voiture garee a l'etroit (la plage, au ras de la rive) est sur une
      // case que la vue dilatee barre: l'A* repartirait alors de la case libre
      // la plus proche, parfois DERRIERE elle, et la voiture foncerait dans la
      // berge des le premier tick. La, seul l'itineraire du pieton a un sens.
      const ici = this.grilleVoiture.caseDe(this.joueur.pos.x, this.joueur.pos.z)
      if (this.grilleVoiture.estLibre(ici.cx, ici.cz) && conduireVers(this.joueur, cible, { ...monde, grille: this.grilleVoiture })) {
        const f = this.joueur.chemin[this.joueur.chemin.length - 1]
        if (f && dist(f.x, f.z, cible.x, cible.z) <= MANQUE_CHEMIN_PARTIEL) return true
      }
      journal.debug('chemin', 'voiture: aucun itineraire a sa taille, on prend celui du pieton')
    }
    return conduireVers(this.joueur, cible, monde)
  }

  private declencherAction(): void {
    const a = this.enAttente
    if (!a) return
    switch (a.quoi) {
      case 'recolte': {
        const r = this.ressources.find((x) => x.id === a.id)
        if (!r || !estDisponible(r, this.tempsJeu, this.batiments.liste())) {
          this.enAttente = null
          return
        }
        this.joueur.etat = 'recolte'
        this.joueur.chemin = []
        this.minuterieCoup = this.resteAvantCoup()
        this.animation.jouer('recolte')
        break
      }
      case 'nourrir': {
        const animal = this.animaux.find((x) => x.id === a.id)
        if (animal) this.zoo.donnerAManger(animal, this.joueur, this.tempsJeu)
        this.enAttente = null
        break
      }
      case 'adopter': {
        const animal = this.animaux.find((x) => x.id === a.id)
        if (animal) this.zoo.installerAnimal(animal)
        this.enAttente = null
        break
      }
      case 'entrer': {
        this.entrer(a.id)
        this.enAttente = null
        break
      }
      case 'vehicule': {
        this.basculerVehicule(a.id)
        this.enAttente = null
        break
      }
      case 'ecole': {
        this.panneauEcole.ouvrir('ecole', this.niveauEcole)
        journal.input('ecole', 'panneau ouvert depuis l ecole')
        this.enAttente = null
        break
      }
      case 'musee': {
        void this.panneauMusee.ouvrir()
        this.enAttente = null
        break
      }
      case 'bibliotheque': {
        this.panneauBibliotheque.ouvrir()
        journal.input('bibliotheque', 'panneau ouvert depuis la bibliotheque')
        this.enAttente = null
        break
      }
      case 'pupitre': {
        // LE MEME panneau que l'ecole. Le pupitre n'ouvre rien de neuf: il
        // donne une cible PRECISE a l'interieur de la classe, la ou l'ecole
        // entiere ne repondait qu'a la proximite du batiment.
        this.panneauEcole.ouvrir('ecole', this.niveauEcole)
        journal.input('ecole', 'panneau ouvert depuis le pupitre')
        this.enAttente = null
        break
      }
      /*
       * ON PIOCHE TANT QU'ON EN A LA FORCE, sans avoir a retaper.
       *
       * L'attente n'est PAS consommee ici, contrairement a presque tout le
       * reste: c'est elle qui fait durer le piochage, coup apres coup, comme la
       * recolte d'un arbre — a ceci pres qu'aucun noeud ne tombe jamais. Ce qui
       * l'arrete est la fatigue (`peutMiner`), un pas de cote hors de portee,
       * ou un autre tap. Redemander un tap tous les 1,3 s au fond d'une mine
       * serait le contraire d'un jeu qui se joue au doigt.
       */
      case 'filon': {
        if (!this.commencerAPiocher()) {
          this.enAttente = null
          break
        }
        this.joueur.etat = 'recolte'
        this.joueur.chemin = []
        this.minuterieCoup = this.resteAvantCoup()
        this.animation.jouer('recolte')
        journal.input('mine', `piochage au filon de ${a.id}`)
        break
      }
      case 'escalier': {
        this.changerNiveau(this.joueur.niveau > 0 ? 0 : 1, a.id, 'escalier')
        this.enAttente = null
        break
      }
      case 'glissade': {
        this.commencerDescente(a.id, a.index)
        this.enAttente = null
        break
      }
      case 'ascenseur': {
        // L'ascenseur ouvre ses BOUTONS: trois destinations ne se devinent
        // pas d'un tap. Le panneau rend le niveau choisi, et `changerNiveau`
        // ne fait que le PROPOSER — `appliquerEntree` tranche au tick suivant.
        const b = this.batiments.get(a.id)
        if (b) {
          const id = a.id
          const noms = planEtageDe(b.kind)?.nomsNiveaux ?? []
          this.panneauAscenseur.ouvrir(this.joueur.niveau, niveauxDe(b.kind), noms, (n) => {
            this.changerNiveau(n, id, 'ascenseur')
          })
        }
        this.enAttente = null
        break
      }
      case 'portable': {
        // Le MEME panneau que l'ecole, en mode portable: maths et calculatrice.
        // Il n'y avait rien a ecrire de neuf, seulement un endroit d'ou l'ouvrir.
        this.panneauEcole.ouvrir('portable', this.niveauEcole)
        journal.input('portable', 'panneau ouvert depuis le bureau de la terrasse')
        this.enAttente = null
        break
      }
      case 'restaurant': {
        this.mangerAuRestaurant()
        this.joueur.etat = 'mange'
        this.minuterieAction = DUREE_REPAS
        this.animation.jouer('mange')
        this.enAttente = null
        break
      }
      case 'cuisine': {
        this.panneauCuisine.ouvrir(this.joueur.inventaire)
        journal.input('cuisine', 'panneau ouvert')
        this.enAttente = null
        break
      }
      case 'table': {
        // On cuisine au comptoir, on MANGE a table: le comptoir ouvre les
        // recettes, la table ouvre le sac pour choisir son plat.
        this.panneauInventaire.ouvrir(this.joueur.inventaire, false)
        this.hud.message('🍽️ À table ! Choisis ce que tu manges.', 2600, 'info')
        journal.input('table', 'panneau ouvert')
        this.enAttente = null
        break
      }
      case 'lit': {
        this.dormirIci()
        this.enAttente = null
        break
      }
      case 'jardin': {
        this.cueillirJardin(a.id)
        this.enAttente = null
        break
      }
      case 'collation': {
        this.prendreCollation()
        this.enAttente = null
        break
      }
      case 'terrain': {
        if (a.action === 'pecher') {
          // Depuis la rive, c'est le MEME petit jeu que depuis la barque: on
          // attend que ca morde, puis on touche a temps. Une prise silencieuse au
          // bout d'un compte a rebours n'aurait pas ete de la peche.
          //
          // L'ATTENTE EST CONSOMMEE ICI. Creuser occupe le bonhomme ('recolte'),
          // ce qui suffit a l'exclure du declenchement differe; pecher le laisse
          // debout et disponible, si bien que l'attente non effacee relancait la
          // ligne a CHAQUE image: le delai avant la touche repartait de zero sans
          // arret et rien ne mordait jamais.
          this.enAttente = null
          this.peche.lancer(a.pos, this.joueur)
          break
        }
        this.joueur.etat = 'recolte'
        this.joueur.chemin = []
        this.minuterieAction = DUREE_ACTION[a.action]
        this.animation.jouer('recolte')
        audio.coup('miner')
        journal.input(a.action, `debut (${a.pos.x.toFixed(1)}, ${a.pos.z.toFixed(1)})`)
        break
      }
    }
  }

  // ------------------------------------------------------------- actions

  private entrer(id: string): void {
    this.interieur = id
    this.joueur.interieur = id
    this.rig.entrerInterieur()
    this.hud.message('Tu es entré. Le toit s’efface pour te laisser voir.', 2600, 'info')
    journal.input('entrer', `batiment ${id}`)
  }

  private sortir(): void {
    if (this.interieur === null) return
    journal.input('sortir', `batiment ${this.interieur}`)
    this.interieur = null
    this.joueur.interieur = null
    // Ni en train d'en descendre une glissade: la demolition, comme la sortie,
    // doit tout rendre au sol d'un seul coup.
    if (this.descente) this.finirDescente(this.descente, 'bloquee')
    // On ne reste pas a l'etage d'un batiment ou l'on n'est plus: sans ca, une
    // demolition laisserait le bonhomme marcher en l'air. `niveauEffectif` le
    // ramenerait au sol au tick suivant, mais autant le dire tout de suite.
    this.joueur.niveau = 0
    this.rig.sortirInterieur()
  }

  /**
   * Monte a l'etage, ou en redescend.
   *
   * LE NIVEAU N'EST PAS POSE ICI, il est PROPOSE. Ce qu'on ecrit est le niveau
   * VOULU; c'est `appliquerEntree` — la meme fonction des deux cotes du fil —
   * qui decide, au tick suivant, s'il tient vraiment a cet endroit. Poser le
   * niveau pour de bon reviendrait a repondre a la question a deux endroits,
   * et deux regles pour la meme question finissent toujours par diverger.
   */
  private changerNiveau(niveau: number, id: string, via: 'escalier' | 'ascenseur'): void {
    if (niveau === this.joueur.niveau) return
    this.joueur.niveau = niveau
    // Un itineraire calcule en bas n'a plus aucun sens en haut: la grille de
    // navigation n'a qu'une seule couche, et le chemin traverserait l'acrotere.
    this.joueur.chemin.length = 0
    this.joueur.direction = null
    this.marqueur.effacer()
    if (via === 'ascenseur') {
      // Le MEME nom que sur le bouton qu'on vient de presser: une table
      // unique, celle du plan (`nomsNiveaux`).
      const nom = planEtageDe(this.batiments.get(id)?.kind ?? 'hotel')?.nomsNiveaux?.[niveau]
      this.hud.message(`🛗 ${nom ?? `Étage ${niveau}`}`, 2600, 'info')
    } else if (niveau > 0) {
      this.hud.message('Tu montes sur la terrasse.', 2600, 'info')
    } else {
      this.hud.message('Tu redescends au salon.', 2200, 'info')
    }
    journal.input('etage', `niveau ${niveau} dans ${id} (${via})`)
  }

  // ------------------------------------------------------------- glissades

  /**
   * ON S'ELANCE. Le tap a designe le haut d'une glissade, on part.
   *
   * Rien n'est pose ici: ni la position, ni le niveau. On enregistre seulement
   * le chemin, et c'est la boucle de simulation qui fabrique, tick apres tick,
   * les entrees ordinaires qui font descendre — voir `entreeDeDescente`. Le
   * serveur les rejoue comme n'importe quel pas de marche.
   */
  private commencerDescente(id: string, index: number): void {
    const b = this.batiments.get(id)
    if (!b) return
    const chemin = cheminGlissade(b, index)
    if (!chemin) return
    const nom = glissadesDe(b.kind)[index]?.nom ?? 'la glissade'
    // Un itineraire ou une poussee de joystick n'ont plus de sens: la descente
    // decide de la direction a chaque tick.
    this.joueur.chemin.length = 0
    this.joueur.direction = null
    this.marqueur.effacer()
    this.descente = { batiment: id, index, chemin, parcouru: 0, temps: 0 }
    this.hud.message(`Tu t’élances — ${nom} !`, 2200, 'info')
    // Le bruit de l'eau qui coule dans le toboggan: pas de son de plus a
    // synthetiser, celui du pas dans l'eau dit exactement ce qu'il faut.
    audio.pas('eau')
    journal.input('glissade', `depart de « ${nom} » (${index}) dans ${id}`, {
      x: chemin.depart.x,
      z: chemin.depart.z,
    })
  }

  /**
   * OU L'ON EN EST de la descente: la position REELLE projetee sur l'axe du
   * toboggan. Voir `DescenteGlissade.parcouru` pour la raison.
   */
  private avancementGlissade(d: DescenteGlissade): number {
    const c = d.chemin
    return (this.joueur.pos.x - c.depart.x) * c.sens.x + (this.joueur.pos.z - c.depart.z) * c.sens.z
  }

  /**
   * L'ENTREE DU TICK PENDANT UNE DESCENTE — une entree ORDINAIRE, et c'est tout
   * le propos.
   *
   * On vise le point du toboggan qu'on devrait atteindre au prochain tick, a
   * `VITESSE_GLISSADE`, avec la forme du bonhomme comme partout ailleurs (un
   * joueur fatigue glisse un peu moins vite: c'est le prix du pas partage, et
   * il est juste). Le NIVEAU reste celui du haut jusqu'a l'arrivee: en changer a
   * mi-course reviendrait a resoudre le pas contre les murs du rez-de-chaussee
   * alors qu'on est encore en l'air.
   */
  private entreeDeDescente(d: DescenteGlissade, n: number): Entree {
    const vise = avancerGlissade(d.chemin, d.parcouru + VITESSE_GLISSADE * TICK_DT)
    return {
      n,
      dt: TICK_DT,
      dx: vise.pos.x - this.joueur.pos.x,
      dz: vise.pos.z - this.joueur.pos.z,
      v: VITESSE_GLISSADE,
      f: formeDe(this.joueur.besoins),
      niv: d.chemin.niveauHaut,
    }
  }

  /** Fait avancer la descente d'un tick, et decide si elle est finie. */
  private majDescente(d: DescenteGlissade): void {
    d.parcouru = clamp(this.avancementGlissade(d), 0, d.chemin.longueur)
    d.temps += TICK_DT
    if (d.chemin.longueur - d.parcouru <= FIN_GLISSADE) {
      this.finirDescente(d, 'arrivee')
      return
    }
    if (d.temps > DUREE_MAX_GLISSADE) this.finirDescente(d, 'bloquee')
  }

  /**
   * ON ARRIVE. Le niveau redescend a celui du bassin — PROPOSE, comme partout:
   * c'est `appliquerEntree` qui tranche au tick suivant, sur la position de
   * depart, exactement comme pour l'escalier.
   */
  private finirDescente(d: DescenteGlissade, raison: 'arrivee' | 'bloquee'): void {
    this.descente = null
    this.joueur.niveau = d.chemin.niveauBas
    this.joueur.chemin.length = 0
    this.joueur.direction = null
    arreter(this.joueur)
    if (raison === 'arrivee') {
      this.hud.message('Plouf ! Tu arrives dans le bassin.', 2200, 'info')
      audio.pas('eau')
    } else {
      // Un geste sans effet est pire qu'un geste refuse: on le DIT.
      this.hud.message('La glissade s’est arrêtée en chemin.', 2200, 'erreur')
      journal.warn('glissade', `descente bloquee dans ${d.batiment} apres ${d.temps.toFixed(1)} s`)
    }
    journal.input('glissade', `${raison} au bas de la glissade ${d.index} de ${d.batiment}`, {
      x: this.joueur.pos.x,
      z: this.joueur.pos.z,
    })
  }

  /**
   * LA HAUTEUR DU BONHOMME PENDANT UNE DESCENTE, ou null s'il n'en fait pas.
   *
   * Elle ne regarde que le RENDU: la simulation ne connait que des niveaux
   * entiers, et le client est libre d'interpoler entre les deux tant qu'il ne
   * touche pas a (x, z) — c'est ecrit dans shared/sim/batiments.ts. Le profil
   * vient de `hauteurGlissade`, LA MEME fonction qui dessine le toboggan: sans
   * ca, on verrait le bonhomme descendre a cote de sa glissade.
   */
  private hauteurDescente(): number | null {
    const d = this.descente
    if (!d) return null
    const b = this.batiments.get(d.batiment)
    if (!b) return null
    // La position reelle, pas `d.parcouru`: celui-ci ne bouge qu'au tick, et le
    // rendu tourne trois fois plus souvent.
    const t = clamp(this.avancementGlissade(d) / d.chemin.longueur, 0, 1)
    return b.y + hauteurGlissade(t)
  }

  /** Le batiment a etage sur lequel le bonhomme se tient, sinon null. */
  private batimentSousMesPieds(): Batiment | null {
    return batimentEtageContenant(this.batiments.liste(), this.joueur.pos.x, this.joueur.pos.z)
  }

  private recolter(): void {
    const a = this.enAttente
    if (!a || a.quoi !== 'recolte') return
    const r = this.ressources.find((x) => x.id === a.id)
    if (!r) {
      this.terminerRecolte()
      return
    }
    if (dist(this.joueur.pos.x, this.joueur.pos.z, r.pos.x, r.pos.z) > REACH + 0.6) {
      this.terminerRecolte()
      return
    }

    const def = ressourceDef(r.kind)
    // Un coup de hache coute de l'energie: `depenserRecolte` existait mais
    // n'etait appelee nulle part, donc recolter etait gratuit.
    this.joueur.besoins = depenserRecolte(this.joueur.besoins)
    const resultat = frapper(r, this.tempsJeu, this.aleaRecolte)
    audio.coup(def.action)
    this.renduRessources.secouer(r)

    /*
     * LE COUP FINAL PAIE, ET C'EST LE SERVEUR QUI PAIE.
     *
     * `frapper` decide encore ICI si le noeud tombe — c'est de la simulation,
     * partagee, et le serveur la reverifie a sa facon en tenant les pv du noeud
     * (`/api/ressource`). Ce qui ENTRE dans le sac, en revanche, ne peut plus
     * venir du navigateur: il annonce quel noeud il a abattu, le serveur relit
     * la meme table et donne.
     */
    if (Object.keys(resultat.gains).length > 0) {
      audio.ramasser()
      void this.demanderSac({ quoi: 'ressource', kind: r.kind }).then((recus) => {
        const entrees = Object.entries(recus ?? {}) as [ItemId, number][]
        if (entrees.length === 0) {
          // Un refus ou un reseau coupe ne doit pas se lire comme « rien
          // tombe »: le noeud est abattu, il faut dire que le sac n'a pas suivi.
          this.hud.message(`${def.nom} : la récolte n’a pas pu être enregistrée.`, 2600, 'erreur')
          journal.warn('inv', `recolte de ${r.kind} non payee par le serveur`)
          return
        }
        this.hud.message(entrees.map(([id, n]) => `+${n} ${objet(id).nom}`).join('  '), 1800, 'succes')
        for (const [id, n] of entrees) {
          journal.info('inv', `${id} +${n}`, { total: quantite(this.joueur.inventaire, id) })
        }
      })
    }

    if (resultat.epuise) {
      audio.ressourceEpuisee(def.action)
      this.indexRessources.majNoeud(r)
      this.renduRessources.majNoeud(r, this.tempsJeu)
      // Le message du gain part avec la reponse du serveur (ci-dessus): c'est
      // lui qui sait ce qu'il a donne, minerai et champignons compris.
      void this.sauvegarde.ressource(r)
      this.terminerRecolte()
    }
  }

  // ----------------------------------------------------------------- avions

  /**
   * L'ETAT DE VOL D'UN APPAREIL, cree a la demande.
   *
   * IL N'EST PAS PERSISTE, et c'est un choix: ce qui est ecrit en base, c'est un
   * avion GARE quelque part (position, cap, couleur), jamais « en train de
   * virer a quarante metres au-dessus du lac ». Un pilote qui ferme son onglet
   * en plein vol retrouve donc son appareil a l'aeroport d'ou il est parti —
   * c'est la seule reponse qui ne laisse pas des avions fantomes suspendus dans
   * le ciel du monde partage.
   */
  private etatAvion(v: Vehicule): EtatAvion {
    const connu = this.vols.get(v.id)
    if (connu) return connu
    // Un appareil qu'on decouvre est gare: on cherche sous ses roues a quel
    // aeroport il appartient.
    const sous = pisteSousAvion(this.batiments.liste(), v.pos, 2)
    const neuf = etatAvionNeuf(sous?.batiment.id ?? null)
    this.vols.set(v.id, neuf)
    return neuf
  }

  /** La piste d'un aeroport connu par son identifiant, ou null. */
  private pisteParId(id: string | null): Piste | null {
    if (!id) return null
    const b = this.batiments.get(id)
    return b ? pisteDe(b) : null
  }

  /**
   * Ce dont le pas de vol a besoin: les deux pistes concernees, et OU se garer.
   *
   * Le point d'arret est choisi ici parce que le jeu est le seul a connaitre
   * les autres appareils: se garer toujours au meme metre finirait par empiler
   * deux avions au meme endroit, et rien dans la simulation pure ne pourrait
   * s'en apercevoir.
   */
  private contexteAvion(v: Vehicule, e: EtatAvion): {
    pisteBase: Piste | null
    pisteDestination: Piste | null
    arret: number
  } {
    const pisteDestination = this.pisteParId(e.destination)
    return {
      pisteBase: this.pisteParId(e.base),
      pisteDestination,
      arret: pisteDestination ? this.placeLibre(pisteDestination, v.id) : RECUL_PARKING,
    }
  }

  /**
   * La premiere place de stationnement libre le long de la piste.
   *
   * Trois places espacees de dix metres: c'est un peu plus que l'envergure, donc
   * deux appareils gares ne se touchent jamais. Au-dela, on se range sur la
   * derniere — mieux vaut deux avions serres qu'un avion qui roule sans fin.
   */
  private placeLibre(piste: Piste, moi: string): number {
    const places = [RECUL_PARKING, RECUL_PARKING + 10, RECUL_PARKING + 20]
    for (const place of places) {
      const p = { x: piste.seuilProche.x + piste.sens.x * place, z: piste.seuilProche.z + piste.sens.z * place }
      const occupee = this.renduVehicules.vehicules().some(
        (autre) => autre.id !== moi && estAvion(autre.kind) && Math.hypot(autre.pos.x - p.x, autre.pos.z - p.z) < 8,
      )
      if (!occupee) return place
    }
    return places[places.length - 1] ?? RECUL_PARKING
  }

  /**
   * L'appareil vient de se garer: on ECRIT sa nouvelle position.
   *
   * C'est le seul moment ou un avion est sauvegarde, et c'est ce qui rend son
   * voyage visible pour les autres: parti de l'aerodrome de la plage, il est
   * desormais gare a l'aeroport de la montagne, pour tout le monde et apres
   * rechargement.
   */
  private avionGare(v: Vehicule): void {
    const sous = pisteSousAvion(this.batiments.liste(), v.pos, 2)
    const etat = this.vols.get(v.id)
    if (etat) etat.base = sous?.batiment.id ?? etat.base
    this.hud.message('🛬 Posé. Bienvenue !', 2600, 'succes')
    journal.input('avion', `pose a ${sous?.batiment.kind ?? 'un aeroport'}`, {
      id: v.id,
      x: v.pos.x.toFixed(1),
      z: v.pos.z.toFixed(1),
    })
    void this.sauvegarde.vehicule(v).then((rep) => {
      // La pose d'un vehicule DEBITE son cout: ici l'appareil existe deja, donc
      // le serveur ne prend rien — il ne fait que ranger sa nouvelle place.
      if (rep.ok) {
        if (rep.inventaire) this.adopterSac(rep.inventaire)
        return
      }
      // Un range refuse ne se tait plus: l'appareil retournerait a son
      // aeroport de depart au prochain chargement, sans explication.
      this.hud.message(`🛬 Atterrissage non enregistré : ${rep.erreur}`, 3500, 'erreur')
    })
  }

  /** Vrai si cet appareil n'est pas gare: il roule ou il vole. */
  private avionEnVol(v: Vehicule): boolean {
    if (!estAvion(v.kind)) return false
    return !estGare(this.etatAvion(v))
  }

  /**
   * CE QU'UN TAP VEUT DIRE QUAND ON EST AUX COMMANDES.
   *
   * Deux ordres, deux seulement, et c'est toute l'interface de pilotage: un
   * point du monde (« va la-bas, puis tourne au-dessus ») ou un aeroport
   * (« pose-toi la »). Rien a apprendre, rien au clavier — la regle du projet:
   * une action qui n'existe qu'au clavier n'existe pas.
   */
  private ordonnerVol(v: Vehicule, p: Vec2): void {
    const e = this.etatAvion(v)
    const aero = this.batiments.aeroportAuPoint(p.x, p.z, 8)
    if (aero) {
      const piste = pisteDe(aero)
      if (!piste) return
      /*
       * L'AEROPORT OU L'ON EST DEJA GARE n'est pas une destination: l'appareil
       * decollait pour se reposer aussitot, sans un mot (vu au telephone le 14
       * octobre, en visant un point a quelques metres de la piste). On le dit.
       */
      if (estGare(e) && pisteSousAvion(this.batiments.liste(), v.pos, 2)?.batiment.id === aero.id) {
        this.hud.message('Tu es déjà à cet aéroport : touche plus loin pour décoller.', 2800, 'info')
        journal.input('avion', `tap sur son propre aeroport ${aero.id}, pas de decollage`)
        return
      }
      viserAeroport(e, aero.id, piste.centre)
      const nom = batimentDef(aero.kind).nom
      this.hud.message(
        auSol(e) ? `🛫 Décollage vers ${nom}…` : `🛬 On se pose à ${nom}.`,
        2800,
        'info',
      )
      journal.input('avion', `cap sur l aeroport ${aero.id}`, { phase: e.phase })
      return
    }
    viserPoint(e, p)
    this.hud.message(
      auSol(e) ? '🛫 Décollage !' : '✈️ En route — l’avion tournera au-dessus.',
      2400,
      'info',
    )
    journal.input('avion', `cap sur (${p.x.toFixed(1)}, ${p.z.toFixed(1)})`, { phase: e.phase })
  }

  /**
   * PARTIR POUR L'AUTRE MONDE.
   *
   * Le pilotage ordinaire se fait en DESIGNANT un endroit, et c'est justement
   * ce qui ne marche pas ici: l'autre ile est a sept cents metres de mer, donc
   * hors de l'ecran meme au zoom de vol. On ne peut pas toucher ce qu'on ne
   * voit pas. Le bouton dit donc la destination au lieu de la faire viser.
   *
   * Une fois l'ordre donne, il n'y a plus rien de particulier: `viserAeroport`
   * est le MEME appel que pour l'aeroport d'a cote, et la machine a etats de
   * `shared/sim/avion.ts` fait le voyage en volant, comme le reste — decollage,
   * transit, point d'attente dans l'axe, finale. Aucune teleportation, aucun
   * ecran de chargement, et le trajet dure ce qu'il dure: une bonne
   * quarantaine de secondes.
   */
  private partirVersAutreMonde(): void {
    const v = this.renduVehicules.vehicules().find((x) => x.id === this.joueur.vehicule)
    if (!v || !estAvion(v.kind)) return
    const e = this.etatAvion(v)
    if (!estGare(e)) return
    const aero = this.batiments.aeroportAutreMonde(v.pos.x, v.pos.z, v.retour)
    if (!aero) return
    const piste = pisteDe(aero)
    if (!piste) return
    /*
     * On note d'ou l'on part: au retour, le meme bouton ramenera l'appareil a
     * CET aeroport, pas au plus proche de l'autre rive. Le champ voyage avec le
     * range de l'atterrissage (`avionGare`), donc il survit au rechargement.
     */
    const depart = pisteSousAvion(this.batiments.liste(), v.pos, 2)?.batiment.id ?? e.base
    if (depart) v.retour = depart
    viserAeroport(e, aero.id, piste.centre)
    const monde = numeroDeMonde(aero.pos.x, aero.pos.z)
    this.hud.message(`🛫 Décollage pour le monde ${monde}…`, 3200, 'info')
    journal.input('avion', `cap sur le monde ${monde} (${aero.id})`, {
      de: `${v.pos.x.toFixed(0)},${v.pos.z.toFixed(0)}`,
      vers: `${aero.pos.x.toFixed(0)},${aero.pos.z.toFixed(0)}`,
    })
  }

  /** Ce que l'infobulle dit au pilote, selon la phase du vol. */
  private indiceAvion(v: Vehicule): string {
    const e = this.etatAvion(v)
    switch (e.phase) {
      case 'gare':
        return 'Touche un endroit pour décoller — ou un aéroport'
      case 'decollage':
        return '🛫 Décollage…'
      case 'montee':
        return '🛫 En montée'
      case 'croisiere':
        return '✈️ En vol — touche ailleurs pour changer de cap'
      case 'orbite':
        return '✈️ En orbite — touche un aéroport pour te poser'
      case 'transit':
        return '🛬 Cap sur l’aéroport'
      case 'alignement':
        return '🛬 On s’aligne avec la piste'
      case 'finale':
        return '🛬 En finale'
      case 'roulage':
        return '🛬 Au sol, on roule vers l’aérogare'
    }
  }

  /**
   * MANGER AU RESTAURANT DE L'AEROGARE. Gratuit, comme les collations de la
   * plage: il n'y a pas de monnaie dans ce jeu.
   *
   * Le plat vient du SERVEUR (`{quoi:'restaurant'}`), qui choisit dans sa liste
   * — un client ne s'offre pas un festin — puis le bonhomme le mange sur place,
   * par le meme chemin que n'importe quel repas (`mangerObjet`), donc avec le
   * meme debit du sac et le meme effet sur la faim.
   */
  private mangerAuRestaurant(): void {
    if (this.tempsJeu < this.restaurantPret) {
      const reste = Math.ceil((this.restaurantPret - this.tempsJeu) / 1000)
      this.hud.message(`La cuisine prépare la suite (${reste} s).`, 2200, 'info')
      audio.erreurInterface()
      return
    }
    this.restaurantPret = this.tempsJeu + DELAI_COLLATION
    void this.demanderSac({ quoi: 'restaurant' }).then((recus) => {
      const lignes = Object.entries(recus ?? {}) as [ItemId, number][]
      const premier = lignes[0]
      if (!premier) {
        this.hud.message('La cuisine est fermée.', 2200, 'erreur')
        return
      }
      const [id] = premier
      this.hud.message(`${objet(id).icone} ${objet(id).nom} — offert par la maison !`, 3000, 'succes')
      journal.input('restaurant', `sert ${id}`)
      // On le mange sur place: c'est un restaurant, pas une epicerie.
      this.mangerObjet(id)
    })
  }

  // ------------------------------------------------------------------ mine

  /**
   * Vrai s'il reste de quoi lever la pioche. Sinon, on le DIT.
   *
   * Le seul refus du jeu qui vienne de la fatigue, et il ne coute rien: le
   * filon est inepuisable, il sera la au reveil. Se taire aurait laisse le
   * bonhomme frapper une roche qui ne rend plus rien, ce qui est exactement le
   * genre de geste sans effet que ce projet a deja paye avec l'ancre de la
   * barque.
   */
  private commencerAPiocher(): boolean {
    if (peutMiner(this.joueur.besoins)) return true
    this.hud.message('Tu es trop fatigué pour piocher. Remonte manger ou dormir.', 3000, 'info')
    audio.erreurInterface()
    journal.info('mine', 'piochage refuse: energie a plat')
    return false
  }

  /**
   * UN COUP DE PIOCHE. L'energie est retiree ICI (elle n'appartient qu'au
   * client, comme la faim), le LOT vient du serveur (`produitMine`): le
   * navigateur ne s'accorde pas un minerai, il annonce qu'il a pioche.
   */
  private piocher(): void {
    const a = this.enAttente
    if (!a || a.quoi !== 'filon') return
    const b = this.batiments.get(a.id)
    if (!b) {
      this.arreterDePiocher()
      return
    }
    const f = positionMeuble(b, 'filon')
    // On s'est ecarte du front de taille: on ne pioche pas la roche de loin.
    if (dist(this.joueur.pos.x, this.joueur.pos.z, f.x, f.z) > REACH + 0.6) {
      this.arreterDePiocher()
      return
    }
    if (!peutMiner(this.joueur.besoins)) {
      this.hud.message('Tu es à bout de forces. La mine t’attendra.', 3200, 'info')
      audio.erreurInterface()
      journal.info('mine', 'piochage arrete: energie a plat')
      this.arreterDePiocher()
      return
    }

    this.joueur.besoins = depenserCoupMine(this.joueur.besoins)
    audio.coup('miner')
    void this.demanderSac({ quoi: 'mine' }).then((recus) => {
      const entrees = Object.entries(recus ?? {}) as [ItemId, number][]
      if (entrees.length === 0) return
      audio.ramasser()
      this.hud.message(entrees.map(([id, n]) => `+${n} ${objet(id).nom}`).join('  '), 1800, 'succes')
      for (const [id, n] of entrees) journal.info('inv', `${id} +${n}`, { via: 'mine' })
    })
  }

  /** Fin d'une seance de piochage: on range la pioche et on sauve le bonhomme. */
  private arreterDePiocher(): void {
    this.enAttente = null
    if (this.joueur.etat === 'recolte') this.joueur.etat = 'immobile'
    void this.sauver()
  }

  /**
   * Se servir a la cabane de la plage. GRATUIT: il n'y a pas de monnaie dans ce
   * jeu, c'est une destination, pas un commerce. Un petit delai evite seulement
   * de vider le comptoir en restant appuye.
   */
  private prendreCollation(): void {
    if (this.tempsJeu < this.collationPrete) {
      const reste = Math.ceil((this.collationPrete - this.tempsJeu) / 1000)
      this.hud.message(`Laisse-lui le temps d’en préparer une autre (${reste} s).`, 2200, 'info')
      return
    }

    // C'est la CABANE qui sert, donc le serveur: il choisit la gaterie et
    // l'ajoute. Le client ne s'en offre plus une lui-meme.
    void this.demanderSac({ quoi: 'collation' }).then((recus) => {
      const lignes = Object.entries(recus ?? {}) as [ItemId, number][]
      const premier = lignes[0]
      if (premier) this.hud.message(`${objet(premier[0]).icone} ${objet(premier[0]).nom} — offert !`, 2600, 'succes')
    })
    this.collationPrete = this.tempsJeu + DELAI_COLLATION

    this.joueur.etat = 'mange'
    this.minuterieAction = 0.8
    this.animation.jouer('mange')
    audio.ramasser()
    journal.input('collation', 'gaterie prise a la cabane')
    void this.sauver()
  }

  /** La clef du progres scolaire: par COMPTE, pas par navigateur. */
  private cleProgresEcole(): string {
    return `jeu.ecole.${this.compte ?? 'invite'}`
  }

  private chargerProgresEcole(): void {
    const brut = lireLocal(this.cleProgresEcole())
    if (!brut) return
    try {
      const lu = JSON.parse(brut) as { progres?: ProgresEcole; niveau?: number }
      if (lu.progres) this.progresEcole = lu.progres
      const n = lu.niveau
      if (typeof n === 'number' && n >= 1 && n <= 6) this.niveauEcole = Math.round(n) as Niveau
    } catch (e) {
      // Une preference abimee ne doit jamais empecher de jouer: on repart a neuf.
      journal.warn('ecole', `progres illisible, remis a zero: ${String(e)}`)
    }
  }

  /**
   * Ecrit le progres AUX DEUX ENDROITS: le stockage local d'abord (il ne peut
   * pas echouer et sert de secours hors ligne), le serveur ensuite.
   *
   * Le niveau choisi, lui, ne part pas: c'est une preference de l'enfant qui
   * joue ici et maintenant, pas un acquis. Deux enfants partagent la maison, et
   * ils ne partagent pas toujours le compte de la meme facon.
   */
  private enregistrerProgresEcole(): void {
    ecrireLocal(this.cleProgresEcole(), JSON.stringify({ progres: this.progresEcole, niveau: this.niveauEcole }))
    // Un invite n'a pas de compte: le serveur n'aurait nulle part ou le ranger
    // et repondrait 401. Il joue avec son navigateur, et c'est tres bien.
    if (this.compte === null || this.progresEcole === null) return
    void this.sauvegarde.ecole(this.progresEcole)
  }

  /**
   * Va chercher le progres du compte sur le serveur, au demarrage.
   *
   * ON GARDE LE PLUS AVANCE DES DEUX, compte en parties jouees. Ce n'est pas
   * de la coquetterie: on arrive ici avec ce que le navigateur avait garde,
   * qui peut etre en avance (derniere partie jouee hors ligne, avant que le
   * serveur ne revienne) ou tres en retard (nouveau telephone, cache vide).
   * Prendre aveuglement la reponse du serveur effacerait le premier cas;
   * l'ignorer ne reparerait pas le second. Le desaccord se resorbe tout seul a
   * la partie suivante, qui repousse le total au serveur.
   */
  private async recupererProgresEcoleServeur(): Promise<void> {
    if (this.compte === null) return
    const distant = await this.sauvegarde.lireEcole()
    if (!distant) return
    const local = this.progresEcole
    if (local && local.parties >= distant.parties) {
      if (local.parties > distant.parties) {
        journal.info('ecole', `progres local en avance (${local.parties} parties contre ${distant.parties})`)
      }
      return
    }
    this.progresEcole = distant
    // On le recopie aussitot dans le stockage local: c'est lui qui repondra si
    // la prochaine partie se joue sans reseau. L'aller-retour vers le serveur
    // qu'il declenche au passage renvoie ce qu'on vient d'en recevoir — un
    // ecrivain unique vaut mieux qu'une exception a retenir.
    this.enregistrerProgresEcole()
    journal.info('ecole', `progres repris du serveur: ${distant.parties} parties, ${distant.points} points`)
  }

  /**
   * Ce qu'une partie d'ecole rapporte.
   *
   * Le proprietaire a voulu que ca donne quelque chose dans le monde. Deux
   * garde-fous, sinon l'ecole devient une mine et non un jeu:
   *
   * — on ne paie QUE ce qui a ete reussi (les points valent deja les justes
   *   multiplies par le niveau), donc cliquer au hasard ne rapporte rien;
   * — le rendement decroit, et une partie ratee ne coute rien. On ne perd
   *   jamais dans ce jeu, l'ecole ne fait pas exception.
   *
   * Le panneau n'appelle ceci que sur une serie TERMINEE, jamais sur un
   * abandon: quitter ne doit pas payer.
   */
  private recompenserEcole(r: ResultatPartie): void {
    this.progresEcole = fusionnerProgres(this.progresEcole, r)
    this.niveauEcole = r.niveau
    this.enregistrerProgresEcole()

    /*
     * CE QUE L'ECOLE PAIE — LA REGLE EST DANS `shared/`, ET LE SERVEUR L'APPLIQUE.
     *
     * Elle vivait ici. Elle en est sortie le jour ou le sac est passe au
     * serveur: une recompense que seul le navigateur sait calculer est une
     * recompense que seul le navigateur peut s'accorder. On annonce donc le
     * RESULTAT de la partie, et c'est le serveur qui paie.
     */
    // ON AFFICHE CE QUE LE SERVEUR A PAYE, pas ce que le navigateur croit: les
    // deux divergeaient (plafond de 60 points la-bas, 120 ici) et le message
    // promettait un lot que le sac ne recevait jamais.
    const tete = `${'⭐'.repeat(r.etoiles) || '📘'} ${r.justes}/${r.total}`
    journal.input('ecole', `partie finie: ${r.justes}/${r.total}`, {
      niveau: r.niveau,
      matiere: r.matiere,
      points: r.points,
      etoiles: r.etoiles,
    })
    void this.demanderSac({ quoi: 'ecole', niveau: r.niveau, points: r.points, etoiles: r.etoiles }).then((gains) => {
      const entrees = Object.entries(gains ?? {}) as [ItemId, number][]
      if (entrees.length === 0) {
        this.hud.message(`${tete} — pas de récompense cette fois. Réessaie !`, 3400, 'info')
        return
      }
      const recu = entrees.map(([id2, n]) => `+${n} ${objet(id2).nom}`).join('  ')
      this.hud.message(`${tete} — ${recu}`, 3400, 'succes')
      journal.info('ecole', 'recompense payee', { gains })
    })
    void this.sauver()
  }

  /**
   * Cueille un jardin. Il redonne quelque chose une fois par journee de jeu:
   * sans ce delai, un seul jardin nourrirait tout le monde indefiniment.
   */
  private cueillirJardin(id: string): void {
    const pret = this.jardinsPrets.get(id) ?? 0
    if (this.tempsJeu < pret) {
      const reste = Math.ceil((pret - this.tempsJeu) / MS_PER_GAME_HOUR)
      const nomB = this.batiments.get(id)
      const quoi = nomB ? batimentDef(nomB.kind) : batimentDef('jardin')
      this.hud.message(`${CeNom(quoi)} a besoin d’encore ${reste} h pour repousser.`, 2400, 'info')
      audio.erreurInterface()
      return
    }

    // Ce qu'on ramasse et le temps que ca met a revenir viennent du MODELE: le
    // jardin, le potager et la serre ne different que par leur table.
    const b = this.batiments.get(id)
    const recolte = b ? batimentDef(b.kind).recolte : undefined
    if (!b || !recolte) return

    // LES DES SONT AU SERVEUR: il relit la meme table `recolte` du modele et
    // rend ce qu'elle donne. Le client ne tire plus rien.
    void this.demanderSac({ quoi: 'jardin', kind: b.kind }).then((recus) => {
      if (!recus) return
      const lignes = Object.entries(recus) as [ItemId, number][]
      if (lignes.length > 0) {
        this.hud.message(lignes.map(([id2, n]) => `+${n} ${objet(id2).nom}`).join('  '), 2400, 'succes')
      }
      journal.input('jardin', `cueilli ${id}`, recus)
    })
    const repousseA = this.tempsJeu + recolte.jours * DAY_LENGTH_MS
    this.jardinsPrets.set(id, repousseA)
    void this.sauvegarde.jardin({ batimentId: id, repousseA })

    this.joueur.etat = 'recolte'
    this.minuterieAction = 0.9
    this.animation.jouer('recolte')
    audio.coup('cueillir')
    audio.ramasser()
    void this.sauver()
  }

  // -------------------------------------- construire, manger, dormir

  /** Fin d'un creusage ou d'une peche: on remet le produit au joueur. */
  private terminerActionTerrain(action: ActionTerrain): void {
    /*
     * LE DE EST AU SERVEUR. « Deux fois sur trois un poisson » est une regle de
     * `produitAction`, et c'est lui qui la joue: si le client tirait, il lui
     * suffisait de recommencer jusqu'a gagner — ou de s'ecrire le poisson
     * directement.
     */
    void this.demanderSac({ quoi: 'terrain', action }).then((recus) => {
      const entrees = Object.entries(recus ?? {}) as [ItemId, number][]
      if (entrees.length === 0) {
        this.hud.message(action === 'pecher' ? 'Ça ne mord pas… réessaie.' : 'Rien dans cette pelletée.', 2000, 'info')
        journal.info(action, 'sans resultat')
        return
      }
      audio.ramasser()
      this.hud.message(entrees.map(([id, n]) => `+${n} ${objet(id).nom}`).join('  '), 2200, 'succes')
      for (const [id, n] of entrees) journal.info('inv', `${id} +${n}`, { via: action })
      void this.sauver()
    })
    this.enAttente = null
  }

  private terminerRecolte(): void {
    this.enAttente = null
    if (this.joueur.etat === 'recolte') this.joueur.etat = 'immobile'
  }

  private mangerObjet(id: ItemId): void {
    const def = objet(id)
    if (def.faim <= 0) {
      audio.erreurInterface()
      return
    }
    if (quantite(this.joueur.inventaire, id) <= 0) {
      this.hud.message(`Tu n’as plus de ${def.nom}.`, 1800, 'erreur')
      audio.erreurInterface()
      return
    }
    // C'est le serveur qui retire: le client ne fait que demander. Mais il
    // DEDUIT tout de suite pour l'oeil (le serveur le remplacera par le sien):
    // sans ca, un double appui sur « Manger » avant la reponse mangeait deux
    // fois une soupe qu'on n'avait qu'une fois.
    this.joueur.inventaire = payer(this.joueur.inventaire, { [id]: 1 }) ?? this.joueur.inventaire
    void this.demanderSac({ quoi: 'consommer', kind: id })
    // A table on prend un vrai repas; ailleurs on grignote. Le bonus se calcule
    // ici et pas dans `manger`: la simulation ne sait pas ou se tient le
    // bonhomme, c'est le jeu qui le sait.
    const aTable = this.cibleMeuble === 'table'
    const avant = this.joueur.besoins.faim
    const apres = manger(this.joueur.besoins, id)
    this.joueur.besoins = aTable
      ? { ...apres, faim: clamp(apres.faim + def.faim * BONUS_REPAS_TABLE, 0, 100) }
      : apres
    const gain = Math.round(this.joueur.besoins.faim - avant)
    this.joueur.etat = 'mange'
    this.minuterieAction = DUREE_REPAS
    this.animation.jouer('mange')
    audio.manger()
    this.hud.message(
      aTable ? `🍽️ ${def.nom} à table — +${gain} faim` : `${def.icone} ${def.nom} — +${gain} faim`,
      2000,
      'succes',
    )
    this.panneauInventaire.rafraichir(this.joueur.inventaire)
    journal.input('manger', `${id}${aTable ? ' a table' : ''}`, { faim: this.joueur.besoins.faim })
  }


  /**
   * ADOPTE LE SAC DU SERVEUR.
   *
   * Le client garde une deduction optimiste pour que le HUD reponde tout de
   * suite — sans elle, chaque coup de hache attendrait le reseau avant
   * d'afficher son bois. Mais c'est le SERVEUR qui fait foi: sa reponse
   * remplace le sac local, elle ne s'y ajoute pas. Les deux ne peuvent donc pas
   * diverger, meme quand une pose est refusee au dernier moment.
   *
   * `null` veut dire « le serveur n'a rien dit »: on garde ce qu'on a, et
   * l'appelant previent le joueur. Inventer un gain que personne d'autre ne
   * verra serait pire que de ne rien donner.
   */
  private adopterSac(inv: Inventaire | null | undefined): void {
    if (!inv) return
    this.joueur.inventaire = inv
    this.panneauInventaire.rafraichir(inv)
    this.panneauCuisine.rafraichir(inv)
    this.panneauFeux.rafraichir(inv, this.horloge.nuit)
    this.menuConstruction.rafraichir(inv)
    this.majCanneEnMain()
  }

  /**
   * Demande un mouvement du sac au serveur et l'adopte.
   *
   * Rend ce qui vient d'entrer, pour que l'appelant l'affiche — ou `null` quand
   * le serveur a refuse. Les gains arrivent au bout d'une action qui dure deja
   * (0,9 s de hache, 2,6 s de ligne): l'aller-retour tient dedans.
   */
  private async demanderSac(geste: Record<string, unknown>): Promise<Cout | null> {
    const r = await this.sauvegarde.sac(geste)
    if (!r) return null
    this.adopterSac(r.inventaire)
    return r.gains
  }

  private cuisiner(idRecette: string): void {
    const r = recette(idRecette)
    if (!r) return
    if (!peutPayer(this.joueur.inventaire, r.cout)) {
      this.hud.message('Il manque des ingrédients.', 2000, 'erreur')
      audio.erreurInterface()
      return
    }
    // C'EST LE SERVEUR QUI FABRIQUE: il paie les ingredients et rend le produit
    // d'un seul geste, a partir de la MEME table de recettes. Le client ne fait
    // qu'annoncer laquelle.
    void this.demanderSac({ quoi: 'recette', recette: idRecette }).then((gains) => {
      if (!gains) {
        this.hud.message('Il manque des ingrédients.', 2400, 'erreur')
        audio.erreurInterface()
        return
      }
      audio.construire()
      this.hud.message(`${r.icone} ${r.nom} ${accorde('prêt', r)} !`, 2400, 'succes')
      journal.input('cuisine', `fabrique ${idRecette}`, { inventaire: this.joueur.inventaire })
      void this.sauver()
    })
  }

  /**
   * La canne est-elle visible dans la main? Elle l'est des qu'on en possede une:
   * c'est ce qui rend la peche visible AVANT d'en avoir besoin, et ce qui
   * rappelle au joueur qu'il l'a.
   */
  private majCanneEnMain(): void {
    const tenue = aCanne(this.joueur.inventaire)
    this.canneEnMain.montrer(tenue)
    if (tenue !== this.canneVisible) {
      this.canneVisible = tenue
      journal.info('peche', tenue ? 'canne en main' : 'canne rangee')
    }
  }

  private dormirIci(): void {
    this.joueur.etat = 'dort'
    this.animation.jouer('dort')
    this.joueur.besoins = dormir(this.joueur.besoins)
    const avant = formaterHeure(this.horloge)
    // DANS LE MONDE PARTAGE, C'EST LE SERVEUR QUI DECIDE DE L'AUBE. On la
    // calcule quand meme ici tout de suite, pour que le joueur n'attende pas un
    // aller-retour devant un ciel encore noir: le serveur confirmera la meme
    // valeur, puisque `prochaineAube` est la meme fonction des deux cotes.
    this.salon?.demanderAube()
    this.tempsJeu = prochaineAube(this.tempsJeu)
    this.horloge = horlogeDepuis(this.tempsJeu)
    this.horlogeSuivie.poseeDUnCoup()
    this.minuterieAction = 1.6
    this.hud.message(`Tu as dormi de ${avant} à ${formaterHeure(this.horloge)}.`, 3000, 'succes')
    journal.input('dormir', `${avant} -> ${formaterHeure(this.horloge)}`, { energie: this.joueur.besoins.energie })
    void this.sauver()
  }

  private lancerFeu(couleur: number, forme: FormeFeu): void {
    if (!this.horloge.nuit) {
      this.hud.message('Les feux d’artifice ne se lancent que la nuit.', 2400, 'erreur')
      audio.erreurInterface()
      return
    }
    if (quantite(this.joueur.inventaire, 'fusee') <= 0) {
      this.hud.message('Aucune fusée. Fabriques-en avec du sable, de la fibre et du bois.', 3000, 'erreur')
      audio.erreurInterface()
      return
    }
    void this.demanderSac({ quoi: 'consommer', kind: 'fusee' })
    // On tire a une dizaine de metres devant: a 1.5 m, l'explosion partait par
    // le haut de l'ecran au lieu d'etre cadree au-dessus du paysage.
    const depart: Vec2 = {
      x: clamp(this.joueur.pos.x + Math.sin(this.joueur.rot) * 11, 6, CARTE_X - 6),
      z: clamp(this.joueur.pos.z + Math.cos(this.joueur.rot) * 11, 6, CARTE_Z - 6),
    }
    const sol = hauteurSecuritaire(this.terrain, depart.x, depart.z)
    // L'explosion monte a 28-45 m: au zoom par defaut (28 m de haut visibles)
    // elle serait hors de l'ecran. On prend donc du recul le temps du spectacle.
    if (this.zoomAvantFeu === null && this.rig.zoom < ZOOM_SPECTACLE) {
      this.zoomAvantFeu = this.rig.zoom
      this.rig.zoomer(ZOOM_SPECTACLE / this.rig.zoom)
      journal.debug('feu', `zoom ${this.zoomAvantFeu.toFixed(1)} -> ${ZOOM_SPECTACLE}`)
    }
    this.minuterieFeu = DUREE_SPECTACLE

    this.renduFeux.lancer(depart, sol, couleur, forme, performance.now() / 1000, this.rig.azimut)
    audio.feuLancement()
    this.panneauFeux.rafraichir(this.joueur.inventaire, this.horloge.nuit)
    journal.input('feu', `lance ${forme} #${couleur.toString(16).padStart(6, '0')}`, {
      restantes: quantite(this.joueur.inventaire, 'fusee'),
    })
  }

  private poser(): void {
    /*
     * LE BOUTON « APPUYEZ POUR POSER » SERT LES DEUX POSES.
     *
     * Un vehicule n'a pas de `kindEnPose` — il a `vehiculeEnPose`. Sans cet
     * aiguillage, le bouton retombait sur le retour muet ci-dessous: ni pose,
     * ni message, ni son, ni ligne de journal expliquant le refus. Le tap au
     * sol, lui, posait bien (voir `gererTapConstruction`), si bien que le
     * bogue etait invisible a tout test qui passe par la — la lecon deja
     * ecrite: « un test qui contourne la couche cassee ne peut pas la
     * trouver ».
     *
     * Le journal d'Amelia du 18 aout en garde la trace exacte: 61 « pose
     * confirmee » en 70 secondes, et pas UN message entre elles, pendant
     * qu'elle essayait de poser son avion sur la piste.
     */
    if (this.vehiculeEnPose) {
      this.poserVehicule()
      return
    }
    const kind = this.kindEnPose
    const p = this.posEnPose
    if (!kind || !p) {
      // Un refus qui ne dit RIEN est ce qui a laisse vivre le bogue du bouton:
      // on ne peut pas chercher une panne dont le journal ne porte pas trace.
      journal.warn('construire', 'pose ignoree: rien en main', {
        kind: kind ?? 'aucun',
        pos: p ? `${p.x.toFixed(1)},${p.z.toFixed(1)}` : 'aucune',
      })
      return
    }
    const v = validerPlacement(
      kind,
      p,
      this.rotEnPose,
      this.terrain,
      this.batiments.liste(),
      this.joueur.inventaire,
      this.indexRessources.dansRayon(p.x, p.z, rayonVoisinage(kind), this.tempsJeu, this.batiments.liste()),
      this.joueur.pos,
    )
    if (!v.ok) {
      this.hud.message(v.raison ?? 'Placement impossible.', 2200, 'erreur')
      audio.erreurInterface()
      return
    }
    const def = batimentDef(kind)
    const restant = payer(this.joueur.inventaire, def.cout)
    if (!restant) {
      this.hud.message('Ressources insuffisantes.', 2000, 'erreur')
      audio.erreurInterface()
      return
    }
    /*
     * ON DEDUIT ICI POUR L'OEIL, PAS POUR LE COMPTE.
     *
     * Le HUD doit repondre tout de suite — sans cette deduction optimiste, le
     * bois resterait affiche une demi-seconde apres la pose. Mais c'est le
     * SERVEUR qui debite (`payerBatiment`), et sa reponse REMPLACE ce sac-la
     * (voir `adopterSac`). Les deux ne peuvent donc pas diverger, et un refus
     * ne coute rien puisque rien n'a ete pris la-bas.
     */
    this.joueur.inventaire = restant
    // Chaque joueur a sa couleur: on teinte ce qu'il batit vers elle, sans
    // effacer son choix, pour qu'on reconnaisse d'un coup d'oeil qui a bati quoi.
    const teintees = {
      murs: melangerVersProprietaire(this.couleursEnPose.murs, this.compte, 0.5),
      toit: melangerVersProprietaire(this.couleursEnPose.toit, this.compte, 0.35),
      porte: melangerVersProprietaire(this.couleursEnPose.porte, this.compte, 0.6),
    }
    const b = creerBatiment(kind, p, this.rotEnPose, this.token, this.terrain, teintees)
    this.batiments.ajouter(b)
    this.renduBatiments.ajouter(b)
    this.bloquerSiPlein(b)
    this.majCouvertureBatiments()
    this.joueur.etat = 'construit'
    this.minuterieAction = 0.9
    this.animation.jouer('construit')
    audio.construire()
    this.hud.message(`${def.icone} ${def.nom} ${accorde('construit', def)} !`, 2600, 'succes')
    journal.input('construire', `pose ${kind} (${p.x.toFixed(1)}, ${p.z.toFixed(1)}) rot ${this.rotEnPose}`, {
      cout: def.cout,
    })
    // Le serveur repond avec le sac qu'il a debite: on l'adopte.
    void this.sauvegarde.batiment(b).then((inv) => this.adopterSac(inv))
    void this.sauver()
    this.quitterConstruction()
  }

  /**
   * Seuls les amenagements pleins (piscine, cloture, lampadaire) bloquent la
   * navigation. Les maisons, elles, doivent rester traversables par la porte:
   * leurs murs sont geres par `resoudreMurs`, ce qui evite aussi d'enfermer un
   * joueur qui se trouvait la au moment de la pose.
   */
  private bloquerSiPlein(b: Batiment): void {
    // La regle elle-meme est dans shared/ (`barreLePassage`): le serveur batit
    // sa propre grille pour faire autorite sur les deplacements, et une cloture
    // bloquee d'un cote seulement serait un endroit du monde ou le bonhomme
    // tremblerait sans fin.
    if (!barreLePassage(b)) return
    const def = batimentDef(b.kind)
    this.grille.bloquerEmprise(b.pos, def.taille.l, def.taille.p, b.rot)
  }

  /**
   * RIEN NE POUSSE SOUS UNE CONSTRUCTION.
   *
   * Deux semis ignorent tout des batiments, et pour de bonnes raisons: le decor
   * (`genererProps`) et les noeuds de ressource (`genererRessources`) ne
   * dependent que de la graine du monde, c'est ce qui fait qu'un client et le
   * serveur voient le meme monde sans se parler. Le decor etait donc pose avant
   * meme que les maisons soient lues, et l'arbre qu'on abat POUR batir revenait
   * trois jours plus tard dans la cuisine.
   *
   * On ne touche a aucun des deux semis: on escamote a l'affichage, et on
   * recalcule depuis la liste complete a chaque fois. C'est ce qui rend le
   * geste identique au chargement, a la pose, a la demolition et a l'arrivee
   * d'un batiment pose par un autre joueur — et c'est ce qui fait revenir
   * l'herbe et les arbres quand la maison tombe.
   */
  private majCouvertureBatiments(): void {
    const liste = this.batiments.liste()
    const decor = this.renduProps.majCouverture(liste)
    const noeuds = this.renduRessources.majBatiments(liste, this.tempsJeu)
    journal.debug('batiment', `sous emprise: ${decor} accessoires, ${noeuds} noeuds`)
  }

  /**
   * Le clapotis de la brasse: un seul par cycle, au moment de la poussee.
   *
   * On ne peut pas reutiliser `piedAuSol()`, qui compte les pas: en nageant, le
   * bonhomme n'en fait aucun. On suit donc la phase de l'animation et on sonne
   * quand elle repasse par zero.
   */
  private phaseClapotis = 0
  private clapotisBrasse(): void {
    const p = this.animation.phasePas()
    if (p < this.phaseClapotis) audio.pas('eau')
    this.phaseClapotis = p
  }

  /**
   * Demolit la construction visee, si elle appartient au joueur.
   *
   * On rend la moitie des ressources: assez pour corriger un placement rate,
   * pas assez pour que batir-demolir devienne une facon de ranger l'inventaire.
   */
  private demolir(p: Vec2): void {
    const cible = this.batiments.liste().find((b) => dansEmprise(b, p.x, p.z, 1))
    if (!cible) {
      this.hud.message('Rien à démolir ici.', 1800, 'info')
      return
    }

    if (!peutDemolir(cible, this.token, estAdmin(this.compte))) {
      this.hud.message('Ce n’est pas à toi : on ne démolit que ses propres constructions.', 3000, 'erreur')
      audio.erreurInterface()
      journal.input('demolir', `refuse: ${cible.kind} de ${cible.proprietaire.slice(0, 8)}`)
      return
    }

    /*
     * CE QUI N'EST PAS A SOI SE DEMANDE. Seul l'administrateur arrive ici avec
     * la construction d'un autre (ou le decor), et il la faisait tomber d'un
     * tap: l'ecole d'un joueur y est passee le 13 octobre. On demande, sans
     * nom (le serveur masque les jetons des autres): « elle n'est pas a toi ».
     */
    if (cible.proprietaire !== this.token) {
      const def = batimentDef(cible.kind)
      this.panneauConfirmation.demander(
        `Démolir ${ceNom(def)} ?`,
        `${Pronom(def)} n’est pas à toi, et rien ne sera récupéré.`,
        '🧨 Démolir',
        () => this.executerDemolition(cible),
      )
      return
    }
    this.executerDemolition(cible)
  }

  private executerDemolition(cible: Batiment): void {
    // Le batiment a pu tomber entre la question et la reponse (un autre l'a
    // demoli, le serveur l'a retire): on ne demolit pas un fantome.
    if (!this.batiments.get(cible.id)) return
    // Le remboursement est calcule et ecrit par le SERVEUR (voir la route
    // DELETE): on affiche ce qu'il rend, on ne se le donne pas.
    const rendu = renduDemolition(cible, this.token)
    const def = batimentDef(cible.kind)
    this.grille.libererEmprise(cible.pos, def.taille.l, def.taille.p, cible.rot)
    this.batiments.retirer(cible.id)
    this.renduBatiments.retirer(cible.id)
    // La maison tombe: l'herbe et les arbres qu'elle recouvrait reviennent.
    this.majCouvertureBatiments()
    if (this.interieur === cible.id) this.sortir()
    audio.construire()
    const entrees = Object.entries(rendu) as [ItemId, number][]
    const texte = entrees.length > 0 ? entrees.map(([id, n]) => `+${n} ${objet(id).nom}`).join('  ') : 'Rien récupéré'
    this.hud.message(`${def.icone} ${def.nom} ${accorde('démoli', def)}. ${texte}`, 3000, 'succes')
    journal.input('demolir', `${cible.kind} en (${cible.pos.x.toFixed(1)}, ${cible.pos.z.toFixed(1)})`, { rendu })

    // Le remboursement vient du serveur, calcule sur le modele qu'il avait en
    // base — jamais sur un montant que le client annonce.
    void this.sauvegarde.supprimerBatiment(cible.id).then((rep) => {
      if (rep) {
        this.adopterSac(rep.inventaire)
        // L'animal de compagnie de cette maison n'a plus de maison: il retourne
        // a la nature au lieu de tourner autour d'un tas de planches disparu.
        this.zoo.libererAnimauxDe(cible.id)
        return
      }
      // CE QUI N'A PAS EU LIEU NE S'AFFICHE PAS. Le serveur a refuse (ou ne
      // repond pas): on remet le batiment la ou il etait, et on le dit.
      this.appliquerEvenementMonde({ quoi: 'batiment-pose', batiment: cible })
      this.hud.message(`${def.icone} La démolition n’a pas pu se faire : ${leNom(def)} est ${accorde('remis', def)} en place.`, 3500, 'erreur')
      audio.erreurInterface()
    })
    void this.sauver()
  }

  private gererTapABord(p: Vec2): boolean {
    const v = this.renduVehicules.vehicules().find((x) => x.id === this.joueur.vehicule)
    if (!v) return false

    /*
     * AUX COMMANDES D'UN AVION, LE TAP EST UN ORDRE DE VOL.
     *
     * C'est la seule interface de pilotage, et elle prend la main sur tout le
     * reste: on ne peche pas depuis un avion, et « toucher sa coque » n'a de
     * sens qu'a l'arret. Un appareil gare qu'on touche sur sa coque, on en
     * descend; partout ailleurs, on lui donne un cap.
     */
    if (estAvion(v.kind)) {
      if (estGare(this.etatAvion(v)) && distanceVehicule(v, p.x, p.z) <= 0) {
        this.basculerVehicule()
        return true
      }
      this.ordonnerVol(v, p)
      return true
    }

    if (this.peche.enCours) return this.peche.ferrerMaintenant(this.joueur)

    /*
     * ON DESCEND EN TOUCHANT SA PROPRE COQUE, et seulement elle.
     *
     * Descendre n'existait qu'au clavier, comme monter. La porte est
     * volontairement etroite — la coque meme, pas ses environs: un tap devant
     * le capot veut dire « conduis-moi la », et une tolerance genereuse ferait
     * descendre a chaque fois qu'on essaie d'avancer.
     */
    if (distanceVehicule(v, p.x, p.z) <= 0) {
      this.basculerVehicule()
      return true
    }

    /*
     * `peutPecherDepuis` exige deja l'ancre: quand elle est jetee, le tap lance
     * la ligne; sinon il tombe plus bas et conduit. Le garde-fou « jette
     * l'ancre d'abord » qui vivait ici ne pouvait donc jamais s'afficher.
     * C'est l'infobulle qui porte ce conseil, la ou le joueur le lit vraiment.
     */
    if (peutPecherDepuis(v, this.terrain)) {
      this.peche.lancer(pointPecheVehicule(v), this.joueur)
      return true
    }
    return false
  }

  /**
   * Trace le passage de la voiture dans la vegetation.
   *
   * Elle couche l'herbe et les fleurs, qui repoussent en deux journees; les
   * arbres, les rochers et les batiments l'arretent, donc rien de construit
   * n'est jamais abime. Le decor est encore en chantier chez un autre agent: on
   * enregistre le passage, l'effet visuel arrive ensuite.
   */
  private coucherVegetation(x: number, z: number, largeur: number): void {
    const n = this.renduProps.coucher(x, z, largeur / 2 + 0.4, this.tempsJeu + 2 * DAY_LENGTH_MS)
    if (n > 0) this.tracesPosees += n
  }

  /**
   * OU L'ON POSE VRAIMENT le vehicule qu'on tient.
   *
   * Pour une voiture ou une barque, la ou le doigt a touche. Pour un AVION, sur
   * l'axe de la piste et dans le sens du decollage (`poseSurPiste`): un
   * appareil de travers sur son ruban devrait manoeuvrer avant de partir, et un
   * appareil a cheval sur le bord roulerait dans l'herbe. Le geste reste le
   * meme — toucher la piste —, c'est la pose qui range.
   */
  private placementVehicule(kind: VehiculeKind, p: Vec2): { pos: Vec2; rot: number } {
    if (!estAvion(kind)) return { pos: p, rot: this.joueur.rot }
    const sous = pisteSousAvion(this.batiments.liste(), p, 4)
    if (!sous) return { pos: p, rot: this.joueur.rot }
    return poseSurPiste(sous.piste, p)
  }

  /** Pose le vehicule en cours, si l'endroit et l'inventaire le permettent. */
  private poserVehicule(): void {
    const kind = this.vehiculeEnPose
    const brut = this.posEnPose
    if (!kind || !brut) return
    const range = this.placementVehicule(kind, brut)
    const p = range.pos

    const v = validerPlacementVehicule(
      kind,
      p,
      range.rot,
      this.terrain,
      this.renduVehicules.vehicules(),
      this.batiments.liste(),
      this.joueur.inventaire,
      this.joueur.pos,
    )
    if (!v.ok) {
      this.hud.message(v.raison ?? 'Placement impossible.', 2400, 'erreur')
      audio.erreurInterface()
      return
    }

    const def = vehiculeDef(kind)
    const restant = payer(this.joueur.inventaire, def.cout)
    if (!restant) {
      this.hud.message('Ressources insuffisantes.', 2000, 'erreur')
      audio.erreurInterface()
      return
    }
    // Deduction optimiste pour l'oeil; c'est le serveur qui debite vraiment
    // (`/api/vehicule`), et sa reponse remplacera ce sac-la.
    this.joueur.inventaire = restant

    const vehicule = creerVehicule(kind, p, range.rot, this.token, this.terrain, this.compte)
    this.renduVehicules.ajouter(vehicule)
    // Un appareil neuf est GARE a l'aeroport sous ses roues: c'est de la qu'il
    // decollera, et c'est ce qui rend « il doit etre bati a un aeroport » vrai
    // jusque dans la mecanique, pas seulement dans le refus de placement.
    if (estAvion(kind)) this.etatAvion(vehicule)
    this.joueur.etat = 'construit'
    this.minuterieAction = 1.1
    this.animation.jouer('construit')
    audio.construire()
    this.hud.message(
      estAvion(kind)
        ? `${def.icone} ${def.nom} ${accorde('prêt', def)} au décollage !`
        : `${def.icone} ${def.nom} ${accorde('prêt', def)} à partir !`,
      3000,
      'succes',
    )
    journal.input('construire', `pose ${kind} (${p.x.toFixed(1)}, ${p.z.toFixed(1)})`, { cout: def.cout })
    void this.sauvegarde.vehicule(vehicule).then((rep) => {
      if (rep.ok) {
        this.adopterSac(rep.inventaire)
        return
      }
      // REFUSE: on retire le vehicule de l'ecran et le sac redevient celui du
      // serveur (rien n'a ete debite la-bas), comme pour un batiment refuse.
      this.renduVehicules.retirer(vehicule.id)
      if (rep.inventaire) this.adopterSac(rep.inventaire)
      else this.joueur.inventaire = ajouterLot(this.joueur.inventaire, def.cout)
      this.hud.message(`${def.icone} ${rep.erreur}`, 3500, 'erreur')
      audio.erreurInterface()
    })
    void this.sauver()
    this.quitterConstruction()
  }

  /**
   * DIT AU JOUEUR QUE C'EST L'ANCRE QUI LE RETIENT.
   *
   * « On n'a pas pu bouger la barque du tout. » Une barque NAIT A L'ANCRE — il
   * le faut, sinon elle derive avant meme qu'on ait pu monter dedans — et une
   * barque a l'ancre ne bouge pas d'un centimetre, quoi qu'on fasse du
   * joystick. Rien ne le disait: l'infobulle annoncait « Pêcher » (ce qui est
   * vrai, l'ancre est justement ce que la peche exige) et le bouton du HUD
   * proposait « Remonter l'ancre » sans que rien n'invite a le lire. On poussait
   * donc dans le vide, et le jeu avait l'air casse.
   *
   * Le message ne part QUE lorsqu'on essaie vraiment d'avancer, et une fois par
   * accalmie: repete a chaque tick il noierait tout le reste, exactement comme
   * les soixante « Tu etais coince » d'une autre journee.
   */
  private signalerAncre(v: Vehicule): void {
    if (!v.ancre || !peutJeterAncre(v)) return
    if (this.tempsJeu - this.dernierRappelAncre < DELAI_RAPPEL_ANCRE) return
    this.dernierRappelAncre = this.tempsJeu
    this.hud.message('⚓ L’ancre est jetée : remonte-la pour repartir.', 3200, 'info')
    journal.info('vehicule', 'pousse contre une ancre jetee', { id: v.id })
  }

  /**
   * UN VEHICULE ARRETE EN ROUTE DOIT DIRE POURQUOI.
   *
   * L'itineraire est trace pour un PIETON (un point) et lisse en ligne droite;
   * une voiture fait deux metres de large et quatre de long. En foret, un coin
   * accroche un tronc, `majVehicule` rend `bloque`, le chemin est abandonne —
   * et rien ne le disait: on touchait le sol, la voiture ne bougeait pas (vu au
   * telephone le 13 octobre). Meme rythme que le rappel de l'ancre.
   */
  private signalerObstacle(v: Vehicule, vise: Vec2): void {
    if (v.ancre && peutJeterAncre(v)) return
    if (this.tempsJeu - this.dernierRappelObstacle < DELAI_RAPPEL_ANCRE) return
    this.dernierRappelObstacle = this.tempsJeu
    const def = vehiculeDef(v.kind)
    /*
     * Sans les obstacles, la place aurait-elle convenu? Sinon c'est le MILIEU.
     * On sonde aussi DEVANT le capot: la grille de navigation barre l'eau et
     * ses abords (une case de 2 m bloquee des qu'un coin mouille), donc la
     * voiture s'arrete sur le sable sec, quelques metres avant la mer — et
     * l'emprise seule y aurait lu « un obstacle » (vu au telephone). Et
     * l'endroit TOUCHE dit le reste: une voiture qui doit d'abord tourner pour
     * aller vers la mer peut s'arreter avant d'avoir l'eau devant le capot.
     */
    const veutEau = def.milieu === 'eau'
    const but = this.butABord
    let milieu =
      !positionValide(v.kind, vise, this.joueur.rot, this.terrain) ||
      (but !== null && this.terrain.estEau(but.x, but.z) !== veutEau)
    for (let d = 0; !milieu && d <= def.taille.p / 2 + 5; d += 0.5) {
      const a = tournerLibre(0, d, v.rot)
      if (this.terrain.estEau(vise.x + a.x, vise.z + a.z) !== veutEau) milieu = true
    }
    const texte = milieu
      ? v.kind === 'barque'
        ? 'La barque ne va pas à terre.'
        : `${LeNom(def)} ne va pas dans l’eau.`
      : `${LeNom(def)} est ${accorde('bloqué', def)} : ça ne passe pas par là.`
    this.hud.message(texte, 2600, 'info')
    journal.info('vehicule', milieu ? 'arret: milieu interdit' : 'arret: obstacle', {
      id: v.id,
      x: +vise.x.toFixed(1),
      z: +vise.z.toFixed(1),
      rot: +v.rot.toFixed(2),
    })
  }

  /**
   * Jette ou remonte l'ancre de la barque ou l'on se trouve.
   *
   * Un seul corps pour la touche « a » et pour le bouton du HUD: deux copies
   * du meme geste finissent toujours par diverger.
   */
  private basculerAncreCourante(par: string): void {
    const v = this.renduVehicules.vehicules().find((x) => x.id === this.joueur.vehicule)
    if (!v || !basculerAncre(v)) return
    this.hud.message(v.ancre ? '⚓ Ancre jetée.' : 'Ancre remontée.', 2000, 'info')
    journal.input('ancre', `${v.ancre ? 'jetee' : 'remontee'} (${par})`)
  }

  /**
   * IL RESTE LA OU ON L'A LAISSE. Descendre ne faisait que sauver le JOUEUR:
   * une voiture ou une barque revenait donc a sa place de construction au
   * prochain chargement, et les autres ne la voyaient jamais bouger (vu au
   * telephone le 14 octobre, la base n'avait pas change d'un centimetre apres
   * un trajet). Seul l'avion qui atterrit se rangeait. Le serveur accepte ce
   * range, gratuit, y compris pour un vehicule emprunte non verrouille.
   */
  private rangerVehicule(v: Vehicule): void {
    void this.sauvegarde.vehicule(v).then((rep) => {
      if (rep.ok) return
      this.hud.message(`Place non enregistrée : ${rep.erreur}`, 3000, 'erreur')
    })
  }

  /** Monte dans le vehicule le plus proche, ou en descend. */
  /**
   * Monte ou descend. `vise` est le vehicule TOUCHE: on monte dans celui-la,
   * pas dans le plus proche de soi — deux avions gares cote a cote, ou sa
   * voiture a cote de la voiture verrouillee d'un autre, faisaient monter dans
   * l'autre (ou repondre « verrouillé » pour une voiture jamais touchee).
   */
  private basculerVehicule(vise: string | null = null): void {
    const idCourant = this.joueur.vehicule
    if (idCourant !== null) {
      const v = this.renduVehicules.vehicules().find((x) => x.id === idCourant)
      if (!v) {
        this.joueur.vehicule = null
        return
      }
      const ok = peutDescendre(v, this.terrain, this.avionEnVol(v))
      if (!ok.ok) {
        this.hud.message(ok.raison ?? 'Impossible de descendre ici.', 3000, 'erreur')
        audio.erreurInterface()
        return
      }
      const p = positionDescente(v, this.terrain)
      this.joueur.pos.x = p.x
      this.joueur.pos.z = p.z
      this.joueur.vehicule = null
      arreter(this.joueur)
      this.rig.sortirInterieur()
      // On quitte l'appareil: la camera reprend le cadrage du bonhomme, meme si
      // l'on descend d'un avion qui n'a jamais decolle.
      this.rig.vueDeVol(false)
      this.hud.message('Tu es descendu.', 1800, 'info')
      journal.input('vehicule', `descendu de ${v.kind}`)
      void this.sauver()
      this.rangerVehicule(v)
      return
    }

    const proche =
      (vise !== null ? this.renduVehicules.vehicules().find((x) => x.id === vise) : undefined) ??
      vehiculeProche(this.renduVehicules.vehicules(), this.joueur.pos.x, this.joueur.pos.z, REACH + 1)
    if (!proche) return
    const ok = peutMonter(proche, this.joueur, REACH + 1, {
      ...this.contexteMontee(),
      enVol: this.avionEnVol(proche),
    })
    if (!ok.ok) {
      this.hud.message(ok.raison ?? 'Impossible de monter.', 2400, 'erreur')
      audio.erreurInterface()
      return
    }
    this.joueur.vehicule = proche.id
    this.joueur.pos.x = proche.pos.x
    this.joueur.pos.z = proche.pos.z
    arreter(this.joueur)
    audio.clic()
    // Un avion ne se conduit pas: il se commande. Le message dit le geste, pas
    // le contraire — « touche le sol pour avancer » ferait pousser un joystick
    // qui ne sert a rien la-haut.
    this.hud.message(
      estAvion(proche.kind)
        ? '✈️ À bord ! Touche un endroit : l’avion y va. Touche un aéroport : il se pose.'
        : `${vehiculeDef(proche.kind).icone} En route ! Touche le sol pour avancer.`,
      3600,
      'succes',
    )
    journal.input('vehicule', `monte dans ${proche.kind}`)
  }

  /**
   * CE QUE LE CLIENT SAIT de la place du conducteur: son jeton, et les
   * vehicules que les voisins pilotent en ce moment.
   *
   * Ce n'est qu'un avis: `EtatPresence.vehicule` arrive avec 100 ms de retard,
   * et deux joueurs qui montent dans la meme seconde ne se voient pas. C'est
   * pour ca que le serveur tranche AUSSI (voir `accorderLeVolant` dans
   * server/salon.ts). Ce que ce contexte apporte, c'est un refus IMMEDIAT et
   * une infobulle honnete dans le cas courant — celui ou l'autre est deja au
   * volant depuis un moment.
   */
  private contexteMontee(): ContexteMontee {
    const occupes = new Set<string>()
    for (const e of this.etatsAutres.values()) {
      if (e.vehicule !== null) occupes.add(e.vehicule)
    }
    return { jeton: this.token, occupes }
  }

  /**
   * Le serveur nous retire le volant: on descend pour de bon.
   *
   * On ne se contente PAS d'afficher le message. Tant que `joueur.vehicule`
   * pointe encore sur ce vehicule, le pilotage local continue de le tirer vers
   * le joystick, `gererTapABord` avale les taps, et le joueur se croit au
   * volant d'une voiture qui appartient a quelqu'un d'autre. On le pose donc a
   * cote, exactement comme une descente ordinaire.
   */
  private rendreLeVolant(id: string, raison: string): void {
    if (this.joueur.vehicule !== id) return
    const v = this.renduVehicules.vehicules().find((x) => x.id === id)
    this.joueur.vehicule = null
    if (v) {
      const p = positionDescente(v, this.terrain)
      this.joueur.pos.x = p.x
      this.joueur.pos.z = p.z
    }
    arreter(this.joueur)
    this.rig.sortirInterieur()
    this.hud.majAncre(false, false)
    this.hud.majVerrou(false, false)
    this.hud.majMonde(false, 2)
    this.hud.message(raison, 3600, 'erreur')
    audio.erreurInterface()
    journal.input('vehicule', `volant rendu: ${raison}`, { id })
  }

  /**
   * Verrouille ou deverrouille le vehicule ou l'on se trouve.
   *
   * LE SERVEUR TRANCHE: on envoie la demande et on n'applique le nouvel etat
   * que s'il l'a ecrit. Poser le verrou localement puis « corriger » au retour
   * ferait clignoter le cadenas a chaque fois que le reseau tousse, et
   * surtout ferait croire une porte fermee alors qu'elle est ouverte.
   */
  private async basculerVerrouCourant(par: string): Promise<void> {
    const v = this.renduVehicules.vehicules().find((x) => x.id === this.joueur.vehicule)
    if (!v) return
    if (!estProprietaire(v, this.token)) {
      this.hud.message('Seul celui qui l’a bâti peut le verrouiller.', 2600, 'erreur')
      return
    }
    const voulu = !v.verrouille
    journal.input('vehicule', `${voulu ? 'verrouillage' : 'deverrouillage'} demande (${par})`, { id: v.id })
    const ecrit = await this.sauvegarde.verrouillerVehicule(v.id, voulu)
    if (!ecrit) {
      this.hud.message('Le serveur a refusé : réessaie.', 2600, 'erreur')
      return
    }
    this.renduVehicules.majEtat(ecrit)
    this.hud.majVerrou(true, ecrit.verrouille)
    this.hud.message(ecrit.verrouille ? '🔒 Verrouillé.' : '🔓 Déverrouillé.', 2000, 'succes')
  }

  private quitterConstruction(): void {
    this.mode = 'jeu'
    this.kindEnPose = null
    this.vehiculeEnPose = null
    this.posEnPose = null
    this.renduBatiments.cacherFantome()
    this.renduVehicules.cacherFantome()
    this.menuConstruction.fermerPlacement()
    this.menuConstruction.fermer()
    this.hud.marquerActif(null)
  }

  // ------------------------------------------------------------- persistance

  /**
   * Sauvegarde immediate. A appeler des que quelque chose d'important vient
   * d'arriver (bonhomme cree, maison posee) et non pas seulement au prochain
   * tour d'horloge: fermer l'onglet dans les 15 secondes suivant la creation
   * faisait perdre le personnage.
   */
  async sauverMaintenant(): Promise<void> {
    await this.sauver()
  }

  /** Sauvegarde de derniere chance, au moment ou l'onglet se ferme. */
  sauverAvantFermeture(): void {
    this.sauvegarde.avantFermeture(this.etatASauver())
  }

  private etatASauver(): EtatSauve {
    return {
      token: this.token,
      nom: this.joueur.nom,
      apparence: this.joueur.apparence,
      inventaire: this.joueur.inventaire,
      besoins: this.joueur.besoins,
      pos: this.joueur.pos,
      tempsJeu: this.tempsJeu,
    }
  }

  private async sauver(): Promise<void> {
    await this.sauvegarde.joueur(this.etatASauver())
  }

  /** Defait une pose que le serveur a refusee, et rend ce qu'elle avait coute. */
  private retirerBatimentRefuse(b: Batiment, raison: string): void {
    const def = batimentDef(b.kind)
    if (this.batiments.get(b.id)) {
      this.grille.libererEmprise(b.pos, def.taille.l, def.taille.p, b.rot)
      this.batiments.retirer(b.id)
      this.renduBatiments.retirer(b.id)
      this.majCouvertureBatiments()
      if (this.interieur === b.id) this.sortir()
    }
    /*
     * ON NE PERD JAMAIS RIEN. Le serveur n'a RIEN pris — il valide le placement
     * avant de debiter, precisement pour qu'un refus ne coute pas de bois — donc
     * il suffit de defaire la deduction optimiste du client. Rien n'est invente:
     * on remet exactement ce qu'on avait retire.
     */
    this.joueur.inventaire = ajouterLot(this.joueur.inventaire, def.cout)
    this.hud.message(raison.length > 0 ? `⚠ ${raison}` : '⚠ Le serveur a refusé cette construction.', 3500, 'erreur')
    journal.warn('construire', `pose refusee par le serveur: ${raison}`, { kind: b.kind, id: b.id })
  }

  // ------------------------------------------------------------- reseau

  /**
   * Ouvre la liaison temps reel.
   *
   * Seulement pour un COMPTE: le serveur verifie le jeton contre la table des
   * comptes et refuse le reste (voir server/salon.ts). Un invite joue donc seul,
   * exactement comme avant, et rien de ce qui suit ne s'execute pour lui.
   */
  private brancherReseau(): void {
    if (this.compte === null || this.salon) return
    this.salon = new SalonClient(this.token, {
      onOuvert: (id, joueurs, reprise, tempsJeu) => {
        journal.info('reseau', `dans le monde partage en tant que ${id.slice(0, 8)}`, {
          autres: joueurs.length,
          reprise,
        })
        // L'heure du monde arrive avec l'accueil: on s'y pose d'un coup, avant
        // la premiere image, plutot que de laisser le ciel changer de couleur
        // sous les yeux du joueur une seconde apres son arrivee.
        this.recalerHorloge(tempsJeu, true)
        this.suivi.vider()
        this.renduJoueurs.definir(joueurs)
        for (const j of joueurs) this.suivi.recevoir(j.id, performance.now(), j.etat)
        if (reprise) {
          this.hud.message('Connexion rétablie.', 2000, 'succes')
          // On a forcement rate des changements pendant la coupure: on relit le
          // monde au lieu d'esperer que rien n'a bouge. La base est la verite.
          void this.rattraperMonde()
        }
      },
      onArrive: (j) => {
        this.renduJoueurs.ajouter(j)
        this.suivi.recevoir(j.id, performance.now(), j.etat)
        this.hud.message(`👋 ${j.nom} vient d’arriver.`, 2600, 'info')
      },
      onPart: (id) => {
        this.suivi.oublier(id)
        this.renduJoueurs.retirer(id)
        this.etatsAutres.delete(id)
      },
      onRoster: (joueurs) => {
        // La liste fait autorite: `definir` retire aussi ceux qui n'y sont
        // plus. On ne vide NI le suivi NI les etats interpoles — les positions
        // deja recues restent bonnes, et les jeter ferait sauter d'un metre
        // tous les voisins pour reparer le cas d'un seul.
        this.renduJoueurs.definir(joueurs)
        for (const j of joueurs) this.suivi.recevoir(j.id, performance.now(), j.etat)
      },
      onPresence: (joueurs, t) => {
        for (const j of joueurs) {
          this.suivi.recevoir(j.id, t, j.e)
          // UNE POSITION NE CREE PAS D'IDENTITE (voir client/render/joueurs.ts):
          // recevoir celle de quelqu'un dont on n'a jamais eu le nom veut dire
          // qu'on a rate son `arrive`, et ce message n'est envoye qu'une fois.
          // Sans cette demande, ce joueur-la resterait invisible pour toujours
          // tout en occupant la liaison dix fois par seconde.
          if (!this.renduJoueurs.connait(j.id)) this.demanderListeJoueurs()
        }
      },
      onRecalage: (n, x, z, rot) => {
        // A bord, personne ne fait autorite sur la position: le serveur ne
        // simule pas les vehicules, il se contente de plafonner (voir
        // server/deplacement.ts). Rejouer ici tirerait le pilote en arriere.
        if (this.joueur.vehicule !== null) return
        const r = this.reconciliation.recevoir(
          n,
          x,
          z,
          rot,
          this.joueur,
          this.terrain,
          this.grille,
          this.batiments.liste(),
          this.optionsDeplacement(),
          this.noeudsSolides(),
        )
        // Seul le SAUT merite une ligne: le lissage arrive dix fois par seconde
        // et noierait le fichier de session, alors qu'un saut est ce qu'un
        // joueur ressent — c'est lui qu'on veut retrouver en relisant.
        if (r.saut) {
          journal.warn('reseau', `position recalee de ${r.ecart.toFixed(1)} m par le serveur`, {
            entrees: r.rejouees,
            vers: { x: Math.round(x * 10) / 10, z: Math.round(z * 10) / 10 },
          })
        }
      },
      onVolantRefuse: (id, raison) => this.rendreLeVolant(id, raison),
      onMonde: (ev) => this.appliquerEvenementMonde(ev),
      onChat: (messages) => this.chat.recevoir(messages),
      onHorloge: (t) => this.recalerHorloge(t, false),
      onFaune: (trame) => {
        this.suiviFaune.recevoir(trame)
        this.fauneRecue = true
      },
      onFerme: () => {
        // Rien a dire ici: c'est le voile qui l'annonce, et il l'annonce tant
        // que ca dure. Un avertissement de quatre secondes pour une coupure de
        // quinze minutes etait la moitie du probleme.
        this.suivi.vider()
        this.renduJoueurs.vider()
        this.etatsAutres.clear()
        // Les entrees en vol ne valent plus rien: au retour de la liaison, le
        // serveur repartira de la position qu'il connaissait avant la coupure,
        // et rejouer par-dessus ce qui date d'il y a dix secondes ramenerait le
        // bonhomme la ou le wifi est tombe.
        this.reconciliation.vider()
        // La faune repasse sous la conduite du client: sans ca, tous les
        // animaux se figeraient jusqu'au retour du reseau. On repart de leur
        // derniere position connue, qui est la bonne.
        this.suiviFaune.vider()
        this.fauneRecue = false
      },
      onRefus: (raison) => journal.info('reseau', `le serveur refuse la liaison: ${raison}`),
    })
    this.salon.connecter()
  }

  /**
   * Redemande au serveur la liste des connectes, au plus une fois toutes les
   * `DELAI_DEMANDE_LISTE_MS`.
   *
   * LE FREIN EST TOUTE LA FONCTION. Ce qui declenche la demande — une position
   * venue d'un inconnu — arrive dix fois par seconde et continuera d'arriver
   * jusqu'a ce que la reponse soit revenue: sans frein, on enverrait une
   * demande par paquet et par joueur inconnu, c'est-a-dire qu'on repondrait a
   * un joueur invisible par une tempete de messages. Trois secondes laissent
   * largement le temps d'un aller-retour, meme depuis la maison ou l'on joue a
   * quatre-vingt-dix millisecondes.
   */
  private demanderListeJoueurs(): void {
    const maintenant = performance.now()
    if (maintenant - this.derniereDemandeListe < DELAI_DEMANDE_LISTE_MS) return
    if (this.salon?.demanderQui() !== true) return
    this.derniereDemandeListe = maintenant
    journal.warn('reseau', 'position recue d un joueur inconnu: on redemande la liste')
  }

  /**
   * Ce qu'on publie de soi: de quoi dessiner un bonhomme ailleurs, plus
   * l'appat, dont la FAUNE DU SERVEUR a besoin pour savoir qui elle suit.
   */
  private etatPourLesAutres(): EtatPresence {
    return {
      x: this.joueur.pos.x,
      z: this.joueur.pos.z,
      rot: this.joueur.rot,
      etat: this.joueur.etat,
      nage: this.joueur.nage,
      vehicule: this.joueur.vehicule,
      interieur: this.interieur,
      niveau: this.joueur.niveau,
      appat: this.appat,
    }
  }

  /**
   * Publie sa position si elle a bouge, et au moins une fois par seconde.
   *
   * Le « au moins » n'est pas du zele: c'est ce qui distingue, chez les autres,
   * un joueur immobile d'un joueur dont la liaison est morte (voir
   * `SuiviPresence.expirer`).
   *
   * ON COMPTE EN TEMPS REEL, PAS EN TEMPS DE BOUCLE. Le `dt` des images est
   * borne a 100 ms pour que le bonhomme ne traverse pas le monde quand l'onglet
   * revient au premier plan; s'en servir ici affamait la publication des qu'une
   * machine ramait: a une image toutes les quatre secondes (rendu logiciel des
   * tests), il fallait deux images pour atteindre le quota, et les autres
   * joueurs faisaient disparaitre celui qui se taisait alors qu'il etait bien
   * la. Le temps qui passe pour le reseau est celui de l'horloge.
   */
  /**
   * Envoie les entrees accumulees depuis la derniere image.
   *
   * A CHAQUE IMAGE, et non a la cadence de la presence: une entree en retard
   * est une entree que le serveur rejouera en retard, donc un recalage de plus.
   * Le lot est minuscule — trois entrees de six nombres, quelques dizaines
   * d'octets — et il n'y a rien a gagner a le laisser vieillir.
   *
   * Rien ne part en solo ni en invite: sans salon, `noter` n'a rien empile et
   * le lot est vide. C'est ce qui garde le jeu identique hors ligne.
   */
  private publierEntrees(): void {
    if (!this.salon) return
    if (this.joueur.vehicule !== null) {
      // Au volant, l'autorite est suspendue des deux cotes: on n'accumule rien,
      // pour qu'au moment de descendre il ne reste pas un fond de tampon qui
      // rejouerait un trajet a pied fait avant de monter.
      this.reconciliation.vider()
      return
    }
    /*
     * LA LIAISON A MOITIE MORTE. MAX_EN_VOL entrees sans confirmation, c'est
     * dix secondes ou l'amont ne passe plus pendant que la prise se dit
     * ouverte: le serveur nous a probablement deja lache pour silence, et
     * au-dela de cet horizon nos propres entrees tombent du tampon — plus rien
     * n'est rejouable, chaque recalage devient un saut en arriere. On coupe
     * NOUS-MEMES: le voile met le monde en pause (ce qui est la verite) et la
     * reprise repart par le chemin normal, ou le serveur accepte notre
     * position de depart. Mesure dans une vraie partie: sans cette coupure, 97
     * secondes de jeu dans le vide avant le code 1006.
     */
    if (this.salon.connecte && this.reconciliation.enAttente >= MAX_EN_VOL) {
      this.salon.couper(`${MAX_EN_VOL} entrees sans confirmation du serveur`)
      return
    }
    this.salon.publierEntrees(this.reconciliation.lot())
  }

  private publierEtat(maintenant: number): void {
    if (!this.salon) return
    const depuis = maintenant - this.dernierePublication
    const e = this.etatPourLesAutres()
    const p = this.dernierPublie
    // DESCENDRE PART AVANT LE PREMIER PAS. Les entrees suivent cet etat dans la
    // meme image, et la prise garde l'ordre: si l'etat attendait sa periode, le
    // serveur recevrait des pas a pied d'un joueur qu'il croit encore au volant,
    // les ignorerait, et repondrait par la position du VEHICULE — le saut qui
    // ramenait le pilote dans sa voiture.
    const vehiculeChange = p !== null && p.vehicule !== e.vehicule
    if (depuis < PERIODE_PUBLICATION_MS && !vehiculeChange) return
    const change =
      !p ||
      Math.abs(p.x - e.x) > 0.01 ||
      Math.abs(p.z - e.z) > 0.01 ||
      Math.abs(p.rot - e.rot) > 0.01 ||
      p.etat !== e.etat ||
      p.nage !== e.nage ||
      p.vehicule !== e.vehicule ||
      p.interieur !== e.interieur ||
      // Monter ou descendre change la hauteur a laquelle les autres nous
      // dessinent: c'est un saut de trois metres, il ne peut pas attendre.
      p.niveau !== e.niveau ||
      // Tendre une friandise doit partir TOUT DE SUITE: c'est elle qui attire
      // les betes dans la simulation du serveur, et attendre le prochain pas
      // ferait ignorer un joueur qui s'accroupit sans bouger.
      p.appat !== e.appat
    if (!change && depuis < 1000) return
    this.dernierePublication = maintenant
    this.dernierPublie = e
    this.salon.publier(e)
  }

  /**
   * Applique un changement du monde annonce par le serveur.
   *
   * TOUT est idempotent: un evenement peut arriver deux fois (reprise de
   * liaison juste apres l'avoir recu), et poser deux fois la meme maison
   * laisserait deux maillages superposes que plus rien ne pourrait retirer.
   */
  private appliquerEvenementMonde(ev: EvenementMonde): void {
    switch (ev.quoi) {
      case 'batiment-pose': {
        if (this.batiments.get(ev.batiment.id)) return
        this.batiments.ajouter(ev.batiment)
        this.renduBatiments.ajouter(ev.batiment)
        this.bloquerSiPlein(ev.batiment)
        this.majCouvertureBatiments()
        journal.info('reseau', `${ev.batiment.kind} apparu`, { id: ev.batiment.id })
        break
      }
      case 'batiment-demoli': {
        const b = this.batiments.get(ev.id)
        if (!b) return
        const def = batimentDef(b.kind)
        this.grille.libererEmprise(b.pos, def.taille.l, def.taille.p, b.rot)
        this.batiments.retirer(b.id)
        this.renduBatiments.retirer(b.id)
        this.majCouvertureBatiments()
        if (this.interieur === b.id) this.sortir()
        // Les pensionnaires suivront leur propre evenement; on les libere ici
        // sans rien reecrire cote serveur, sinon chaque client renverrait la
        // meme nouvelle a tous les autres.
        this.zoo.libererAnimauxDe(b.id, false)
        journal.info('reseau', `${b.kind} demoli ailleurs`, { id: b.id })
        break
      }
      case 'vehicule-pose': {
        // Le meme evenement sert a POSER et a annoncer un changement d'etat —
        // le verrou passe par la. Deja connu veut donc dire « mets a jour ce
        // qui a change », pas « ignore »: sans cette branche, une voiture
        // verrouillee par son proprietaire restait ouverte chez les voisins,
        // et seul le serveur le savait.
        if (this.renduVehicules.majEtat(ev.vehicule)) return
        this.renduVehicules.ajouter(ev.vehicule)
        break
      }
      case 'vehicule-retire': {
        if (this.joueur.vehicule === ev.id) this.joueur.vehicule = null
        this.renduVehicules.retirer(ev.id)
        break
      }
      case 'ressource': {
        const r = this.ressources.find((x) => x.id === ev.ressourceId)
        if (!r || (r.pv === ev.pv && r.repousseA === ev.repousseA)) return
        r.pv = ev.pv
        r.repousseA = ev.repousseA
        this.indexRessources.majNoeud(r)
        this.renduRessources.majNoeud(r, this.tempsJeu)
        break
      }
      case 'jardin': {
        // On garde l'echeance la PLUS TARDIVE: deux clients qui cueillent le
        // meme jardin a une seconde d'ecart ne doivent pas se voler la repousse
        // l'un a l'autre. Le rendu suit tout seul, il relit la maturite a
        // chaque image.
        const connu = this.jardinsPrets.get(ev.jardin.batimentId) ?? 0
        if (ev.jardin.repousseA <= connu) return
        this.jardinsPrets.set(ev.jardin.batimentId, ev.jardin.repousseA)
        journal.info('reseau', 'jardin cueilli ailleurs', { id: ev.jardin.batimentId })
        break
      }
      case 'animal': {
        this.zoo.appliquerSauvegarde([ev.animal], this.batiments.liste())
        break
      }
      case 'animal-libere': {
        this.zoo.libererDepuisReseau(ev.animalId)
        break
      }
    }
  }

  /**
   * Relit le monde entier et se recale dessus.
   *
   * Appele apres une coupure: on ne sait pas ce qu'on a manque, et la base est
   * la seule a le savoir. On applique les differences plutot que de tout
   * reconstruire — refaire les maillages de tous les batiments ferait un a-coup
   * visible pour une reprise qui, la plupart du temps, ne change rien.
   */
  private async rattraperMonde(): Promise<void> {
    try {
      // Avec le jeton: le serveur exige un compte, et rend a chacun ses
      // propres jetons de proprietaire (masques pour ceux des autres).
      const r = await fetch('/api/monde', { headers: { 'x-jeu-token': this.token } })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const m = (await r.json()) as {
        batiments: Batiment[]
        vehicules: Vehicule[]
        ressourcesEpuisees: { ressourceId: number; pv: number; repousseA: number }[]
        animaux: AnimalSauve[]
        jardins: JardinCueilli[]
      }
      let poses = 0
      let retires = 0
      const vus = new Set<string>()
      for (const b of m.batiments ?? []) {
        vus.add(b.id)
        if (!this.batiments.get(b.id)) {
          this.appliquerEvenementMonde({ quoi: 'batiment-pose', batiment: b })
          poses++
        }
      }
      for (const id of this.batiments.ids()) {
        if (vus.has(id)) continue
        this.appliquerEvenementMonde({ quoi: 'batiment-demoli', id })
        retires++
      }

      const vusVehicules = new Set<string>()
      for (const brut of m.vehicules ?? []) {
        const v = validerVehicule(brut)
        if (!v) continue
        vusVehicules.add(v.id)
        if (!this.renduVehicules.vehicules().some((x) => x.id === v.id)) {
          this.renduVehicules.ajouter(v)
        }
      }
      for (const v of [...this.renduVehicules.vehicules()]) {
        if (!vusVehicules.has(v.id)) this.appliquerEvenementMonde({ quoi: 'vehicule-retire', id: v.id })
      }

      for (const e of m.ressourcesEpuisees ?? []) {
        this.appliquerEvenementMonde({ quoi: 'ressource', ...e })
      }
      // Un jardin absent de la liste a repousse pendant la coupure: le serveur
      // purge les echeances echues, donc l'absence est une information.
      const cueillis = new Set((m.jardins ?? []).map((j) => j.batimentId))
      for (const id of [...this.jardinsPrets.keys()]) {
        if (!cueillis.has(id)) this.jardinsPrets.delete(id)
      }
      for (const j of m.jardins ?? []) this.appliquerEvenementMonde({ quoi: 'jardin', jardin: j })
      if ((m.animaux ?? []).length > 0) this.zoo.appliquerSauvegarde(m.animaux, this.batiments.liste())
      journal.info('reseau', `monde rattrape: ${poses} batiments apparus, ${retires} disparus`)
    } catch (e) {
      journal.warn('reseau', `rattrapage impossible: ${String(e)}`)
    }
  }

  // ------------------------------------------------------------- boucle

  demarrer(): void {
    this.brancherReseau()
    this.tempsPrecedent = performance.now()
    this.compteurFps = this.tempsPrecedent
    const boucle = (maintenant: number): void => {
      if (this.arrete) return
      this.boucleId = requestAnimationFrame(boucle)
      this.frame(maintenant)
    }
    this.boucleId = requestAnimationFrame(boucle)
    journal.info('boucle', 'demarree')
  }

  /** Se cale sur l'heure du serveur (voir client/horloge-suivie.ts). */
  private recalerHorloge(tempsJeu: number, dUnCoup: boolean): void {
    const pose = this.horlogeSuivie.recaler(tempsJeu, this.tempsJeu, dUnCoup)
    if (pose === null) return
    this.tempsJeu = pose
    this.horloge = horlogeDepuis(this.tempsJeu)
  }

  private frame(maintenant: number): void {
    // Un onglet en arriere-plan produit des dt enormes: on les borne pour ne
    // pas faire bondir le joueur a travers le monde ni sauter la nuit.
    const brut = (maintenant - this.tempsPrecedent) / 1000
    const dt = clamp(brut, 0, 0.1)
    this.tempsPrecedent = maintenant

    /*
     * HORS LIGNE, LE MONDE S'ARRETE.
     *
     * Le jeu continuait sans le serveur: on marchait, on batissait, on cueillait
     * dans un monde que personne ne voyait, et au retour de la liaison le
     * serveur reprenait la main sur les positions. Rien a l'ecran ne
     * distinguait « je suis seul » de « je suis coupe », ce qui a fait croire
     * tout un apres-midi qu'on ne voyait jamais les autres.
     *
     * On sort AVANT la simulation, l'horloge et la publication: seul le voile
     * vieillit. `tempsPrecedent` vient d'etre remis a jour, donc la reprise ne
     * ramasse pas la coupure entiere d'un coup.
     *
     * Ne concerne que le monde partage: `this.salon` est nul pour un invite,
     * qui joue seul par construction et n'a aucune liaison a perdre.
     */
    if (this.salon && !this.salon.connecte) {
      this.voileHorsLigne.montrer(this.salon.refus)
      this.voileHorsLigne.maj(dt, this.salon.refus)
      this.majRendu(0, maintenant)
      return
    }
    if (this.voileHorsLigne.affiche) this.voileHorsLigne.cacher()

    this.tempsJeu += this.horlogeSuivie.pasNaturel(dt, brut)
    this.tempsJeu += this.horlogeSuivie.pasRattrapage(dt)
    this.horloge = horlogeDepuis(this.tempsJeu)

    this.simuler(dt, brut)
    // L'ETAT AVANT LES ENTREES, et l'ordre compte une seule fois mais il
    // compte: c'est le premier etat publie qui POSE le joueur sur le serveur
    // (voir `poser` dans server/deplacement.ts). Envoyer les entrees d'abord,
    // c'etait faire partir le bonhomme de sa derniere position ecrite en base,
    // qui peut dater d'un quart de minute de marche.
    this.publierEtat(maintenant)
    this.publierEntrees()
    this.battement.battre(dt, this.joueur, this.interieur, this.horloge, this.dernierIndice)
    this.majCibles()
    this.majRendu(dt, maintenant)

    this.minuterieSauvegarde += dt
    if (this.minuterieSauvegarde >= INTERVALLE_SAUVEGARDE) {
      this.minuterieSauvegarde = 0
      void this.sauver()
    }
  }

  /**
   * Ce que le vehicule change au deplacement, s'il y en a un.
   *
   * En UN seul endroit parce que la reconciliation doit rejouer les entrees
   * avec exactement les memes options que la prediction: deux lectures de la
   * monture qui divergeraient donneraient deux positions differentes pour la
   * meme entree, et c'est precisement le defaut qu'on cherche a rendre
   * impossible.
   */
  private optionsDeplacement(): { multiplicateur: number; aBord: boolean } {
    const monture = this.joueur.vehicule
      ? this.renduVehicules.vehicules().find((v) => v.id === this.joueur.vehicule) ?? null
      : null
    return { multiplicateur: monture ? multiplicateurVitesse(monture) : 1, aBord: monture !== null }
  }

  private simuler(dt: number, brut = dt): void {
    // Pas fixe: la simulation doit rester reproductible.
    this.resteTick += dt
    let ticks = 0
    while (this.resteTick >= TICK_DT && ticks < MAX_CATCHUP_TICKS) {
      this.resteTick -= TICK_DT
      ticks++
      const options = this.optionsDeplacement()

      /*
       * LE PAS D'AUTORITE, et c'est le meme des deux cotes du fil.
       *
       * On fabrique l'ENTREE du tick — la direction visee, la vitesse voulue,
       * la duree du pas — on l'applique TOUT DE SUITE chez soi, et on la garde
       * pour l'envoyer. Le serveur rejouera exactement `appliquerEntree` sur
       * son propre terrain, avec les memes nombres: c'est la seule facon pour
       * que sa reponse ne provoque aucune secousse. Toute la mecanique
       * (glissement le long des murs, resolution des cloisons, deblocage) vit
       * dans cette fonction partagee, et nulle part ailleurs.
       */
      /*
       * UNE DESCENTE DE GLISSADE FABRIQUE SON ENTREE, ET RIEN DE PLUS.
       *
       * C'est la seule facon de descendre qui tienne dans ce moteur: le pas
       * partage reste le pas partage, seule l'INTENTION change de source — le
       * toboggan au lieu du joystick. `appliquerEntree` ne sait rien des
       * glissades et n'a rien a apprendre, et le serveur rejoue la meme chose.
       */
      if (this.descente) this.majDescente(this.descente)
      const entree = this.descente
        ? this.entreeDeDescente(this.descente, ++this.sequence)
        : entreeDeTick(this.joueur, ++this.sequence)
      const avantX = this.joueur.pos.x
      const avantZ = this.joueur.pos.z
      const degage = appliquerEntree(
        this.joueur,
        entree,
        this.terrain,
        this.grille,
        this.batiments.liste(),
        options,
        this.noeudsSolides(),
      )
      // A bord, on JOURNALISE le deblocage sans le dire: voir `signaler`.
      if (degage) this.filet.signaler({ x: avantX, z: avantZ }, degage, this.tempsJeu, this.joueur.vehicule !== null)
      // A bord, l'autorite est suspendue des deux cotes: le serveur ne rejoue
      // pas la mecanique des vehicules et n'a donc rien a faire de ces entrees.
      if (this.salon && this.joueur.vehicule === null) this.reconciliation.noter(entree)
      // Ce que le serveur a corrige se resorbe ici, un cinquieme par tick: le
      // joueur ne doit jamais voir sa correction.
      this.reconciliation.lisser(this.joueur)
      // Le spa passe par les besoins EXISTANTS, pas par une jauge parallele:
      // s'y tremper rend de l'energie, exactement comme recolter en coute. Il ne
      // fait PAS nager — la nage est l'etat de la mer et de la piscine.
      const auSpa = dansSpa(this.batiments.liste(), this.joueur.pos.x, this.joueur.pos.z, this.joueur.niveau)
      majNageEtBesoins(this.joueur, this.terrain, TICK_DT * 1000, options, auSpa)

      // Les vehicules AVANT les animaux et APRES les murs: le recalage du pilote
      // doit etre le dernier mot sur sa position.
      for (const v of this.renduVehicules.vehicules()) {
        const aBord = this.joueur.vehicule === v.id
        /*
         * UN AVION VOLE TOUT SEUL, et emmene son pilote.
         *
         * C'est l'exception a la mecanique de tous les autres vehicules, et
         * elle est traitee ici, en amont: `majAvion` avance l'appareil selon SA
         * physique (cap, vitesse, altitude), puis on assoit le bonhomme dedans.
         * `majVehicule`, pour un avion, ne fait plus que rendre compte.
         */
        if (estAvion(v.kind)) {
          const e = this.etatAvion(v)
          const avant = e.phase
          // D'ou l'on part: le rendu dessine ENTRE cet etat-la et le suivant,
          // sinon l'appareil saute d'un metre une image sur trois.
          this.renduVehicules.noterTick(v)
          majAvion(v, e, TICK_DT, this.terrain, this.contexteAvion(v, e))
          if (avant !== 'gare' && e.phase === 'gare') this.avionGare(v)
          if (aBord) {
            this.joueur.pos.x = v.pos.x
            this.joueur.pos.z = v.pos.z
            this.joueur.rot = v.rot
            // Le pilote ne marche pas: il est assis. Sans ca, le bonhomme
            // pedalait dans le vide a vingt metres par seconde.
            arreter(this.joueur)
            // La camera s'ecarte des que les roues quittent le sol, et revient
            // au cadrage du bonhomme une fois gare: a vingt metres par seconde,
            // le cadrage ordinaire ne montre plus ou l'on va.
            this.rig.vueDeVol(!auSol(e))
          }
          continue
        }
        // Ou le pilote voulait aller: c'est ce qui dit, en cas d'arret, si
        // c'est l'eau (ou la rive) ou un obstacle qui a barre la route.
        const vise = { x: this.joueur.pos.x, z: this.joueur.pos.z }
        const fin = this.joueur.chemin[this.joueur.chemin.length - 1]
        // Un dernier pas refuse A L'ARRIVEE (le coin qui frole le mur du garage
        // ou l'on vient de se ranger) n'est pas un arret en route: on se tait.
        const arrive = fin !== undefined && this.joueur.direction === null && dist(vise.x, vise.z, fin.x, fin.z) < 1.5
        const roulait =
          aBord && !arrive && (this.joueur.chemin.length > 0 || this.joueur.direction !== null)
        // Les batiments ou l'on entre ne sont pas barres dans la grille: leurs
        // murs arretent la voiture point par point de son emprise (`dansUnMur`).
        const voisins = this.batiments
          .liste()
          .filter((b) => Math.hypot(b.pos.x - v.pos.x, b.pos.z - v.pos.z) < RAYON_MURS_VEHICULE)
        /*
         * SUR L'EAU, LA GRILLE NE DIT RIEN D'UTILE: elle marque toute l'eau
         * bloquee (pour que l'A* du pieton n'y trace rien), si bien qu'avec
         * elle comme obstacle chaque point d'une barque etait refuse — pas un
         * centimetre en pleine mer, et « La barque ne va pas à terre » pour
         * toute explication (vu au telephone le 13 octobre). A une barque, seuls
         * les ouvrages s'opposent; le milieu, lui, est garde par `milieuValide`.
         */
        const surEau = vehiculeDef(v.kind).milieu === 'eau'
        const r = majVehicule(v, TICK_DT, this.joueur, this.terrain, {
          bloque: (bx, bz) =>
            (surEau ? this.grille.empriseCouvre(bx, bz) : !this.grille.estLibreMonde(bx, bz)) ||
            dansUnMur(voisins, bx, bz, RAYON_MUR_VEHICULE),
          onPassage: aBord && v.kind === 'voiture' ? (px, pz, l) => this.coucherVegetation(px, pz, l) : undefined,
        })
        if (aBord) {
          this.joueur.pos.x = r.pos.x
          this.joueur.pos.z = r.pos.z
          if (r.bloque) {
            arreter(this.joueur)
            this.signalerAncre(v)
            if (roulait) this.signalerObstacle(v, vise)
          }
        }
      }

      // LA FAUNE EST AU SERVEUR quand le monde est partage. Tant qu'aucune
      // trame n'est arrivee (invite, liaison coupee, partie solo) le client la
      // simule lui-meme, exactement comme avant: c'est ce qui garde le jeu
      // jouable tout seul.
      if (!this.fauneRecue) {
        majAnimaux(this.animaux, TICK_DT, this.joueur.pos, this.terrain, this.horloge, this.aleaAnimaux, this.appat, this.token)
      }
    }
    if (ticks >= MAX_CATCHUP_TICKS) this.resteTick = 0

    // Le lissage suit le TEMPS DES IMAGES, pas les ticks: l'ecart entre deux
    // positions successives est ce dont le rendu deduit la vitesse d'animation
    // des betes, et il doit donc etre continu.
    if (this.fauneRecue) this.suiviFaune.appliquer(this.animaux, dt, this.terrain)

    // Action verrouillee en cours (repas, sommeil, pose, creusage, peche).
    if (this.minuterieAction > 0) {
      this.minuterieAction -= dt
      if (this.minuterieAction <= 0) {
        const a = this.enAttente
        if (a && a.quoi === 'terrain') this.terminerActionTerrain(a.action)
        if (
          this.joueur.etat === 'mange' ||
          this.joueur.etat === 'dort' ||
          this.joueur.etat === 'construit' ||
          this.joueur.etat === 'recolte'
        ) {
          this.joueur.etat = 'immobile'
        }
      }
    }

    // Recolte d'un noeud: un coup toutes les DUREE_COUP secondes, module par la
    // forme du bonhomme. Les actions de terrain, elles, sont a duree fixe.
    if (this.joueur.etat === 'recolte' && this.enAttente?.quoi === 'recolte') {
      this.minuterieCoup -= dt * facteurRecolte(this.joueur.besoins)
      if (this.minuterieCoup <= 0) {
        this.minuterieCoup = DUREE_COUP
        this.prochainCoupMs = performance.now() + (DUREE_COUP / facteurRecolte(this.joueur.besoins)) * 1000
        this.recolter()
      }
    }

    /*
     * Le piochage au filon: la MEME cadence modulee par la forme que la recolte
     * — un mineur fatigue frappe plus lentement — mais rien ne tombe jamais.
     * C'est l'energie qui met fin a la seance, pas le gisement.
     */
    if (this.joueur.etat === 'recolte' && this.enAttente?.quoi === 'filon') {
      this.minuterieCoup -= dt * facteurRecolte(this.joueur.besoins)
      if (this.minuterieCoup <= 0) {
        this.minuterieCoup = DUREE_COUP_MINE
        this.prochainCoupMs = performance.now() + (DUREE_COUP_MINE / facteurRecolte(this.joueur.besoins)) * 1000
        this.piocher()
      }
    }

    // Peche en cours.
    this.peche.maj(dt)

    // Retour du zoom apres un feu d'artifice.
    if (this.minuterieFeu > 0) {
      this.minuterieFeu -= dt
      if (this.minuterieFeu <= 0 && this.zoomAvantFeu !== null) {
        this.rig.zoomer(this.zoomAvantFeu / this.rig.zoom)
        this.zoomAvantFeu = null
        journal.debug('feu', 'zoom restaure')
      }
    }

    // Action en attente declenchee a l'arrivee. Un noeud a recolter se vise
    // en son CENTRE, et depuis que troncs et rochers arretent le pieton ce
    // dernier point est inatteignable: le bonhomme butait sur l'ecorce, le
    // chemin ne se vidait jamais et la hache ne se levait pas (campagne du
    // 14 octobre). C'est la portee qui juge, pas le dernier point.
    const finDeChemin =
      this.joueur.chemin.length === 0 || (this.joueur.chemin.length === 1 && this.enAttente?.quoi === 'recolte')
    if (this.enAttente && this.joueur.etat !== 'recolte' && finDeChemin) {
      const a = this.enAttente
      const pos = this.positionAction(a)
      if (pos && dist(this.joueur.pos.x, this.joueur.pos.z, pos.x, pos.z) <= this.porteeCible(a) + ARRIVE_EPSILON) {
        this.declencherAction()
      } else if (pos === null) {
        this.enAttente = null
      }
    }

    this.progres.verifier(this.joueur, dt)

    // Entree et sortie de maison, deduites de la position reelle.
    const dedans = batimentContenant(this.batiments.liste(), this.joueur.pos.x, this.joueur.pos.z)
    if (dedans && this.interieur !== dedans.id) this.entrer(dedans.id)
    else if (!dedans && this.interieur !== null) this.sortir()

    /*
     * LE BALAYAGE D'ENTRETIEN SUIT L'HORLOGE MURALE, pas le pas de simulation.
     *
     * Il se decomptait avec `dt`, qui est BORNE a 0,1 s pour empecher un onglet
     * revenu d'arriere-plan de faire bondir le bonhomme. Sur une machine lente
     * — deux images par seconde en rendu logiciel — une image consomme 0,5 s de
     * temps reel mais ne retire que 0,1 s au compteur: le balayage annonce
     * « toutes les cinq secondes » n'avait lieu qu'une fois par vingt-cinq
     * secondes, et cinq fois moins souvent encore quand la machine peine
     * vraiment. L'herbe couchee par la voiture mettait donc des minutes a se
     * redresser, precisement quand tout rame.
     *
     * Un entretien n'est pas de la simulation: rien de ce qu'il fait ne depend
     * du pas fixe, donc il doit suivre le temps VRAI.
     */
    this.minuterieRepousse -= brut
    if (this.minuterieRepousse <= 0) {
      this.minuterieRepousse = 5
      // On ne rafraichit que les noeuds concernes: tout reecrire reteleversait
      // les 2 x 5229 matrices d'instance en une seule image.
      const reveilles = repousserListe(this.ressources, this.tempsJeu, this.batiments.liste())
      for (const r of reveilles) {
        this.indexRessources.majNoeud(r)
        this.renduRessources.majNoeud(r, this.tempsJeu)
      }
      if (reveilles.length > 0) journal.info('ressources', `${reveilles.length} noeuds ont repousse`)

      // L'herbe couchee par la voiture se redresse au bout de deux journees.
      const redressees = this.renduProps.majRepousse(this.tempsJeu)
      if (redressees > 0) journal.info('vegetation', `${redressees} touffes redressees`)
    }
  }

  private minuterieRepousse = 5
  private dernierIndice: string | null = null
  /** Reutilise chaque frame: allouer un Vector3 par image nourrit le ramasse-miettes. */
  private dirCameraTemp = new THREE.Vector3()
  private posJoueurTemp = new THREE.Vector3()
  /** Enfoncement courant dans l'eau, lisse pour eviter le saut a l'entree. */
  private enfoncement = 0
  /** Prochaine cueillette possible par jardin, en temps de jeu. */
  private jardinsPrets = new Map<string, number>()
  /**
   * Maturite d'un jardin pour le rendu: 0 tout juste cueilli, 1 mur.
   *
   * Passee au rendu, elle est ce qui rend la repousse VISIBLE. Un jardin vide
   * ressemblait trait pour trait a un jardin mur: le proprietaire y est revenu
   * six fois en quatorze minutes pour apprendre par un message qu'il n'y avait
   * rien. Fonction flechee: la boucle de jeu l'appelle chaque image, une
   * fermeture par image nourrirait le ramasse-miettes.
   */
  private maturiteJardin = (id: string): number => {
    const pret = this.jardinsPrets.get(id)
    if (pret === undefined || this.tempsJeu >= pret) return 1
    return clamp(1 - (pret - this.tempsJeu) / DAY_LENGTH_MS, 0, 1)
  }
  /** Temps de jeu a partir duquel la cabane sert une nouvelle collation. */
  private collationPrete = 0
  /** Temps de jeu a partir duquel le restaurant de l'aerogare sert a nouveau. */
  private restaurantPret = 0
  /**
   * L'ETAT DE VOL DE CHAQUE APPAREIL, par identifiant de vehicule.
   *
   * En memoire seulement: ce qui est persiste, c'est un avion GARE (voir
   * `avionGare`). Un onglet ferme en plein vol n'abandonne donc pas un appareil
   * fantome au milieu du ciel — il le laisse a l'aeroport d'ou il est parti.
   */
  private readonly vols = new Map<string, EtatAvion>()
  /** Modele de vehicule en cours de pose, sinon null. */
  private vehiculeEnPose: VehiculeKind | null = null
  /** Nombre d'accessoires couches depuis le debut, pour le journal et les tests. */
  private tracesPosees = 0
  private surRedim: (() => void) | null = null
  private surClavier: ((e: KeyboardEvent) => void) | null = null
  /** Branche par main.ts: vide l'identite locale et recharge. */
  onDeconnexion: (() => void) | null = null

  private positionAction(a: NonNullable<ActionEnAttente>): Vec2 | null {
    if (a.quoi === 'terrain') return a.pos
    if (a.quoi === 'jardin') return this.batiments.position(a.id)
    if (a.quoi === 'collation') return this.batiments.position(a.id)
    if (a.quoi === 'ecole') return this.batiments.position(a.id)
    if (a.quoi === 'recolte') return this.ressources.find((r) => r.id === a.id)?.pos ?? null
    if (a.quoi === 'nourrir' || a.quoi === 'adopter') {
      return this.animaux.find((x) => x.id === a.id)?.pos ?? null
    }
    // Un vehicule n'est pas un batiment: il ne se cherche pas dans la meme
    // liste, et l'y chercher rendait null, donc « impossible d'aller la ».
    if (a.quoi === 'vehicule') {
      return this.renduVehicules.vehicules().find((v) => v.id === a.id)?.pos ?? null
    }
    const b = this.batiments.get(a.id)
    if (!b) return null
    // Viser le meuble, pas le centre de la maison: c'est la distance au
    // comptoir, a la table ou au lit qui declenche l'action.
    if (a.quoi === 'cuisine') return positionCuisine(b)
    if (a.quoi === 'table') return positionTable(b)
    if (a.quoi === 'lit') return positionLit(b)
    if (a.quoi === 'pupitre') return positionMeuble(b, 'pupitre')
    if (a.quoi === 'filon') return positionMeuble(b, 'filon')
    if (a.quoi === 'restaurant') return positionRestaurant(b)
    if (a.quoi === 'entrer') return porteInterieur(b)
    if (a.quoi === 'escalier') return positionEscalier(b)
    if (a.quoi === 'ascenseur') return positionAscenseur(b)
    if (a.quoi === 'portable') return positionPortable(b)
    // Le haut du toboggan vient du PLAN, comme tout le reste: `cheminGlissade`
    // est la meme fonction que celle qui trace la descente.
    if (a.quoi === 'glissade') return cheminGlissade(b, a.index)?.depart ?? null
    return b.pos
  }

  /**
   * L'infobulle d'un animal a portee de bras, et l'appat qu'on lui tend.
   *
   * C'est ici que se decide `this.appat`, qui commande aussi l'attirance dans
   * la simulation: tendre la main, c'est offrir a l'animal VISE la premiere de
   * ses gourmandises qu'on ait sur soi.
   */
  private indiceAnimal(a: Animal): string {
    const def = animalDef(a.kind)
    const nom = def.nom
    if (estFamilier(a)) {
      this.appat = null
      if (!this.zoo.estSonAnimal(a)) return `${nom} — l’animal de quelqu’un d’autre`
      if (a.ancre !== null) return `${nom} ${accorde('apprivoisé', def)} — ${leComplement(def)} rendre à la nature`
      const accueil = this.zoo.accueilPour(a)
      if (accueil.genre === 'maison') return `Adopter ${leNom(def)} — ${pronom(def)} habitera ici`
      if (accueil.genre === 'enclos') return `Installer ${leNom(def)} dans l’enclos`
      return `${nom} ${accorde('apprivoisé', def)} — ${pronom(def)} te suit`
    }
    this.appat = this.zoo.friandisePour(a, this.joueur.inventaire)
    if (this.appat) return `Nourrir ${leNom(def)} (${objet(this.appat).nom}) — ${a.repas}/${REPAS_APPRIVOISEMENT}`
    return `${UnNom(def)} — apprivoise-${leComplement(def)} avec ${animalDef(a.kind)
      .friandises.map((f) => objet(f).nom.toLowerCase())
      .join(', ')}`
  }

  /**
   * Vrai si un tap sur cette bete FERAIT quelque chose: c'est un de nos
   * familiers, ou une bete sauvage a qui l'on peut tendre une friandise. Les
   * autres ne disputent rien a l'arbre ni au meuble d'a cote — meme regle pour
   * le tap (`cibleAuPoint`) et pour l'infobulle (`animalAvantRessource`).
   */
  private animalQuiCompte(a: Animal): boolean {
    if (estFamilier(a)) return this.zoo.estSonAnimal(a)
    return this.zoo.friandisePour(a, this.joueur.inventaire) !== null
  }

  /**
   * Vrai si l'animal a portee doit passer devant le noeud de ressource a portee.
   *
   * L'infobulle doit dire la MEME chose que ce que le tap va faire, sinon elle
   * ment: elle annoncait « Couper — Arbre » a un joueur penche sur un lapin.
   * Meme arbitrage que `cibleAuPoint`, meme marge.
   */
  private animalAvantRessource(): boolean {
    const a = this.cibleAnimal
    if (!a || !this.animalQuiCompte(a)) return false
    const r = this.cibleRessource
    if (!r) return true
    const { x, z } = this.joueur.pos
    return dist(x, z, a.pos.x, a.pos.z) <= dist(x, z, r.pos.x, r.pos.z) + MARGE_ANIMAL_VISE
  }

  /** Determine ce que le joueur peut faire ici et met a jour l'infobulle. */
  private majCibles(): void {
    const { x, z } = this.joueur.pos

    /*
     * A L'ETAGE, l'infobulle dit exactement ce que `cibleAuPoint` accepte, et
     * rien de plus: le portable, l'escalier, le spa. Annoncer « Couper — Arbre »
     * a quelqu'un qui est sur son toit serait le meme mensonge que celui deja
     * paye — l'infobulle doit dire ce que le tap va faire.
     */
    if (this.joueur.niveau > 0) {
      this.cibleRessource = null
      this.cibleAnimal = null
      this.cibleMeuble = null
      this.appat = null
      this.actionTerrain = null
      this.renduRessources.surbriller(null)
      this.renduAnimaux.surbriller(null)
      this.hud.majAncre(false, false)
      this.hud.majVerrou(false, false)
      this.hud.majMonde(false, 2)

      let ici: string | null = null
      const gliss = this.batiments.glissadeProche(x, z, REACH, this.joueur.niveau)
      if (this.descente) {
        // Pendant la descente, l'infobulle dit ce qui se passe: sans elle, le
        // bonhomme file tout seul et rien n'explique pourquoi le tap ne repond
        // plus.
        ici = 'Tu descends la glissade…'
      } else if (gliss) {
        // L'infobulle annonce EXACTEMENT ce que le tap va faire, et nomme la
        // glissade visee: trois departs a deux metres l'un de l'autre, il faut
        // savoir laquelle on prend.
        const nom = glissadesDe(gliss.batiment.kind)[gliss.index]?.nom ?? 'Glissade'
        ici = `Glisser — ${nom}`
      } else if (this.batiments.restaurantProche(x, z, REACH, this.joueur.niveau)) {
        ici = 'Manger au restaurant (gratuit)'
      } else if (this.batiments.portableProche(x, z, REACH, this.joueur.niveau)) {
        ici = 'Travailler sur le portable — Bureau'
      } else if (dansSpa(this.batiments.liste(), x, z, this.joueur.niveau)) {
        ici = 'Tu te détends dans le spa — l’énergie remonte'
      } else if (this.batiments.tableEtageProche(x, z, REACH, this.joueur.niveau)) {
        ici = 'Manger à table — Salle à manger'
      } else if (this.batiments.ascenseurProche(x, z, REACH)) {
        ici = 'Prendre l’ascenseur'
      } else if (this.batiments.escalierProche(x, z, REACH)) {
        // Le nom du niveau vient du PLAN quand le modele en donne un: « le
        // salon » n'a aucun sens au bord d'un bassin.
        const bas = planEtageDe(this.batimentSousMesPieds()?.kind ?? 'villa_etage')?.nomsNiveaux?.[0]
        ici = bas ? `Redescendre — ${bas}` : 'Redescendre au salon'
      }
      this.dernierIndice = ici
      this.hud.majCible(ici)
      return
    }

    this.cibleRessource = this.indexRessources.plusProche(x, z, REACH, this.tempsJeu, this.batiments.liste())
    this.cibleAnimal = animalLePlusProche(this.animaux, x, z, REACH)
    // Aucun animal a portee: on ne tend plus rien.
    if (!this.cibleAnimal) this.appat = null
    const meuble = this.batiments.meubleProche(x, z, REACH, this.interieur)
    this.cibleMeuble = meuble ? meuble.meuble : null
    // Le point que designe l'infobulle: toucher la pastille revient a toucher
    // ce point-la (voir `surPastille`). Null quand elle ne nomme rien de precis.
    this.pointIndice =
      meuble && this.mode === 'jeu' ? this.positionAction({ quoi: meuble.meuble, id: meuble.batiment.id }) : null

    this.renduRessources.surbriller(this.cibleRessource)
    this.renduAnimaux.surbriller(this.cibleAnimal)

    let indice: string | null = null
    if (this.mode === 'construction') {
      indice = null
    } else if (this.mode === 'demolition') {
      // L'administrateur demolit tout (avec confirmation pour ce qui n'est pas
      // a lui): « une de tes constructions » lui mentait.
      indice = estAdmin(this.compte)
        ? 'Touche une construction pour la démolir'
        : 'Touche une de tes constructions pour la démolir'
    } else if (this.cibleMeuble === 'cuisine') {
      indice = 'Cuisiner'
    } else if (this.cibleMeuble === 'table') {
      indice = 'Manger à table'
    } else if (this.cibleMeuble === 'lit') {
      indice = 'Dormir'
    } else if (this.cibleMeuble === 'pupitre') {
      indice = 'Travailler au pupitre'
    } else if (this.cibleMeuble === 'filon') {
      // L'infobulle dit ce que le tap va faire, la fatigue comprise: annoncer
      // « Piocher » a un bonhomme a plat serait promettre un geste sans effet.
      indice = peutMiner(this.joueur.besoins)
        ? 'Piocher le filon — minerai et pierre'
        : 'Filon — tu es trop fatigué pour piocher'
    } else if (this.cibleRessource && !this.animalAvantRessource()) {
      const def = ressourceDef(this.cibleRessource.kind)
      const verbe = def.action === 'couper' ? 'Couper' : def.action === 'miner' ? 'Miner' : 'Cueillir'
      indice = `${verbe} — ${def.nom}`
      this.pointIndice = this.cibleRessource.pos
    } else if (this.cibleAnimal) {
      indice = this.indiceAnimal(this.cibleAnimal)
      if (this.animalQuiCompte(this.cibleAnimal)) this.pointIndice = this.cibleAnimal.pos
    } else if (this.cibleRessource) {
      const def = ressourceDef(this.cibleRessource.kind)
      const verbe = def.action === 'couper' ? 'Couper' : def.action === 'miner' ? 'Miner' : 'Cueillir'
      indice = `${verbe} — ${def.nom}`
      this.pointIndice = this.cibleRessource.pos
    } else if (this.batiments.escalierProche(x, z, REACH)) {
      indice = 'Monter sur la terrasse'
    } else if (this.batiments.ascenseurProche(x, z, REACH)) {
      indice = 'Prendre l’ascenseur'
    } else if (this.interieur === null) {
      const porte = this.batiments.porteProche(x, z, REACH)
      if (porte) indice = `Entrer — ${batimentDef(porte.kind).nom}`
    }
    if (indice === null) {
      const cabane = this.batiments.cabaneProche(x, z, REACH + 1.5)
      if (cabane) {
        indice =
          this.tempsJeu >= this.collationPrete ? 'Prendre une collation (gratuit)' : 'La cabane en prépare une autre…'
      }
    }

    if (indice === null) {
      const ecole = this.batiments.ecoleProche(x, z, REACH + 2)
      if (ecole) indice = 'Faire des exercices — École'
    }

    if (indice === null) {
      // L'infobulle annonce EXACTEMENT ce que le tap va faire, comme partout.
      if (this.batiments.villageProche('musee', x, z, REACH + 2)) indice = 'Dessiner et voir la galerie — Musée'
      else if (this.batiments.villageProche('bibliotheque', x, z, REACH + 2)) {
        indice = 'Lire une bande dessinée — Bibliothèque'
      }
    }

    if (indice === null) {
      const jardin = this.batiments.jardinProche(x, z, REACH + 1)
      if (jardin) {
        const pret = this.jardinsPrets.get(jardin.id) ?? 0
        // Nommer le modele vise: devant une serre, « Cueillir le jardin »
        // laisserait croire qu'on parle d'autre chose, et l'infobulle doit dire
        // exactement ce que le tap va faire.
        const nom = batimentDef(jardin.kind).nom
        indice = this.tempsJeu >= pret ? `Cueillir — ${nom}` : `${nom} en repousse`
      }
    }

    // A bord: l'ancre et la peche prennent la main sur le reste.
    if (this.joueur.vehicule !== null) {
      const v = this.renduVehicules.vehicules().find((x2) => x2.id === this.joueur.vehicule)
      // AUX COMMANDES D'UN AVION, l'infobulle dit la PHASE du vol: c'est la
      // seule chose que le pilote ait besoin de savoir, et elle change tout le
      // temps. Ni ancre ni peche la-haut; le verrou, lui, s'offre AU PARKING —
      // sans lui, un avion n'etait jamais verrouillable, et n'importe qui
      // pouvait partir avec celui d'Amelia.
      if (v && estAvion(v.kind)) {
        this.hud.majAncre(false, false)
        this.hud.majVerrou(estProprietaire(v, this.token) && estGare(this.etatAvion(v)), v.verrouille)
        // Le depart pour l'autre monde ne s'offre qu'a l'ARRET, a un aeroport,
        // et seulement s'il y a la-bas une piste ou se poser.
        const ailleurs = estGare(this.etatAvion(v)) ? this.batiments.aeroportAutreMonde(v.pos.x, v.pos.z, v.retour) : null
        this.hud.majMonde(ailleurs !== null, ailleurs ? numeroDeMonde(ailleurs.pos.x, ailleurs.pos.z) : 2)
        this.actionTerrain = null
        this.dernierIndice = this.indiceAvion(v)
        this.hud.majCible(this.dernierIndice)
        return
      }
      if (v) {
        this.hud.majMonde(false, 2)
        // L'ancre ne s'offre que la ou elle sert: une barque, et de l'eau sous
        // la coque. Sur la route, un bouton d'ancre ne serait qu'une enigme.
        this.hud.majAncre(vehiculeDef(v.kind).peutPecher && this.terrain.estEau(v.pos.x, v.pos.z), v.ancre)
        // Le verrou n'est propose qu'a celui qui l'a bati: c'est aussi lui, et
        // lui seul, que le serveur laissera tourner la clef.
        this.hud.majVerrou(estProprietaire(v, this.token), v.verrouille)
        /*
         * `peutPecherDepuis` exige l'ancre, donc sa branche « sinon » ne
         * pouvait jamais conseiller de la jeter: le conseil etait ecrit et
         * inatteignable. On teste maintenant la barque a flot separement, ce
         * qui est le seul moment ou l'ancre veut dire quelque chose.
         */
        /*
         * PRES DE LA RIVE, LA BARQUE PROPOSE DE DESCENDRE. Ses deux conseils de
         * peche passaient toujours devant: a flot, la pastille ne disait jamais
         * « Descendre », et rien a l'ecran n'apprenait qu'on sort en touchant
         * la coque (vu au telephone le 13 octobre). La ligne se lance toujours
         * d'un tap sur l'eau; c'est seulement la pastille qui change d'avis.
         */
        const aQuai = vehiculeDef(v.kind).milieu === 'eau' && peutDescendre(v, this.terrain).ok
        if (this.peche.enCours) indice = this.peche.indice()
        else if (aQuai) indice = `Descendre ${duNom(vehiculeDef(v.kind))}`
        else if (peutPecherDepuis(v, this.terrain)) indice = 'Pêcher'
        else if (vehiculeDef(v.kind).peutPecher && this.terrain.estEau(v.pos.x, v.pos.z)) {
          indice = 'Jette l’ancre pour pêcher'
        } else indice = `Descendre ${duNom(vehiculeDef(v.kind))}`
        // « Descendre » se TOUCHE: la pastille vise la coque, et toucher sa
        // propre coque est precisement le geste qui fait descendre. Elle
        // n'etait qu'une etiquette: le doigt la traversait et la voiture
        // partait vers le sol derriere (vu au telephone le 13 octobre).
        this.pointIndice = indice?.startsWith('Descendre') ? { x: v.pos.x, z: v.pos.z } : null
        this.actionTerrain = null
        this.dernierIndice = indice
        this.hud.majCible(indice, this.pointIndice !== null)
        return
      }
    }

    this.hud.majAncre(false, false)
    // A pied, on ne verrouille rien: la clef se tourne depuis la place du
    // conducteur, ce qui evite d'avoir a viser une portiere au doigt.
    this.hud.majVerrou(false, false)
    // Et l'on ne decolle pas pour l'autre monde a pied.
    this.hud.majMonde(false, 2)

    if (indice === null) {
      const proche = vehiculeProche(this.renduVehicules.vehicules(), x, z, REACH + 1)
      // On ne propose pas de monter dans un appareil qui passe au-dessus: c'est
      // la meme regle que pour les cibles d'un tap.
      if (proche && !this.avionEnVol(proche)) {
        indice = `Monter dans ${leNom(vehiculeDef(proche.kind))}`
        this.pointIndice = proche.pos
      }
    }

    // Une ligne deja a l'eau depuis la rive: c'est elle qui compte.
    if (this.peche.enCours) {
      this.actionTerrain = null
      this.dernierIndice = this.peche.indice()
      this.hud.majCible(this.dernierIndice)
      return
    }

    // Creuser ou pecher: seulement si rien de plus interessant n'est a portee.
    this.actionTerrain = indice === null ? actionDisponible(this.terrain, x, z) : null
    if (this.actionTerrain) indice = NOM_ACTION[this.actionTerrain]
    /*
     * Sur le sable, creuser et pecher sont tous les deux possibles, et l'indice
     * n'en montrait qu'un. Le joueur ne pouvait pas deviner que toucher l'eau
     * lance une ligne: le geste existait sans que rien ne l'annonce. On le dit.
     */
    if (this.actionTerrain === 'creuser' && eauAPortee(this.terrain, x, z) !== null) {
      indice = 'Creuser le sable — ou touche l’eau pour pêcher'
    }
    /*
     * AVEC LA CANNE, C'EST LA PECHE QUI PARLE LA PREMIERE.
     *
     * Le journal d'une session entiere ne montre que « Creuser le sable »: le
     * proprietaire se promenait a vingt ou quarante metres du rivage, et la seule
     * phrase qui enseignait le geste n'apparaissait qu'une fois deja debout au
     * ras de l'eau. Une canne dans le sac veut dire qu'on veut pecher: des que de
     * l'eau est a portee de lancer, on l'annonce, et on garde une moitie de
     * phrase pour dire que le sable se creuse toujours.
     */
    if (indice === null || this.actionTerrain !== null) {
      const eau = aCanne(this.joueur.inventaire) && !this.joueur.nage
        ? eauAPortee(this.terrain, x, z, porteeLancer(this.joueur.inventaire))
        : null
      if (eau) {
        this.actionTerrain = 'pecher'
        indice = peutCreuser(this.terrain, x, z)
          ? 'Pêcher — touche l’eau (ou le sable pour creuser)'
          : 'Pêcher — touche l’eau'
      }
    }
    // Creuser se fait sous ses pieds: la pastille creuse la ou l'on se tient.
    if (this.actionTerrain === 'creuser' && this.pointIndice === null) this.pointIndice = { x, z }
    if (this.actionTerrain === 'pecher') this.pointIndice = null
    this.dernierIndice = indice
    this.hud.majCible(indice, this.pointIndice !== null)
  }

  /**
   * LA PASTILLE ORANGE EST UN BOUTON QUAND ELLE NOMME UNE CIBLE.
   *
   * Elle ressemblait a un bouton sans en etre un: le tap la traversait et
   * tombait sur le sol en dessous, si bien que toucher « Miner — Rocher »
   * faisait PARTIR le bonhomme loin du rocher (vu sur le telephone le 12
   * octobre). Toucher la pastille vaut maintenant toucher ce qu'elle nomme.
   */
  private surPastille(): void {
    const p = this.pointIndice
    if (!p) return
    journal.input('tap', `pastille: ${this.dernierIndice ?? ''}`, { x: p.x, z: p.z })
    this.taperLePoint({ x: p.x, z: p.z })
  }

  /**
   * Hauteur du sol sous quelqu'un, ETAGE COMPRIS.
   *
   * UNE SEULE fonction pour la question, comme `positionPossible` repond a « le
   * bonhomme peut-il etre ici ? ». Le joueur local, les voisins et la camera
   * doivent monter exactement de la meme hauteur: deux calculs de la meme chose
   * finiraient par ne plus etre d'accord, et l'un des deux se retrouverait a
   * marcher dans la dalle. Fonction flechee: la boucle de rendu la passe en
   * rappel a chaque image, une fermeture par image nourrirait le ramasse-miettes.
   */
  private solSousLesPieds = (x: number, z: number, niveau: number): number => {
    return hauteurNiveau(this.batiments.liste(), x, z, niveau) ?? this.terrain.hauteur(x, z)
  }

  private majRendu(dt: number, maintenant: number): void {
    const { x, z } = this.joueur.pos
    /*
     * LES VEHICULES D'ABORD, parce qu'un pilote s'assoit dans SON appareil.
     *
     * Un avion n'est plus dessine la ou la simulation vient de le mettre mais
     * ENTRE ses deux derniers etats (`alpha`, la fraction de tick deja
     * ecoulee): 50 ms de pas de simulation contre 16 ms d'image, c'est un
     * saut d'un metre une image sur trois a vingt metres par seconde. Le
     * bonhomme et la camera doivent suivre EXACTEMENT la meme pose, sinon le
     * pilote glisse sur son siege — d'ou cet appel remonte avant eux.
     */
    this.renduVehicules.maj(dt, maintenant / 1000, this.resteTick / TICK_DT)
    // A la nage on se tient a la surface: le fond marin descend a -6 m, y suivre
    // enterrerait le bonhomme sous l'eau. A l'etage, c'est la dalle qui porte.
    // Sur un toboggan, la hauteur ne vient ni du terrain ni de la dalle mais du
    // PROFIL de la glissade: on descend entre deux niveaux, et le moteur n'a que
    // des niveaux entiers (voir `hauteurDescente`).
    const solIci = this.hauteurDescente() ?? this.solSousLesPieds(x, z, this.joueur.niveau)
    const y = this.joueur.nage ? Math.max(solIci, SEA_LEVEL) : solIci

    // Bonhomme. A la nage il s'enfonce, se couche vers l'avant et ondule: on le
    // fait ici plutot que dans l'animation, qui ne connait pas l'eau.
    const aBord = this.joueur.vehicule !== null
    const vehiculeCourant = aBord
      ? this.renduVehicules.vehicules().find((v) => v.id === this.joueur.vehicule) ?? null
      : null
    const nage = !aBord && (this.joueur.nage || dansPiscine(this.batiments.liste(), x, z))
    const cibleEnfoncement = nage ? PROFONDEUR_NAGE : 0
    this.enfoncement += clamp(cibleEnfoncement - this.enfoncement, -2.5 * dt, 2.5 * dt)
    const ondule = nage ? Math.sin(maintenant / 420) * 0.05 : 0
    /*
     * A BORD, C'EST LE VEHICULE QUI PORTE LE BONHOMME, pas le sol.
     *
     * Tant qu'il n'y avait que la voiture et la barque, les deux se valaient
     * (leur plancher EST le sol ou la mer). Un avion, lui, est a quarante
     * metres: assis sur « la hauteur du terrain + la hauteur d'assise », le
     * pilote serait reste dans l'herbe pendant que son appareil traversait le
     * ciel. On part donc du plancher du vehicule, qui sait, lui, s'il vole.
     */
    const assise = vehiculeCourant ? HAUTEUR_ASSISE[vehiculeCourant.kind] : 0
    // Dans un avion, on prend la pose INTERPOLEE — celle qui est reellement a
    // l'ecran. Pour tout le reste, elle est identique a celle de la simulation.
    const poseVol =
      vehiculeCourant && estAvion(vehiculeCourant.kind)
        ? this.renduVehicules.poseAffichee(vehiculeCourant.id)
        : null
    const xAff = poseVol ? poseVol.x : x
    const zAff = poseVol ? poseVol.z : z
    const yAssis = poseVol
      ? poseVol.y + assise
      : vehiculeCourant
        ? vehiculeCourant.y + assise
        : y - this.enfoncement + ondule
    this.bonhomme.racine.position.set(xAff, yAssis, zAff)
    this.bonhomme.racine.rotation.y = poseVol ? poseVol.rot : this.joueur.rot
    // Le corps se couche vers l'avant en entrant dans l'eau. L'inclinaison est
    // plus douce qu'avant: la brasse porte maintenant elle-meme l'attitude du
    // buste et de la tete, et les deux se cumulaient en un plongeon.
    this.bonhomme.racine.rotation.x = aBord ? 0 : (this.enfoncement / PROFONDEUR_NAGE) * 0.38
    // Nager n'est pas un etat du joueur mais une facon de se deplacer (voir
    // `Joueur.nage`): l'animation, elle, a besoin de savoir.
    const enMouvement = this.joueur.etat === 'marche' || this.joueur.etat === 'course'
    /*
     * LA CADENCE SUIT LA VRAIE VITESSE, y compris a la nage.
     *
     * Elle se deduisait de la marche et de la course seules. Depuis que
     * l'energie accelere le nageur, un bonhomme en pleine forme avancait 47 %
     * plus vite que ses bras ne le disaient — il glissait. Le plafond de nage
     * se calcule ici comme dans le pas partage, avec la meme fonction.
     */
    const vitesseCible = nage
      ? vitesseNage(formeDe(this.joueur.besoins))
      : this.joueur.etat === 'course'
        ? RUN_SPEED
        : this.joueur.etat === 'marche'
          ? WALK_SPEED
          : 0
    this.animation.jouer(nage && enMouvement ? 'nage' : actionDepuisEtat(this.joueur.etat))
    this.animation.maj(dt, vitesseCible * (this.intensiteJoystick > 0 ? this.intensiteJoystick : 1))
    if (this.animation.piedAuSol() && !nage) audio.pas(this.terrain.echantillon(x, z).sol)
    // A la nage, le clapotis suit le rythme de la brasse et non celui des pas.
    if (nage && enMouvement) this.clapotisBrasse()

    /*
     * LA CAMERA SUIT LE BONHOMME LA OU IL EST, y compris a quarante metres.
     *
     * Elle suivait `y`, la hauteur du SOL sous lui. Tant qu'on marchait, c'etait
     * la meme chose; en avion, la camera visait le terrain pendant que
     * l'appareil traversait le ciel, et il derivait vers le coin de l'ecran.
     */
    this.rig.suivre(xAff, yAssis, zAff, dt)
    this.rig.maj(dt)
    const biome = this.terrain.biome(x, z)
    /*
     * La teinte du LIEU colore la brume, et le biome ne repond que s'il n'y a
     * pas de lieu pour le faire.
     *
     * Le parametre existait mais n'etait jamais passe: la fonctionnalite etait
     * morte. Elle vaut doublement maintenant — les onze regions de l'ile 2
     * partagent huit biomes, donc c'est la brume qui fait qu'on SENT le
     * changement quand on passe du canyon lilas aux neiges.
     */
    const brume = this.terrain.regionAuPoint(x, z)?.couleurBrume ?? BIOMES[biome].couleurBrume
    this.ambiance.maj(this.horloge, this.rig.cible, this.rig.camera.position, this.rig.azimut, brume, this.rig.camera)
    const nuit = this.ambiance.facteurNuit

    // Ambiance sonore: seulement quand le contexte change.
    if (biome !== this.biomeCourant || this.horloge.phase !== this.phaseCourante) {
      this.biomeCourant = biome
      this.phaseCourante = this.horloge.phase
      audio.ambiance(biome, this.horloge.phase)
      journal.debug('ambiance', `${biome} / ${this.horloge.phase}`)
    }

    // Decors animes.
    const tempsSec = maintenant / 1000
    this.renduEau.maj(tempsSec)
    this.renduEau.couleurEau(nuit)
    this.renduProps.reglerDetail(this.rig.zoom, this.rendu.domElement.height)
    // Le vent ne souffle que sur ce qu'on voit: la portee suit le cadrage, donc
    // le balayage coute la meme chose sur une ile de 700 m que sur une de 200.
    this.renduProps.maj(tempsSec, this.rig.cible, this.rig.zoom * 2.6)
    this.renduRessources.maj(dt, this.tempsJeu)
    this.renduAnimaux.maj(this.animaux, dt)
    // Le mobilier d'enclos ne se refabrique que quand la liste des foyers
    // change: `maj` sort tout de suite si la signature est la meme.
    this.renduZoo.maj(this.zoo.foyers())
    this.majAutresJoueurs(dt, maintenant)
    /*
     * HORS de `majAutresJoueurs`, volontairement: cette methode sort tout de
     * suite quand il n'y a pas de salon, et l'aiguille resterait figee en mode
     * invite. Le nord, lui, se perd des qu'on pivote la camera, seul ou non.
     */
    // `presents()` et pas `etiquettes()`: le tour du cadran doit couvrir TOUS
    // les connectes, y compris ceux qui sont trop loin pour avoir un corps —
    // ce sont justement eux qu'on cherche.
    this.boussole.maj(this.rig.azimut, this.joueur.pos, this.renduJoueurs.presents(), this.rig.camera)
    this.marqueur.maj(dt)
    audio.maj(dt)

    // Batiments: fondu du toit et fenetres allumees.
    this.rig.camera.getWorldDirection(this.dirCameraTemp)
    this.posJoueurTemp.set(x, y + 1, z)
    this.renduBatiments.majFondu(this.interieur, this.dirCameraTemp, dt, this.posJoueurTemp, this.joueur.niveau)
    this.renduBatiments.majNuit(nuit)
    this.renduBatiments.majJardins(this.maturiteJardin)
    this.renduVehicules.majNuit(nuit)

    // Fantome d'un vehicule en cours de pose.
    if (this.mode === 'construction' && this.vehiculeEnPose) {
      if (!this.posEnPose) {
        this.posEnPose = { x: x + Math.sin(this.joueur.rot) * 5, z: z + Math.cos(this.joueur.rot) * 5 }
      }
      // Le fantome montre LA POSE REELLE, rangee sur l'axe pour un avion: ce
      // qu'on voit avant de payer doit etre ce qu'on obtient.
      const range = this.placementVehicule(this.vehiculeEnPose, this.posEnPose)
      const pv = range.pos
      const vv = validerPlacementVehicule(
        this.vehiculeEnPose,
        pv,
        range.rot,
        this.terrain,
        this.renduVehicules.vehicules(),
        this.batiments.liste(),
        this.joueur.inventaire,
        this.joueur.pos,
      )
      this.renduVehicules.fantome(
        this.vehiculeEnPose,
        pv,
        hauteurSecuritaire(this.terrain, pv.x, pv.z),
        range.rot,
        vv.ok,
        couleurCompte(this.compte),
      )
      this.menuConstruction.majPlacement(vv.ok, vv.raison)
    }

    // Fantome de construction.
    if (this.mode === 'construction' && this.kindEnPose) {
      if (!this.posEnPose) {
        // Par defaut, deux metres devant le joueur.
        this.posEnPose = { x: x + Math.sin(this.joueur.rot) * 4, z: z + Math.cos(this.joueur.rot) * 4 }
      }
      const p = this.posEnPose
      // On ne passe que les noeuds du voisinage: balayer les 5229 ressources
      // a chaque image faisait dependre le mode construction de la densite de
      // la vegetation.
      const v = validerPlacement(
        this.kindEnPose,
        p,
        this.rotEnPose,
        this.terrain,
        this.batiments.liste(),
        this.joueur.inventaire,
        this.indexRessources.dansRayon(p.x, p.z, rayonVoisinage(this.kindEnPose), this.tempsJeu, this.batiments.liste()),
        this.joueur.pos,
      )
      this.renduBatiments.fantome(
        this.kindEnPose,
        p,
        hauteurSecuritaire(this.terrain, p.x, p.z),
        this.rotEnPose,
        v.ok,
        this.couleursEnPose,
      )
      this.menuConstruction.majPlacement(v.ok, v.raison)
    }

    // Feux d'artifice.
    this.lampe.maj(dt, x, y, z, this.joueur.rot, nuit)
    // Sans cet appel, le tri par champ de vision de la faune est desactive:
    // 40 appels de dessin et 328 000 triangles au lieu de 5 et 5 000.
    this.renduAnimaux.majCamera(this.rig.camera)
    this.renduRessources.reglerDetail(this.rig.zoom, this.rendu.domElement.height)
    this.renduFeux.majCamera(this.rig.camera, this.rendu.domElement.height)
    this.renduFeux.maj(dt, tempsSec)

    // Interface.
    this.hud.majTemps(this.horloge)
    this.hud.majBesoins(this.joueur.besoins)
    this.hud.majInventaire(this.joueur.inventaire)
    this.hud.activerFeux(
      this.horloge.nuit,
      this.horloge.nuit ? '' : 'Les feux d’artifice ne se lancent que la nuit.',
    )

    this.rendu.render(this.scene, this.rig.camera)

    // Images par seconde.
    this.frames++
    if (maintenant - this.compteurFps >= 1000) {
      this.fps = (this.frames * 1000) / (maintenant - this.compteurFps)
      this.frames = 0
      this.compteurFps = maintenant
      this.hud.majFps(this.fps)
      this.ajusterResolution()
      // Compteurs memoire lisibles par les tests d'endurance.
      ;(window as unknown as { __infoRendu: unknown }).__infoRendu = {
        geometries: this.rendu.info.memory.geometries,
        textures: this.rendu.info.memory.textures,
        programmes: this.rendu.info.programs?.length ?? -1,
      }
      noterPerf({
        fps: this.fps,
        draws: this.rendu.info.render.calls,
        tris: this.rendu.info.render.triangles,
        memoireGeo: this.rendu.info.memory.geometries,
        memoireTex: this.rendu.info.memory.textures,
      })
    }
  }

  /**
   * Place et anime les autres joueurs.
   *
   * On les affiche tels qu'ils etaient il y a cent millisecondes (voir
   * shared/sim/presence.ts): c'est ce qui permet d'INTERPOLER entre deux
   * positions connues au lieu de sauter d'un paquet a l'autre.
   */
  private majAutresJoueurs(dt: number, maintenant: number): void {
    if (!this.salon) return
    for (const id of this.suivi.expirer(maintenant)) {
      journal.info('reseau', 'joueur silencieux retire', { id })
      this.renduJoueurs.retirer(id)
      this.etatsAutres.delete(id)
    }
    this.etatsAutres.clear()
    for (const id of this.suivi.ids()) {
      const e = this.suivi.etatDe(id, maintenant)
      if (e) this.etatsAutres.set(id, e)
    }
    this.renduJoueurs.maj(
      dt,
      this.etatsAutres,
      this.joueur.pos.x,
      this.joueur.pos.z,
      (idVehicule) => {
        const v = this.renduVehicules.vehicules().find((x) => x.id === idVehicule)
        return v ? HAUTEUR_ASSISE[v.kind] : 0
      },
      // La MEME hauteur que pour soi-meme: sinon quelqu'un qui se promene sur
      // sa terrasse apparait debout sur la pelouse, a travers sa maison.
      this.solSousLesPieds,
    )
    this.etiquettes.maj(
      this.renduJoueurs.etiquettes(),
      this.rig.camera,
      this.rendu.domElement.clientWidth,
      this.rendu.domElement.clientHeight,
    )
  }

  /**
   * Resolution adaptative: on suit les images par seconde et on ajuste la
   * finesse du rendu. Il faut deux secondes de suite du meme cote du seuil
   * pour bouger, sinon la resolution oscillerait a chaque hoquet.
   */
  /**
   * Resolution adaptative. La DECISION vit dans `client/render/resolution.ts`,
   * qui est pur et donc testable; ici on ne fait que l'appliquer.
   */
  private ajusterResolution(): void {
    const plafond = Math.min(window.devicePixelRatio, RATIO_MAX)
    const nouveau = deciderResolution(this.etatResolution, this.fps, plafond)
    if (nouveau !== null) this.appliquerRatio(nouveau)
  }

  private appliquerRatio(ratio: number): void {
    if (Math.abs(ratio - this.ratioRendu) < 0.01) return
    const avant = this.ratioRendu
    this.ratioRendu = ratio
    this.rendu.setPixelRatio(ratio)
    this.rendu.setSize(window.innerWidth, window.innerHeight, false)
    journal.info('perf', `resolution ${avant.toFixed(2)} -> ${ratio.toFixed(2)} (${this.fps.toFixed(0)} ips)`, {
      pixels: Math.round(window.innerWidth * ratio) * Math.round(window.innerHeight * ratio),
    })
  }

  // ------------------------------------------------------------- divers

  /**
   * Force le fantome de construction a un modele et une rotation donnes.
   * Sert aux tests d'endurance: c'est le chemin ou des geometries fuyaient.
   */
  fantomeTest(kind: BatimentKind, rot: number): void {
    this.mode = 'construction'
    this.kindEnPose = kind
    this.rotEnPose = ((rot % 4) + 4) % 4
    this.couleursEnPose = { ...couleursDefaut(kind) }
    this.posEnPose = { x: this.joueur.pos.x + 5, z: this.joueur.pos.z + 5 }
  }

  /** Sort du mode construction. */
  finFantomeTest(): void {
    this.quitterConstruction()
  }

  /** Les animaux. Exposes pour les tests d'endurance. */
  get animauxTest(): Animal[] {
    return this.animaux
  }

  enclosTest(x: number, z: number): EnclosVu {
    return enclosAutour(this.batiments.liste(), x, z)
  }

  /**
   * Les enclos du zoo pose a cet endroit, dans le repere du monde.
   *
   * Un test qui recopierait `PLAN_ZOO` testerait un plan qui n'est plus celui
   * du jeu des la premiere retouche. Il DEMANDE, comme pour `pointConstructible`.
   */
  enclosDuZooTest(x: number, z: number): { x: number; z: number; nom: string; capacite: number }[] {
    const zoo = this.batiments.liste().find((b) => cloisonsInterieures(b.kind).length > 0 && dansEmprise(b, x, z))
    if (!zoo) return []
    return enclosDe(zoo).map((e) => ({ x: e.centre.x, z: e.centre.z, nom: e.nom, capacite: e.capacite }))
  }

  /** Le rig de camera. Expose pour mesurer le cout du zoom. */
  get rigTest(): RigCamera {
    return this.rig
  }

  /** La scene three.js. Exposee pour les garde-fous de performance. */
  get sceneTest(): THREE.Scene {
    return this.scene
  }

  // Voir client/passerelles-test.ts pour le POURQUOI de ces deux passerelles.
  pointConstructibleTest(kind: BatimentKind, rot: number): Vec2 | null {
    return pointConstructible(
      kind,
      rot,
      this.terrain,
      this.batiments.liste(),
      this.joueur.inventaire,
      this.ressources,
    )
  }

  pointMarchableTest(biome: BiomeId): Vec2 | null {
    return pointMarchable(this.terrain, this.grille, biome)
  }

  /** Etat de la lampe, pour les tests. */
  get lampeAllumeeTest(): boolean {
    return this.lampe.estAllumee
  }

  /** Le noeud de ressource le plus proche, pour les tests. */
  noeudProcheTest(portee: number): Ressource | null {
    const { x, z } = this.joueur.pos
    return this.indexRessources.plusProche(x, z, portee, this.tempsJeu, this.batiments.liste())
  }

  /** Vrai si le compte connecte a les pouvoirs d'administration. */
  get estAdminTest(): boolean {
    return estAdmin(this.compte)
  }

  /** Couleur du joueur connecte. */
  get couleurJoueur(): number {
    return couleurCompte(this.compte)
  }

  autresJoueursTest(): JoueurVu[] {
    return autresJoueurs(this.renduJoueurs.etiquettes(), this.etatsAutres)
  }

  animalTest(id: number): AnimalVu | null {
    return animalVu(this.animaux, id, this.suiviFaune, this.fauneRecue)
  }

  /** Les animaux dont le serveur donne des nouvelles en ce moment. */
  fauneSuivieTest(): number[] {
    return this.suiviFaune.ids()
  }

  /** L'heure du monde telle que ce client la tient, en ms de temps de jeu. */
  tempsJeuTest(): number {
    return this.tempsJeu
  }

  /**
   * L'etat d'un noeud de ressource, pour les tests: c'est ainsi qu'on verifie
   * qu'un arbre abattu par quelqu'un d'autre est bien tombe chez soi aussi.
   */
  noeudTest(id: number): { pv: number; repousseA: number } | null {
    const r = this.ressources.find((x) => x.id === id)
    return r ? { pv: r.pv, repousseA: r.repousseA } : null
  }

  /**
   * L'etat d'un jardin, pour les tests: sa maturite (0 tout juste cueilli, 1
   * mur) et l'echeance de sa repousse en temps de jeu, ou null s'il est mur.
   */
  jardinTest(id: string): { maturite: number; repousseA: number | null } {
    return { maturite: this.maturiteJardin(id), repousseA: this.jardinsPrets.get(id) ?? null }
  }

  /**
   * Simule une coupure du reseau: la liaison tombe et se reprend toute seule.
   * Passerelle de test.
   */
  simulerCoupureTest(attenteMs?: number): void {
    this.salon?.couperPourTest(attenteMs)
  }

  /**
   * Affiche des lignes de conversation comme si elles venaient du serveur.
   *
   * Passerelle de test. Une partie de test joue SEULE: sans elle, aucun apercu
   * ne pourrait montrer le chat rempli, et c'est justement rempli qu'on veut le
   * juger — les couleurs de comptes, la lisibilite de nuit, la place qu'il prend
   * sur un telephone.
   */
  chatTest(messages: MessageChat[]): void {
    this.chat.recevoir(messages)
  }

  /** Coupe la liaison temps reel sans quitter la partie. Passerelle de test. */
  couperReseauTest(): void {
    this.salon?.arreter()
    this.salon = null
    this.suivi.vider()
    this.renduJoueurs.vider()
    this.etatsAutres.clear()
    // La faune revient sous la conduite du client, la ou le serveur l'avait
    // laissee: sans ca, tous les animaux se figeraient pour de bon.
    this.suiviFaune.vider()
    this.fauneRecue = false
    this.horlogeSuivie.repasserEnSolo()
  }

  /** Statistiques pour les tests et le journal. */
  statistiques(): Record<string, number | string> {
    return {
      fps: Math.round(this.fps),
      appels: this.rendu.info.render.calls,
      triangles: this.rendu.info.render.triangles,
      ressources: this.ressources.length,
      animaux: this.animaux.length,
      batiments: this.batiments.nombre,
      vehicules: this.renduVehicules.nombreVehicules(),
      autresJoueurs: this.renduJoueurs.nombreConnus,
      autresAffiches: this.renduJoueurs.nombreAffiches,
      reseau: this.salon ? (this.salon.connecte ? 'connecte' : 'coupe') : 'hors ligne',
      // Qui fait vivre les animaux: le serveur (monde partage) ou ce client
      // (partie solo, invite, liaison coupee). Un test qui verifie l'autorite
      // serveur doit pouvoir en etre sur avant de comparer des positions.
      faune: this.fauneRecue ? 'serveur' : 'local',
      fauneSuivie: this.suiviFaune.nombre,
      props: this.renduProps.nombreInstances(),
      vegetationCouchee: this.renduProps.nombreCouches(),
      // Ce qui est escamote sous une construction: l'herbe du salon et l'arbre
      // qui repoussait dans la cuisine. Un test de bout en bout doit pouvoir le
      // lire avant et apres une pose, puis apres la demolition.
      decorSousBatiment: this.renduProps.nombreCouverts(),
      noeudsSousBatiment: this.renduRessources.nombreCouverts(),
      trianglesTerrain: this.renduTerrain.nombreTriangles(),
      heure: formaterHeure(this.horloge),
      jour: this.horloge.jour,
      phase: this.horloge.phase,
      x: Math.round(this.joueur.pos.x * 10) / 10,
      z: Math.round(this.joueur.pos.z * 10) / 10,
      biome: this.terrain.biome(this.joueur.pos.x, this.joueur.pos.z),
    }
  }

  /** Avance l'horloge du jeu: sert aux tests pour atteindre la nuit vite. */
  avancerHorloge(heures: number): void {
    this.tempsJeu += (heures / 24) * DAY_LENGTH_MS
    this.horloge = horlogeDepuis(this.tempsJeu)
    journal.info('triche', `horloge avancee de ${heures} h -> ${formaterHeure(this.horloge)}`)
  }

  /**
   * Batit a un endroit precis en passant par le vrai chemin de validation et de
   * pose. Sert aux tests: seule la position est assistee, la logique est celle
   * du jeu.
   */
  batirTest(kind: BatimentKind, x: number, z: number, rot = 0): boolean {
    this.kindEnPose = kind
    this.rotEnPose = ((rot % 4) + 4) % 4
    this.couleursEnPose = { ...couleursDefaut(kind) }
    this.posEnPose = { x, z }
    const avant = this.batiments.nombre
    this.poser()
    return this.batiments.nombre > avant
  }

  /**
   * Verdict de placement SANS rien poser, pour les tests.
   *
   * Batir une barriere entiere pour decouvrir qu'un arbre en refuse une piece
   * laisse dans le monde une cloture inutile qui empeche le prochain essai:
   * un test qui cherche un emplacement doit pouvoir demander avant de poser.
   */
  peutBatirTest(kind: BatimentKind, x: number, z: number, rot = 0): boolean {
    return validerPlacement(
      kind,
      { x, z },
      ((rot % 4) + 4) % 4,
      this.terrain,
      this.batiments.liste(),
      this.joueur.inventaire,
      this.indexRessources.dansRayon(x, z, rayonVoisinage(kind), this.tempsJeu, this.batiments.liste()),
      this.joueur.pos,
    ).ok
  }

  pointsBatiment(id: string): PointsBatiment | null {
    const b = this.batiments.get(id)
    return b ? pointsBatiment(b) : null
  }

  /** Identifiants des batiments poses, pour les tests. */
  idsBatiments(): string[] {
    return this.batiments.ids()
  }

  get interieurCourant(): string | null {
    return this.interieur
  }

  get besoinsCourants(): Besoins {
    return { ...this.joueur.besoins }
  }

  /** Indice contextuel affiche au joueur, pour les tests. */
  get indiceCourant(): string | null {
    return this.dernierIndice
  }

  /** Lance un feu sans passer par l'interface: sert aux tests et aux apercus. */
  lancerFeuTest(couleur: number, forme: FormeFeu): void {
    this.donner({ fusee: 1 })
    this.lancerFeu(couleur, forme)
  }

  /**
   * Rejoue la logique d'un tap sur un point du monde donne.
   *
   * Les tests visaient l'ecran, ce qui les rendait dependants de l'azimut de la
   * camera. Ici on entre par le meme chemin que le joueur (`taperLePoint`), a
   * ceci pres qu'on lui donne le point du monde au lieu de le lui faire calculer
   * depuis un pixel: seule la conversion ecran vers monde est court-circuitee.
   */
  viserTest(x: number, z: number): void {
    this.taperLePoint({ x, z })
  }

  /** Enfoncement dans l'eau, pour les tests de nage. */
  /**
   * Nombre de lots de vegetation actuellement couches.
   *
   * Les traces de la voiture ne se voient sur aucune valeur du modele: l'herbe
   * n'est pas detruite, ses instances sont ecrasees dans le rendu. Sans cette
   * passerelle, un test ne pourrait constater le passage de la voiture qu'en
   * comparant des pixels.
   */
  get couchesVegetationTest(): number {
    return this.renduProps.nombreCouches()
  }

  /** Construit un vehicule a cet endroit. Renvoie vrai s'il a ete pose. */
  batirVehiculeTest(kind: VehiculeKind, x: number, z: number): boolean {
    this.vehiculeEnPose = kind
    this.kindEnPose = null
    this.posEnPose = { x, z }
    const avant = this.renduVehicules.vehicules().length
    this.poserVehicule()
    return this.renduVehicules.vehicules().length > avant
  }

  /** Les vehicules poses dans le monde. */
  get vehiculesTest(): Vehicule[] {
    return this.renduVehicules.vehicules()
  }

  /**
   * Le vehicule ou le joueur est assis, en LECTURE seule.
   *
   * `basculerVehiculeTest()` repond bien la meme chose, mais en montant ou en
   * descendant au passage: s'en servir pour observer changeait ce qu'on
   * observait. C'est ce qui a laissee passer l'impossibilite de monter au doigt.
   */
  get vehiculeCourantTest(): string | null {
    return this.joueur.vehicule
  }

  /**
   * L'etage ou se tient le joueur: 0 au sol, 1 sur la terrasse.
   *
   * Le niveau ne se lit sur rien d'observable — ni la position, qui reste en
   * 2D, ni le rendu, qui n'est que des pixels. Sans cette passerelle, un test
   * de bout en bout ne pourrait pas dire si l'escalier a servi a quelque chose.
   */
  get niveauTest(): number {
    return this.joueur.niveau
  }

  /** Monte dans le vehicule a portee, ou en descend. Renvoie l'etat obtenu. */
  basculerVehiculeTest(): string | null {
    this.basculerVehicule()
    return this.joueur.vehicule
  }

  /**
   * LA PHASE DE VOL d'un appareil, et son altitude au-dessus du sol.
   *
   * Sans cette passerelle, un test de bout en bout ne saurait pas distinguer un
   * avion qui decolle d'un avion qui roule: la position seule ne dit rien de
   * l'altitude (le rendu la porte, pas le modele), et « il vole » est justement
   * ce qu'on veut verifier. Les noms de phase sont ceux de
   * `shared/sim/avion.ts`, pas une traduction.
   */
  volTest(id: string): { phase: string; altitude: number; vitesse: number } | null {
    const v = this.renduVehicules.vehicules().find((x) => x.id === id)
    if (!v || !estAvion(v.kind)) return null
    const e = this.etatAvion(v)
    return { phase: e.phase, altitude: e.altitude, vitesse: e.vitesse }
  }

  get enfoncementTest(): number {
    return this.enfoncement
  }

  /**
   * Vrai si le bonhomme nage dans un bassin de batiment (piscine, parc).
   *
   * `Joueur.nage` ne repond PAS a cette question: il ne vaut que pour l'eau du
   * terrain (voir `majNageEtBesoins`), et un bassin est pose sur la terre
   * ferme. Sans cette passerelle, un test ne pourrait constater la baignade que
   * par l'enfoncement du rendu, c'est-a-dire par une consequence lissee dans le
   * temps — et il accuserait le mauvais coupable le jour ou l'enfoncement
   * changerait de vitesse.
   */
  get dansBassinTest(): boolean {
    return dansPiscine(this.batiments.liste(), this.joueur.pos.x, this.joueur.pos.z)
  }

  /**
   * La glissade en cours de descente, ou null.
   *
   * Une descente ne se lit sur rien d'observable: la position bouge comme une
   * marche ordinaire (c'est tout son propos) et la hauteur n'existe que dans le
   * rendu. Sans cette passerelle, un test ne saurait pas distinguer « il a
   * glisse » de « il a marche jusque-la ».
   */
  get descenteTest(): { batiment: string; index: number; parcouru: number } | null {
    const d = this.descente
    if (!d) return null
    return { batiment: d.batiment, index: d.index, parcouru: d.parcouru }
  }

  /** Allume ou eteint la lampe: sert aux tests et aux apercus. */
  lampeTest(allumee: boolean): void {
    this.lampe.allumer(allumee)
    this.hud.marquerActif(allumee ? 'lampe' : null)
  }

  /**
   * Ajoute des objets: sert aux tests.
   *
   * Elle passe par le SERVEUR comme tout le reste. Ecrire seulement dans le sac
   * local rendrait les tests menteurs depuis que l'autorite a change: le
   * navigateur afficherait mille bois, et la premiere pose serait refusee faute
   * de quoi payer.
   */
  donner(lot: Inventaire): void {
    void this.demanderSac({ quoi: 'triche', lot }).then(() => this.majCanneEnMain())
    journal.info('triche', 'objets ajoutes', lot)
  }

  get inventaire(): Inventaire {
    return this.joueur.inventaire
  }

  get horlogeCourante(): Horloge {
    return this.horloge
  }

  arreter(): void {
    this.arrete = true
    cancelAnimationFrame(this.boucleId)
    arreter(this.joueur)
    this.pointeur.detruire()
    if (this.surRedim) removeEventListener('resize', this.surRedim)
    if (this.surClavier) removeEventListener('keydown', this.surClavier)
    this.panneauAdmin.detruire()
    this.voileHorsLigne.detruire()
    this.salon?.arreter()
    this.salon = null
    this.etiquettes.detruire()
    this.boussole.detruire()
    this.chat.detruire()
    this.renduJoueurs.dispose()
    this.renduVehicules.dispose()
    this.renduZoo.dispose()
    this.surRedim = null
    this.surClavier = null
    journal.info('boucle', 'arretee')
  }
}
